/**
 * VoxelWorld - Manages chunk loading/unloading around the player
 */

import * as THREE from 'three';
import {
  VISIBILITY_RADIUS,
  VISIBILITY_UNLOAD_BUFFER,
  VISIBILITY_ALL,
  CHUNK_SIZE,
  CHUNK_WORLD_SIZE,
  VOXEL_SCALE,
  FACE_OFFSETS_6,
  NEGATIVE_MARGIN_OFFSETS_7,
  POSITIVE_MARGIN_OFFSETS_7,
  REQUEST_TIMEOUT_MS,
  MSG_VOXEL_CHUNK_REQUEST,
  MSG_MAP_TILE_REQUEST,
  MSG_SURFACE_COLUMN_REQUEST,
  worldToChunk,
  chunkKey,
  chunkKeyLod,
  parseChunkKey,
  BuildOperation,
  getAffectedChunks,
  drawToChunk,
  VoxelChunkData,
  RequestNack,
  encodeVoxelChunkRequest,
  encodeSurfaceColumnRequest,
  encodeMapTileRequest,
  SurfaceColumnResponse,
  MapTileResponse,
  computeVisibility,
  getChunkRangeFromHeights,
  getSunlitAbove,
  computeAndPropagateLight,
  coarseBlockFalloff,
  faceDonatesLight,
  faceWouldBrighten,
  bottomRowSkyClass,
  BOTTOM_ROW_ALL_DARK,
  BOTTOM_ROW_ALL_FULL,
  BOTTOM_ROW_MIXED,
  relightRegion,
  type RelightTarget,
  chunkHasEmitter,
  chunkHasBlockLight,
  MAP_TILE_SIZE,
  tilePixelIndex,
} from '@worldify/shared';
import { Chunk, ChunkPhase } from './Chunk.js';
import { CoverageCore } from './CoverageCore.js';
import { isSpawnReadyAt, type SpawnProbe } from '../spawn/spawnReadiness.js';
import { ChunkGeometry } from './ChunkGeometry.js';
import { LodResidencyManager, type LodSelection, type RegionCensus } from './LodResidencyManager.js';
import {
  MAX_BASE_LOD,
  MAX_FAR_LOD_LEVELS,
  octetAlignedBounds,
  packCoord,
  packColumn,
  columnKeyLod,
  unpackCoord,
} from './LodShellSelector.js';
import { packPairKey, unpackPairKey, packChunkKeyLod } from '@worldify/shared';
import { TerrainBatch } from './TerrainBatch.js';
import { voxelAllowanceMs, refreshPeriodMs } from './frameBudget.js';
import { RemeshPipeline } from './RemeshPipeline.js';
import { marginConsumerNeedsRemesh } from './marginConvergence.js';
import { borrowedBorders, widenedColumnRange, type BorrowedBorders, type ChunkRange } from './columnMarginRange.js';
import {
  scheduleLodRequests, marginSourceKey, shellDistance, orderColumnsNeedingTiles, passesPanDebounce,
  compareStreamOrder,
  MAX_BUNDLE_CHUNKS, coarseColumnWindow, coarseChunkSlots,
} from './lodRequestSchedule.js';
import { streamingGate } from './streamingGate.js';
import { MeshWorkerPool, meshWorkerCount } from './MeshWorkerPool.js';
import { expandChunkToGrid, setMarginPolicy, type MarginPolicy } from './ChunkMesher.js';
import { resampleLightAttributes } from './MeshGeometry.js';
import { sendBinary } from '../../net/netClient.js';
import { useGameStore } from '../../state/store.js';
import { perfStats } from '../debug/PerformanceStats.js';
import {
  getVisibleChunks,
  getFrustumFromCamera,
  getCameraDirection,
  type ChunkProvider,
} from './VisibilityBFS.js';
import { TerrainWorkerPool, terrainWorkerCount } from './TerrainWorkerPool.js';
import { SeamStitcher } from './SeamStitcher.js';
import { DisplayCommit } from './DisplayCommit.js';
import { getActiveWorldSeed, getActiveWorldGenConfig, hasChunk, loadChunk, saveChunk, editedChunkRange, pushUndo, popUndo, loadLodEditState, saveLodEdits, saveLodPending, type ChunkSnapshot, type SnapPoint } from '../world/WorldManager.js';
import { LodEditLayers, type LodEditBatch, type LodEditTouch } from './LodEditLayers.js';

/** A whole chunk's local voxel bounds — the refold box for an undo (its snapshot is chunk-wide). */
const WHOLE_CHUNK_BOX = {
  x0: 0, y0: 0, z0: 0, x1: CHUNK_SIZE - 1, y1: CHUNK_SIZE - 1, z1: CHUNK_SIZE - 1,
} as const;

/** Callback type for requesting chunk data from server */
export type ChunkRequestFn = (cx: number, cy: number, cz: number) => void;

/** What an undo reverted: the chunk keys, plus the snap points the placement had deposited. */
export interface UndoResult { keys: string[]; snapPoints: SnapPoint[]; }

const NO_UNDO: UndoResult = { keys: [], snapPoints: [] };

/**
 * How long a persisted-chunk IndexedDB read may take before we give up and regenerate.
 * Bounded because a read that never settles would strand the request forever (see
 * loadLocalChunk); generating is a few ms on a worker, so falling back is cheap.
 */
const IDB_CHUNK_READ_TIMEOUT_MS = 2000;

/**
 * THE ±1 DILATION THE MARGIN-SOURCE PASS REACHES: POSITIVE_MARGIN_OFFSETS_7 union FACE_OFFSETS_6.
 *
 * DERIVED from both tables rather than restated, because the chunk dependency contract in
 * shared/voxel/constants.ts requires every consumer to derive from them — and deduplicated, because
 * [1,0,0], [0,1,0] and [0,0,1] appear in BOTH, so walking the two tables back to back probed three of
 * every thirteen positions twice. Flat, so the marking loop indexes instead of destructuring a nested
 * array per iteration (the typed-array invariant in CLAUDE.md).
 */
const MARGIN_DILATION: Int8Array = (() => {
  const seen = new Set<number>();
  const out: number[] = [];
  for (const [dx, dy, dz] of [...POSITIVE_MARGIN_OFFSETS_7, ...FACE_OFFSETS_6]) {
    const k = (dx + 1) * 9 + (dy + 1) * 3 + (dz + 1);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(dx, dy, dz);
  }
  return new Int8Array(out);
})();

/**
 * Chunk-Y range for a column from its (stamp-corrected) heights.
 * - maxCy: topmost chunk to LOAD, from maxHeight + 1 — the top face of the highest solid voxel is
 *   meshed from the voxel ABOVE it, so a flat top flush with a chunk's top row (localY 31) needs the
 *   next chunk up loaded to supply the air margin (else the extrapolated margin repeats the solid
 *   voxel and the top face is culled → flat roofs clipped). Non-flush tops resolve to their own chunk.
 * - minCy: chunk of the LOWEST surface point. Chunks strictly below it are fully underground (no open
 *   sky anywhere in the footprint), so they may safely default to dark when the chunk above isn't
 *   loaded. Surface chunks (slopes, flush tops, BFS-edge columns) are at cy >= minCy and stay lit.
 *
 * Both are properties of the whole 32×32 footprint, which is right for a CLIP. It is not right for
 * "how high is the ground here" — see surfaceHeightAt, which used to answer from `maxHeight` and now
 * indexes the heights.
 */
function columnChunkRange(heights: ArrayLike<number>): { minCy: number; maxCy: number } {
  const { minCy, maxHeight } = getChunkRangeFromHeights(heights);
  return { minCy, maxCy: Math.floor((maxHeight + 1) / CHUNK_SIZE) };
}

/** The columns that borrow THIS column's low border as their mesh margin: −X, −Z and −XZ. */
const COLUMN_BORROWERS: readonly (readonly [number, number])[] = [[-1, 0], [0, -1], [-1, -1]];

/**
 * Surface height at a world position, in METRES, from its tile's heights — the value at THAT column,
 * not the tile's maximum.
 *
 * Exported for the test: the whole defect lives in this indexing, and it is worth one assertion that
 * a road beside a tower reads as a road.
 */
export function columnHeightFromTile(
  heights: ArrayLike<number>, x: number, z: number, tx: number, tz: number,
): number {
  const lx = Math.min(MAP_TILE_SIZE - 1, Math.max(0, Math.floor(x / VOXEL_SCALE) - tx * CHUNK_SIZE));
  const lz = Math.min(MAP_TILE_SIZE - 1, Math.max(0, Math.floor(z / VOXEL_SCALE) - tz * CHUNK_SIZE));
  return heights[tilePixelIndex(lx, lz)] * VOXEL_SCALE;
}

/** Shared read-only all-dark "light from above" (used for underground chunks with no chunk above
 *  loaded, so caves aren't lit as open sky). Never mutated — the sunlight pass only reads it. */
const DARK_ABOVE = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);

/**
 * What the base level wants this pass, decided but not yet submitted — see buildBaseRequestPlan.
 * `sorted` is the near-first chunk order and `orderedColumns` the column-bundle order derived from
 * it; the two budgets travel with the plan so the issue phase cannot drift from the phase that
 * published the backlog.
 */
interface BaseRequestPlan {
  sorted: Array<{ p: number; cx: number; cy: number; cz: number; lod: number; d: number; own: number; pre: number; group: number }>;
  orderedColumns: number[];
  maxTiles: number;
  maxChunks: number;
}



/** Fast typed-array equality check (same length assumed). */
function arraysEqual(a: Uint32Array, b: Uint32Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Manages the voxel world - chunk loading, unloading, and streaming.
 */
export class VoxelWorld implements ChunkProvider {
  /** All loaded chunks, keyed by "cx,cy,cz" */
  readonly chunks: Map<string, Chunk> = new Map();

  /** All chunk geometries, keyed by "cx,cy,cz" */
  readonly geometries: Map<string, ChunkGeometry> = new Map();
  /** `geometries` by packChunkKeyLod — the stitcher's per-vertex lookups (see chunkIndex for why). */
  readonly geometryIndex: Map<number, ChunkGeometry> = new Map();

  /** Chunk keys currently in build preview (set by BuildPreview, read by visibility) */
  readonly previewChunks: Set<string> = new Set();

  /** Chunks pending data from server, keyed by "cx,cy,cz" */
  private pendingChunks: Set<string> = new Set();

  /** Columns pending data from server, keyed by "tx,tz" */
  private pendingColumns: Set<number> = new Set();

  /** Tiles pending from server, keyed by "tx,tz" */
  private pendingTiles: Set<number> = new Set();

  /** Timestamps for pending requests (keyed same as their pending sets) */
  private pendingChunkTimes: Map<string, number> = new Map();
  private pendingColumnTimes: Map<number, number> = new Map();
  private pendingTileTimes: Map<number, number> = new Map();

  /** Coarse (LOD ≥ 1) chunk requests in flight to the local terrain pool, keyed "cx,cy,cz,lod".
   *  Separate from pendingChunks so ring streaming has its own budget and can never starve (or be
   *  starved by) LOD-0 streaming. */
  private pendingLodChunks: Set<string> = new Set();
  private pendingLodChunkTimes: Map<string, number> = new Map();

  /** Coarse column probes in flight, keyed "cx,cz,lod". */
  private pendingLodColumns: Set<number> = new Set();
  private pendingLodColumnTimes: Map<number, number> = new Map();

  /** Column info from received tiles. maxCy = top chunk to load (stamp-inclusive, highest surface
   *  point). minCy = chunk of the LOWEST surface point — chunks below it are fully underground.
   *  Keyed by "tx,tz". */
  /**
   * Per-column vertical extent, keyed by packColumn(cx, cz, 0) — an integer, not `${cx},${cz}`.
   * isEmptyAir reads this on the BFS's hottest path: once per traversed cell AND 13× per reachable
   * chunk inside the margin-source loop (~22k lookups per pass at view 5), so a template key here was
   * ~22k string allocations per pass on its own. Same reasoning as the packed reachable set; the
   * coarse counterpart (lodColumnInfo) has been packed since the column-probe work.
   */
  private columnInfo: Map<number, { maxCy: number; minCy: number }> = new Map();
  /**
   * Per-column surface heights, for surfaceHeightAt alone.
   *
   * SEPARATE FROM columnInfo, and bounded, because the two have different lifetimes: a column's clip
   * is consulted for every column ever visited and is two numbers, while these are 2 KB apiece and
   * are only ever read for the one column under the explore target. Cleared wholesale past the cap,
   * the same idiom the terrain caches use — a miss just means surfaceHeightAt answers null, which is
   * the same answer it gives before a tile arrives and which the caller already retries through.
   */
  private columnHeights: Map<number, ArrayLike<number>> = new Map();
  private static readonly MAX_COLUMN_HEIGHTS = 4096;
  /**
   * A column's clip from its OWN heights (edits included), and the low-border extremes its −X / −Z /
   * −XZ neighbours borrow from it. `columnInfo` is the own range widened by the three neighbours a
   * chunk reads as margin (columnMarginRange.ts) and is re-derived from these whenever any of the
   * four tiles arrives, so it never compounds and a demolished tower releases its clip.
   */
  private columnOwnRange: Map<number, ChunkRange> = new Map();
  private columnBorders: Map<number, BorrowedBorders> = new Map();

  /** Coarse-column info from lodColumn probes (COARSE chunk indices): maxCy = top chunk to load,
   *  minCy = chunks strictly below are provably fully solid. Keyed by "cx,cz,lod". The LOD rings'
   *  vertical clip — the coarse counterpart of columnInfo. */
  readonly lodColumnInfo: Map<number, { maxCy: number; minCy: number }> = new Map();

  /** Local terrain generation worker pool (offline mode). Created lazily. */
  private localPool: TerrainWorkerPool | null = null;

  /** Callback to notify external systems (e.g. map cache) when a tile arrives */
  onTileReceived: ((tx: number, tz: number, heights: Int16Array, materials: Uint8Array) => void) | null = null;

  /**
   * Callback fired when a chunk's voxel data is (re)ingested — i.e. real
   * generated/streamed content, including procedural stamps (trees/rocks/
   * buildings). Lets the minimap refresh from actual chunks, not just the
   * stamp-free terrain baseline. Not fired on identical no-op re-sends.
   */
  onChunkIngested: ((chunkKey: string) => void) | null = null;

  /**
   * Fired when every chunk is dropped at once (dispose / clearAndReload / world switch).
   * Bulk clears do NOT fire per-chunk unload listeners, so anything caching chunk arrays —
   * notably the physics VoxelField — must reset here or it will keep colliding against a
   * world that no longer exists.
   */
  onWorldCleared: (() => void) | null = null;

  /** Listeners notified when a chunk is unloaded */
  private unloadListeners: Set<(chunkKey: string) => void> = new Set();

  /** Reference to the Three.js scene */
  readonly scene: THREE.Scene;

  /** Camera reference for frustum culling */
  private camera: THREE.Camera | null = null;

  /** Whether initial surface column has been requested */
  private initialColumnRequested = false;
  /**
   * THE BOOTSTRAP LATCH (see streamingGate.ts). Set the first time the centre's column height is
   * known, never cleared except on a world reset. Written as a per-pass CONDITION instead, this
   * deadlocked a fast pan: the tile-request path is downstream of the gate, so a centre that
   * outran a tile round trip could never become known and streaming stopped for good.
   */
  private streamingBootstrapped = false;
  /** When the initial column was requested — the deadline's clock (0 = not requested). */
  private initialColumnRequestedMs = 0;

  /**
   * WHICH WORLD THE RESIDENT STATE BELONGS TO. Bumped by `dispose`, so anything that went async
   * before a world switch can tell that its answer is for a world that no longer exists.
   *
   * It is needed because a chunk key carries no world identity — "cx,cy,cz,lod" is the same
   * coordinate in every world — so a late result does not merely arrive stale, it lands ON the new
   * world's chunk at that coordinate and overwrites it with the previous world's terrain. Work that
   * can be CANCELLED is cancelled instead (the terrain pool is disposed, the mesh pool's queues are
   * dropped); this covers the reads that cannot be, namely IndexedDB, which has already captured the
   * outgoing world's id in its key by the time the switch happens.
   */
  private worldEpoch = 0;

  /** Last player chunk position (for detecting chunk changes) */
  private lastPlayerChunk: { cx: number; cy: number; cz: number } | null = null;

  /** Last chunk where BFS was computed (for hysteresis) */
  private lastBFSChunk: { cx: number; cy: number; cz: number } | null = null;

  /**
   * The octet-aligned traversal box of the last BFS (set with lastBFSChunk, from the same center +
   * radius the BFS used — see octetAlignedBounds). isBaseChunkCovered tests against THIS box so the
   * coverage walk and the BFS cut can never disagree about which chunks are distance-culled. Only
   * valid while lastBFSChunk is non-null (the null gate).
   */
  private lastBFSAlignedBox: { minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number } | null = null;

  /** The pure coverage decision layer (classification today; walk/eviction in later slices). */
  private readonly coverageCore = new CoverageCore({
    isEmptyAir: (cx, cy, cz) => this.isEmptyAir(cx, cy, cz),
    bfsBox: () => (this.lastBFSChunk ? this.lastBFSAlignedBox : null),
    inRenderSet: (cx, cy, cz) => this.shouldMeshChunk(cx, cy, cz, 0),
    frontierActive: () => this.bfsFrontierActive,
    isMeshedComplete: (cx, cy, cz) => {
      const chunk = this.chunkAt(cx, cy, cz, 0);
      return chunk !== undefined && chunk.phase === ChunkPhase.MeshedComplete;
    },
  });

  /**
   * Cached reachable set from the last BFS (reused until the player changes chunk), PACKED with
   * packCoord. Every consumer holds coordinates already, so a string set meant building a key just to
   * ask a question — up to 7 per shouldMeshChunk call, once per queue entry per frame.
   */
  private cachedReachable: Set<number> = new Set();
  /** Last BFS met unloaded terrain inside the box — see VisibilityResult.frontierActive. */
  private bfsFrontierActive = true;

  /** Scratch grid for the margin-source dilation (see addMarginSourceRequests). Grows to the largest
   *  reachable box seen and is refilled per pass, so a settled view allocates nothing. */
  private marginMarks: Uint8Array = new Uint8Array(0);

  /**
   * lod-0 chunks whose applied mesh is UNCONVERGED — either MeshedIncomplete (a skipped face: cannot
   * draw yet) or drawn with an EXTRAPOLATED margin source (extrapolatedMargins ≠ 0: draws, but wrong
   * along that edge/corner column). Kept so the convergence sweep (healStuckIncompleteChunks) can
   * revisit exactly those instead of scanning every chunk. Maintained where phase is applied (the
   * remesh listener) and on unload.
   */
  private incompleteChunks = new Set<string>();
  /** First time each member of incompleteChunks was seen stuck — for the WEDGE report only. */
  private incompleteSinceMs = new Map<string, number>();

  /** Build operations deferred until all affected chunks are loaded */
  private deferredBuildOps: Array<{ operation: BuildOperation; affectedKeys: string[] }> = [];

  /** Whether the world has been initialized */
  private initialized = false;

  /** Dynamic visibility radius (defaults to shared constant, overridden by quality settings) */
  private _visibilityRadius: number = VISIBILITY_RADIUS;

  /** Number of coarser far-view LOD levels around the base region (0 = rings off). */
  private _farLodLevels = 0;

  /** LOD level of the base (finest displayed) region. Pinned 0 in first person; explore zoom steps it. */
  private displayBaseLod = 0;

  /** Zoom-driven base LOD target; displayBaseLod steps toward it one level at a time. */
  private targetBaseLod = 0;
  private lastBaseLodStepMs = 0;
  private static readonly BASE_LOD_STEP_TIMEOUT_MS = 2500;
  /**
   * When the DISPLAYED base level last actually changed (0 = never). Distinct from
   * lastBaseLodStepMs, which is the step TIMEOUT clock and is also reset on every target change —
   * the audit's stale-BFS watchdog needs "since the step landed", not "since the user scrolled".
   */
  /** When the coarse shells last shifted (pan debounce reference; see updateLodStreaming). */
  private lastShellShiftMs = 0;
  private static readonly LOD_PAN_DEBOUNCE_MS = 150;
  /** When the streaming centre last crossed a chunk — the gate's "is the view moving". */
  private lastCentreMoveMs = 0;
  /** When the CURRENT run of motion began; a single crossing (a spawn snap, a teleport) never
   *  outlives the window, so only a sustained pan is distinguishable from a cold load. */
  private centreMoveStartMs = 0;
  /** Longer than a one-chunk-per-eight-frames pan's 133 ms, so a sustained pan never reads settled. */
  private static readonly CENTRE_MOTION_MS = 500;

  /**
   * How long edits coalesce before the LOD fold runs. Deliberately long: the fold is not
   * display-critical for the level the player is building on (lod 0 is already committed and
   * remeshed), a refold is idempotent so delaying it can never corrupt anything, and drag-painting
   * or a multi-part preset otherwise cascades once per stroke. A zoom-out flushes it early, and a
   * tab closed inside the window replays from the persisted markers next session.
   */
  private static readonly EDIT_FOLD_DEBOUNCE_MS = 3000;

  /** LOD ownership of volume: which chunk at which level draws each region (null when rings off). */
  private lastLodSelection: LodSelection | null = null;

  /** Resident coarse (lod > 0) chunk count — cheap "anything to sweep?" check when rings turn off. */
  private lodChunkCount = 0;

  /**
   * NUMERIC RESIDENCY INDEX — `chunks` by packed integer coord, one map per lod. `chunks` remains
   * the single source of truth for a chunk's data and phase; this only mirrors WHICH positions are
   * resident, so the LOD walk can look a chunk up without building a `cx,cy,cz,lod` string first.
   * That allocation (not the hash) was the top line in the profiler at ~150k calls per walk pass.
   * Maintained in exactly the places that add or remove residency — indexChunk / unindexChunk /
   * clear — and cross-checked against `chunks.size` by the DEV invariant so a missed site fails
   * loudly instead of silently mis-drawing.
   */
  private chunkIndex: Map<number, Chunk>[] = [];

  /** Resident chunk count per lod. */
  private residentByLod: number[] = [];

  /**
   * PER LOD: the chunks the retired fallback could actually draw — MeshedComplete and OUTSIDE the
   * level's in-range set — as packed coords. Answers `retiredMeshesAt` for one selection pass.
   *
   * DERIVED, not co-maintained, and deliberately so. A list kept incrementally would need hooks on
   * residency, on every mesh-phase transition in both directions, and on every shell shift — five
   * places to drift, in the subsystem whose header explains that it derives coverage precisely to
   * avoid co-maintained sets and their forget-on-unload contracts. One scan of the residency index
   * per pass cannot drift and costs O(resident chunks). It used to yield only a boolean per level,
   * which left the walk to FIND the chunks by fanning out over the onion; the list is what lets it
   * iterate them instead (see LodResidencyDeps.retiredMeshesAt).
   */
  private retiredMeshesByLod: number[][] = [];

  /** Rebuild `retiredMeshesByLod`. Arrays are reused across passes (length reset, not reallocated). */
  private recomputeRetiredMeshIndex(): void {
    for (let lod = 0; lod < this.chunkIndex.length; lod++) {
      const m = this.chunkIndex[lod];
      let list = this.retiredMeshesByLod[lod];
      if (!list) list = this.retiredMeshesByLod[lod] = [];
      list.length = 0;
      if (!m || m.size === 0) continue;
      // A level absent from the onion has NO in-range set, so every resident chunk of it is retired
      // — which is exactly the zoom-step case the fallback exists for, not an edge case.
      const inRange = this.lodManager.levelInRange(lod);
      for (const [packed, chunk] of m) {
        if (chunk.phase !== ChunkPhase.MeshedComplete) continue;
        if (inRange?.has(packed)) continue;
        list.push(packed);
      }
    }
    for (let lod = this.chunkIndex.length; lod < this.retiredMeshesByLod.length; lod++) {
      this.retiredMeshesByLod[lod].length = 0;
    }
  }

  /**
   * Set when inputs to updateMeshVisibility() change (new geometry,
   * unloaded chunks, preview changes).  Cleared after each full scan.
   * The BFS chunkChanged flag is handled separately.
   */
  private visibilityDirty = true;

  /** THE ONLY WAY TO ARM `visibilityDirty`. */
  private dirtyVisibility(): void {
    this.visibilityDirty = true;
  }

  /** Worker pool for off-thread mesh generation */
  readonly meshPool: MeshWorkerPool;

  /** Groups chunk geometries into spatial buckets for draw-call reduction */
  /** Every chunk of a (lod, layer) in one multi-draw call — see TerrainBatch / docs/terrain-batching-plan.md. */
  readonly terrain: TerrainBatch;

  /** Manages remesh queue and worker dispatch */
  readonly remeshPipeline: RemeshPipeline;

  /** Reconciles vertex normals across chunk-mesh seams. */
  readonly seamStitcher: SeamStitcher;
  /** The one commit point for display changes (C4) — see its header. */
  readonly displayCommit: DisplayCommit;
  /** This pass's display DECISIONS (key -> desired visibility), consumed whole by the commit. */
  private pendingDisplayDecisions = new Map<string, boolean>();
  /** Hides withheld by the no-uncover rule last pass — see holdUncoveredHides. */
  private readonly retainedHides = new Set<string>();
  /** The last commit left transactions undone — the next walk is due as soon as the frame has room. */
  private commitBacklog = false;

  /** Decides which chunk at which LOD covers each region (the 8↔1 swap). */
  readonly lodManager: LodResidencyManager;

  /** Player edits, folded up the LOD hierarchy and merged into coarse chunks at ingest. */
  readonly editLayers: LodEditLayers;
  /**
   * Resident coarse chunks whose DATA is still procedural-only after a cascade, held until the walk
   * puts them on screen. THE POINT OF THE SPLIT: a build touches one chunk per level, and remeshing
   * a coarse chunk is the expensive half of an edit (mesh + seam + skirt + a group re-merge inside
   * the frame budget). While the player builds at lod 0 no coarse level is in range, so these sets
   * stay untouched and an edit costs exactly what it always cost.
   */
  private readonly staleLodMeshes = new Set<string>();
  /** As above, but the layer LOWERED a floor — merging cannot undo that, so these must regenerate. */
  private readonly staleLodData = new Set<string>();


  constructor(scene: THREE.Scene) {
    this.scene = scene;
    // Console access to the stream diagnostics (debugStreamGate / debugChunkStreamState).
    (globalThis as Record<string, unknown>).__voxelWorld = this;
    this.meshPool = new MeshWorkerPool(meshWorkerCount());
    this.terrain = new TerrainBatch(scene);
    this.terrain.setViewDistance(this._visibilityRadius);
    this.remeshPipeline = new RemeshPipeline(
      this.chunks,
      this.geometries,
      this.terrain,
      this.meshPool,
      this.pendingChunks,
      (cx, cy, cz, lod) => this.isMarginSourceExpected(cx, cy, cz, lod),
      (cx, cy, cz, lod) => this.shouldMeshChunk(cx, cy, cz, lod),
      (lod) => this.levelOpen(lod),
      this.geometryIndex,
    );
    this.remeshPipeline.onDispatch = (key) => {
      this.meshDispatchTotal++;
      this.meshedEver.add(key);
      if (this.streamingIdle()) this.meshDispatchIdle++;
    };
    // Let the mesher resolve absent margins it can prove: open sky (mesh against air — terrain tops
    // at a chunk boundary cap instead of gap) and, for coarse chunks, provably-solid ground below
    // the column's lowest surface (mesh flat against rock). Everything else stays 'unknown'
    // (extrapolate + skip the face; never rendered).
    setMarginPolicy((cx, cy, cz, lod) => this.marginPolicyAt(cx, cy, cz, lod));
    this.lodManager = new LodResidencyManager({
      isBaseCovered: (cx, cy, cz) => this.isBaseChunkCovered(cx, cy, cz),
      isCoarseCovered: (cx, cy, cz, lod) => {
        const policy = this.marginPolicyAt(cx, cy, cz, lod);
        return policy === 'air' || policy === 'solid';
      },
      isRendered: (cx, cy, cz, lod) => this.chunkAt(cx, cy, cz, lod)?.phase === ChunkPhase.MeshedComplete,
      retiredMeshesAt: (lod) => {
        const list = this.retiredMeshesByLod[lod];
        return list && list.length > 0 ? list : null;
      },
      finerSurfaceTopCy: (cx, cz, lod) => {
        if (lod === 0) return this.columnInfo.get(packColumn(cx, cz, 0))?.maxCy ?? null;
        return this.lodColumnInfo.get(packColumn(cx, cz, lod))?.maxCy ?? null;
      },
      // NO-STEAL truth: is this chunk contributing PIXELS right now (TerrainBatch display state, which
      // excludes empty geometry — a chunk drawing nothing has no pixels to lose, so it must not
      // block a coarse fallback). Reached only from the walk's takeover branches, so the key build
      // is off the hot path.
      isDisplayed: (cx, cy, cz, lod) => this.terrain.isChunkDisplayed(chunkKeyLod(cx, cy, cz, lod)),
      isMeshPending: (cx, cy, cz, lod) => this.remeshPipeline.hasPendingApply(chunkKeyLod(cx, cy, cz, lod)),
      isProvablyEmpty: (cx, cy, cz, lod) => this.provablyEmpty(cx, cy, cz, lod),
      // Resident and refused by the render set: the mesher will never build it, so the region it
      // sits in has had everything asked of it. See isOcclusionParked for why coverage disagrees.
      isOcclusionParked: (cx, cy, cz) => this.hasChunkAt(cx, cy, cz, 0)
        && !this.shouldMeshChunk(cx, cy, cz, 0),
      // RESOLUTION truth, which is display OWNERSHIP (`isVisible`) rather than pixels: a chunk
      // whose mesh is empty — fully solid, no surface — has taken its volume and leaves no hole.
      // `isChunkDisplayed` excludes exactly those, and counting them unresolved would hold every
      // level open over solid rock forever.
      forEachDisplayed: (cb) => {
        for (const [key, c] of this.chunks) {
          if (this.terrain.isVisible(key)) cb(c.cx, c.cy, c.cz, c.lod);
        }
      },
    });
    this.editLayers = new LodEditLayers({
      debounceMs: VoxelWorld.EDIT_FOLD_DEBOUNCE_MS,
      // The fold reads the chunk's CURRENT voxels: the resident copy, or the persisted one when the
      // edit's chunk was evicted (or when a marker is replayed in a later session). Null means
      // unreadable — the edit keeps its marker and is folded on a later pass instead of being lost.
      fineChunkData: async (cx, cy, cz) => {
        const live = this.chunkAt(cx, cy, cz, 0);
        if (live) return live.data;
        if (!this.isLocal) return null;
        const epoch = this.worldEpoch;
        const saved = await loadChunk(chunkKey(cx, cy, cz));
        return epoch === this.worldEpoch ? saved : null;   // switched worlds mid-read
      },
      onLayersChanged: (touched, clips) => this.onEditLayersChanged(touched, clips),
      // Server mode has no local world to write to: the cascade still runs, so a build is correct at
      // range for the session, and nothing is persisted.
      persist: (batch) => { if (this.isLocal) saveLodEdits(batch.layers, batch.clips); },
      savePending: (list) => { if (this.isLocal) saveLodPending(list); },
    });
    this.seamStitcher = new SeamStitcher(
      this.geometries,
      // The stitcher reconciled seam normals in the chunk's own geometry; the batch holds a copy, so
      // write that attribute's range for the layers it touched — zero frames, cost ∝ the normals.
      (ck) => {
        const g = this.geometries.get(ck);
        if (g) this.terrain.updateChunkNormals(ck, g.takeNormalsDirty());
      },
      // Display-true ownership predicate (M8): the flush runs after the walk, so this reads the
      // CURRENT frame's verdicts.
      (key) => this.terrain.isVisible(key),
      // Apron sink: seam strips are geometries in the batch's apron layers (TerrainBatch.setApron).
      (key, face, layer, apron) => this.terrain.setApron(key, face, layer, apron),
      // A band mask (or its restore) rewrote the chunk's INDEX buffer in place — write that range.
      (ck) => {
        const g = this.geometries.get(ck);
        if (g) this.terrain.updateChunkIndices(ck, g.takeIndicesDirty());
      },
      // Owner-level skirt sink: ONE strip per (coarse owner, layer), because the owner's own corners
      // have to be internal to a single strip. See SkirtManager.ts and docs/display-contract.md C3.
      (ownerKey, layer, skirt) => this.terrain.setSkirt(ownerKey, layer, skirt),
      // The seam's sign source. Case L samples the fine chunk's +a same-LOD neighbour, which is
      // resident but not displayed and so unreachable through `geometries`.
      (cx, cy, cz, lod) => this.chunkAt(cx, cy, cz, lod),
      // Geometry and display by packed key — the resolve path's lookups, no key strings.
      (packed) => this.geometryIndex.get(packed),
      (packed) => this.terrain.isVisiblePacked(packed),
    );

    // THE ONE COMMIT POINT (C4). The walk only DECIDES (pendingDisplayDecisions); this applies —
    // flips grouped into parent↔octet transactions that land whole or not at all, skirt
    // settlement in the same call, and the 1 Hz C1 audit. See DisplayCommit's header for the
    // veto/deferral rules.
    this.displayCommit = new DisplayCommit({
      applyFlip: (key, visible) => this.terrain.setVisible(key, visible),
      onFlipApplied: (key) => this.seamStitcher.onDisplayChanged(key),
      flushSeams: (budgetEnd?: number) => {
        perfStats.begin('skirts'); // its own stage, taken out of the enclosing `commit`
        this.seamStitcher.flush(budgetEnd);
        perfStats.end('skirts');
      },
      // Admission order only, so lod-0-chunk units are precise enough — and lastPlayerChunk is
      // exactly the frame of reference the walk itself ranks by.
      distanceSq: (key) => {
        const p = this.lastPlayerChunk;
        if (!p) return 0;
        const { cx, cy, cz, lod } = parseChunkKey(key);
        const s = 1 << lod;
        const dx = (cx + 0.5) * s - (p.cx + 0.5);
        const dy = (cy + 0.5) * s - (p.cy + 0.5);
        const dz = (cz + 0.5) * s - (p.cz + 0.5);
        return dx * dx + dy * dy + dz * dz;
      },
      onDeferred: () => this.dirtyVisibility(),
      // The skirt owners a flip hands to the flush — the commit's real cost, charged at ownerCostMs each.
      flipOwners: (key) => this.seamStitcher.priorityOwnersFor(key),
      ownerCostMs: () => this.seamStitcher.estimatedOwnerCostMs(),
    });


    // New/updated geometry may need its visibility evaluated, invalidates the cached
    // mesh count (polled every frame for the debug overlay), and its seams need
    // reconciling with neighbors. An INCOMPLETE mesh in the render set triggers margin healing
    // so it converges to drawable instead of pinning its LOD parent forever.
    this.remeshPipeline.addListener((key) => {
      this.dirtyVisibility();
      this.meshCountDirty = true;
      this.seamStitcher.onGeometryChanged(key);
      // Track the frontier's incomplete set as phases are applied, so the convergence sweep has an
      // exact, tiny work list (see healStuckIncompleteChunks).
      const applied = this.chunks.get(key);
      if (applied && applied.lod === 0) {
        if (applied.phase === ChunkPhase.MeshedIncomplete || applied.extrapolatedMargins !== 0) {
          this.incompleteChunks.add(key);
        } else { this.incompleteChunks.delete(key); this.incompleteSinceMs.delete(key); }
      }
      this.healIncompleteMargins(key);
    });
  }

  /** Add a chunk to the numeric residency index (call sites: every `chunks.set`). */
  private indexChunk(chunk: Chunk): void {
    const lod = chunk.lod;
    let m = this.chunkIndex[lod];
    if (!m) { m = new Map<number, Chunk>(); this.chunkIndex[lod] = m; }
    m.set(packCoord(chunk.cx, chunk.cy, chunk.cz), chunk);
    this.residentByLod[lod] = (this.residentByLod[lod] ?? 0) + 1;
    if (lod === 0) this.baseArrivals++;
  }

  /** Remove a chunk from the numeric residency index (call sites: every `chunks.delete`). */
  private unindexChunk(chunk: Chunk): void {
    if (this.chunkIndex[chunk.lod]?.delete(packCoord(chunk.cx, chunk.cy, chunk.cz))) {
      this.residentByLod[chunk.lod]--;
    }
  }

  /**
   * THE residency probe: O(1) chunk lookup by coords + EXPLICIT lod — no key string. Coordinate
   * probes of residency go through this (or hasChunkAt), never `chunks.get(chunkKey(...))`:
   * chunkKey is byte-identical to chunkKeyLod at lod 0 and silently disjoint above it, so the
   * string form is a lod-0 landmine as well as an allocation. `this.chunks` (string-keyed) stays
   * as the mesh pipeline's identity map — sites that already hold a key string for allied maps
   * (pending sets, remesh queue, relight targets) keep using it.
   */
  private chunkAt(cx: number, cy: number, cz: number, lod: number): Chunk | undefined {
    return this.chunkIndex[lod]?.get(packCoord(cx, cy, cz));
  }

  /** Residency membership by coords + explicit lod (see chunkAt). */
  private hasChunkAt(cx: number, cy: number, cz: number, lod: number): boolean {
    return this.chunkIndex[lod]?.has(packCoord(cx, cy, cz)) ?? false;
  }

  /**
   * MARGIN HEALING — make a stuck render-set chunk drawable instead of covering it up. A frontier
   * lod0 chunk can mesh MeshedIncomplete because one of its +margin sources is unloaded and
   * nothing will bring it (the source sits one ring past the render set, which the BFS request
   * paths deliberately don't cascade into). Incomplete meshes never draw, so without this the
   * chunk stays 'waiting' and its LOD parent covers it FOREVER (the original stuck-parent bug).
   * The wrong fix — classifying it as covered so the parent releases — left a visible hole (the
   * surface-hole regression). The right fix: request the missing sources directly. They become
   * pending → coverage keeps the parent drawn while we wait → arrival fires the existing −7
   * re-trigger → the chunk re-meshes complete → the swap happens exactly when it is drawable.
   * Bounded: only +7 of render-set incomplete chunks; the healed sources load data-only (their
   * queued remesh is parked by shouldMeshNow), so this cannot cascade ring after ring.
   */
  private healIncompleteMargins(key: string): void {
    const chunk = this.chunks.get(key);
    if (!chunk || chunk.lod !== 0) return; // lod ≥ 1 margins ride desiredLoadSet — always expected
    // UNCONVERGED means either marker: a skipped face (never draws) or an extrapolated source
    // (draws, wrong along that edge/corner). Gating on the phase alone left the diagonal class with
    // no healer at all — if its source is never requested by anything else, the wrong column is
    // permanent, which is the state this whole mechanism exists to end.
    if (chunk.phase !== ChunkPhase.MeshedIncomplete && chunk.extrapolatedMargins === 0) return;
    if (!this.shouldMeshChunk(chunk.cx, chunk.cy, chunk.cz, 0)) return; // never drawn → no healing
    for (let i = 0; i < POSITIVE_MARGIN_OFFSETS_7.length; i++) {
      const [dx, dy, dz] = POSITIVE_MARGIN_OFFSETS_7[i];
      // A COMPLETE chunk only needs the sources it actually extrapolated; requesting the rest
      // would be the unconditional-diagonal churn this pipeline deliberately removed.
      if (chunk.phase === ChunkPhase.MeshedComplete && (chunk.extrapolatedMargins & (1 << i)) === 0) continue;
      const nx = chunk.cx + dx, ny = chunk.cy + dy, nz = chunk.cz + dz;
      const nKey = chunkKey(nx, ny, nz);
      if (this.chunks.has(nKey) || this.pendingChunks.has(nKey)) continue; // here / coming
      if (this.marginPolicyAt(nx, ny, nz, 0) !== 'unknown') continue;      // resolves without loading
      if (this.cachedReachable.has(packCoord(nx, ny, nz))) continue;        // BFS will request it
      // Never request anything the unloader won't retain (shared predicate — see retainsLod0): it
      // would be evicted before the consumer re-meshes, and the re-mesh (still incomplete) would
      // re-request it — a permanent request→evict live-lock at the frontier.
      if (!this.retainsLod0(nx, ny, nz)) continue;
      // A margin in an UNTILED column can't be requested as a chunk: without column info the
      // request path has no vertical extent to check it against, and healing it as a single used to
      // race the incoming column (hundreds of duplicate generations, each replacement re-queuing
      // neighbours). So ask for the COLUMN instead of skipping.
      //
      // Skipping was a DEADLOCK. requestTileFromServer has exactly one other caller: the tile
      // look-ahead inside requestVisibleChunks, which only walks the BFS's `toRequest` list. Once
      // streaming settles that list is empty, so no tile is ever requested for a column beyond the
      // frontier again — while `marginPolicyAt` reports every position in an untiled column as
      // 'unknown' and `isMarginSourceExpected` reports it as not-coming, so the consumer meshes
      // MeshedIncomplete immediately and stays hidden forever. Nothing else could break the cycle:
      // healing waited for the tile pipeline, the tile pipeline waited for a request, and the only
      // requester had gone quiet. That was the `[incomplete,sel=T,hid,none]` hole — permanent,
      // and uncovered because coarse levels legitimately draw nothing in a canopy band (no stamps).
      if (!this.columnInfo.has(packColumn(nx, nz, 0))) {
        this.healMissingColumn(nx, nz);
        continue;
      }
      this.requestChunkFromServer(nx, ny, nz);
    }
  }

  /**
   * Request the tile/column for an untiled column blocking an incomplete chunk's margin. Deduped by
   * `pendingTiles`/`pendingColumns` and, after arrival, by `columnInfo` — so each column is asked for
   * once. Unbudgeted on purpose: the columns reachable this way are the ones adjacent to render-set
   * chunks, i.e. one ring, and a budget here would need its own retry path to avoid re-deadlocking.
   */
  private healMissingColumn(cx: number, cz: number): void {
    const colKey = packPairKey(cx, cz);
    if (this.pendingTiles.has(colKey) || this.pendingColumns.has(colKey)) return; // already coming
    this.requestTileFromServer(cx, cz);
  }

  /**
   * THE LOD-0 CONVERGENCE INVARIANT, enforced every streaming pass: a chunk in the render set that
   * meshed INCOMPLETE must be making progress toward drawable. It is the fine-level counterpart of
   * requeueUnmeshedLodChunks, and it exists because "heal on apply" was a one-shot: healing ran from
   * the remesh listener, so a chunk whose heal was declined (or whose margin arrived by a path that
   * didn't re-queue it) never got a second chance — it just stayed hidden.
   *
   * Two actions per chunk, both idempotent:
   *  - re-heal (which now includes asking for a missing column), and
   *  - re-add it to the remesh queue. The queue is the right place to wait: process() re-tests
   *    marginSourcesReady every pass and parks the entry until its margins are real, and isBusy
   *    ignores non-dispatchable entries, so a parked entry blocks nothing.
   *
   * The set is maintained where phase is applied, so this scans the frontier (tens of chunks), never
   * the whole world.
   */
  private healStuckIncompleteChunks(): void {
    if (this.incompleteChunks.size === 0) return;
    const now = Date.now();
    let extrapolatedDrawn = 0;
    for (const key of this.incompleteChunks) {
      const chunk = this.chunks.get(key);
      const incomplete = chunk?.phase === ChunkPhase.MeshedIncomplete;
      const masked = (chunk?.extrapolatedMargins ?? 0) !== 0;
      if (!chunk || (!incomplete && !masked)) {
        this.incompleteChunks.delete(key);
        this.incompleteSinceMs.delete(key);
        continue;
      }
      // Outside the render set it is not meant to draw; nothing to converge to.
      if (!this.shouldMeshChunk(chunk.cx, chunk.cy, chunk.cz, 0)) continue;

      this.healIncompleteMargins(key);
      // Re-queue only the class that cannot DRAW. A drawn-but-extrapolated chunk already has a
      // usable mesh and its re-mesh is triggered by the missing source ARRIVING (the −7 trigger
      // reads the mask); re-dispatching it every pass instead would mesh the same unchanged inputs
      // forever — the redundant-dispatch churn this pipeline removed.
      if (incomplete) this.remeshPipeline.add(key);
      else { extrapolatedDrawn++; continue; }

      // Still stuck after a long while? Name the margins that can't be resolved. This is the
      // tripwire the class of bug needed: it hid behind a "by design" comment for weeks because the
      // only symptom was a HOLE chain that had to be read backwards.
      const since = this.incompleteSinceMs.get(key);
      if (since === undefined) {
        this.incompleteSinceMs.set(key, now);
      }
    }
  }

  /** Cached count of chunks with visible geometry (recomputed only when dirty). */
  private cachedMeshCount = 0;
  private meshCountDirty = true;

  /**
   * Initialize the world.
   * Marks as initialized - chunks come from server.
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;
  }

  /**
   * Register a listener called when a chunk remesh result is applied.
   */
  addRemeshListener(listener: (chunkKey: string) => void): void {
    this.remeshPipeline.addListener(listener);
  }

  /**
   * Remove a previously registered remesh listener.
   */
  removeRemeshListener(listener: (chunkKey: string) => void): void {
    this.remeshPipeline.removeListener(listener);
  }

  /**
   * Mark visibility as dirty so the next update rescans all geometries.
   * Call when external state that affects visibility changes (e.g. previewChunks).
   */
  markVisibilityDirty(): void {
    this.dirtyVisibility();
  }

  /**
   * Register a listener called when a chunk is unloaded.
   */
  addUnloadListener(listener: (chunkKey: string) => void): void {
    this.unloadListeners.add(listener);
  }

  /**
   * Remove a previously registered unload listener.
   */
  removeUnloadListener(listener: (chunkKey: string) => void): void {
    this.unloadListeners.delete(listener);
  }

  /**
   * Set the camera for frustum culling.
   */
  setCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }

  /**
   * Set visibility radius dynamically (quality settings).
   * Invalidates BFS cache so chunks update next frame.
   */
  setVisibilityRadius(radius: number): void {
    if (radius === this._visibilityRadius) return;
    this._visibilityRadius = radius;
    this.terrain.setViewDistance(radius);
    // Invalidate BFS cache and force a mesh-visibility/unload rescan so the
    // change takes effect immediately even when the player isn't moving.
    this.lastBFSChunk = null;
    this.dirtyVisibility();
  }

  get visibilityRadius(): number {
    return this._visibilityRadius;
  }

  /**
   * Set how many coarser far-view LOD levels stream in around the base region (0 = off — the world
   * renders exactly as without the LOD system). Invalidate the BFS/visibility so shells recompute.
   */
  setFarLodLevels(levels: number): void {
    const clamped = Math.max(0, Math.min(MAX_FAR_LOD_LEVELS, Math.floor(levels)));
    if (clamped === this._farLodLevels) return;
    this._farLodLevels = clamped;
    this.lastBFSChunk = null;
    this.dirtyVisibility();
  }

  get farLodLevels(): number {
    return this._farLodLevels;
  }

  /**
   * Set the zoom-driven base LOD target (explore zoom-out coarsens the central region; first
   * person pins 0). displayBaseLod steps toward it one level at a time in updateLodStreaming.
   */
  setTargetBaseLod(target: number): void {
    const clamped = Math.max(0, Math.min(this.maxBaseLod, Math.floor(target)));
    if (clamped === this.targetBaseLod) return;
    this.targetBaseLod = clamped;
    this.lastBaseLodStepMs = Date.now();
    this.lastBFSChunk = null;
    this.dirtyVisibility();
  }

  /**
   * How coarse the central region may get right now: the zoom-out ceiling when far rings are on, else
   * pinned to 0 (with no rings there is no coarse level to step to). THE single derivation — GameCore
   * reads it for the zoom policy and the orbit range, and it used to be spelled `farLodLevels > 0 ? 3 : 0`
   * in both places.
   */
  get maxBaseLod(): number {
    return this._farLodLevels > 0 ? MAX_BASE_LOD : 0;
  }

  get currentBaseLod(): number {
    return this.displayBaseLod;
  }

  /** Cached per-LOD summary for the debug overlay ("0:1241/774/599 1:214/125/79"). */
  private lodSummaryCache = '';
  private lodSummaryCacheMs = 0;
  /** Chunks CONTRIBUTING PIXELS, all levels — the denominator the panel's waste figure needs. */
  private drawnTotalCache = 0;

  /**
   * THE WHOLE LADDER IN ONE ROW: `[0]✓ 1241/774/599  1▶u12f3 64/64/12  2· 8/8/0 -2  wd+1`.
   *
   * Per level: the displayed base in brackets, the fine-first gate glyph (see lodGateGlyph), then
   * `RESIDENT/MESHED/DRAWN`, then the decimation deficit when non-zero. It replaces four rows that
   * were all indexed by the same thing — base, gate, decim and the counts — where reading one fault
   * meant looking the same level up four times, for three of the panel's widest rows.
   *
   * ALL THREE COUNTS, because the differences between them are where every streaming fault of the
   * last week actually lived and the single resident number showed none of them: 1241 resident against
   * 774 meshed is the residency ratio, 774 meshed against 599 drawn is mesh work spent on nothing,
   * and comparing the DRAWN column across levels is what says whether a coarse level is beating a
   * finer one to the screen.
   *
   * Residency is read off the maintained index (free). Meshed and drawn need a scan, which is why
   * this whole thing sits behind the 500 ms cache it always had — at ~1200 chunks that is a few
   * thousand map reads twice a second, against a panel that re-renders on change only.
   */
  getLodStatusSummary(): string {
    const now = Date.now();
    if (now - this.lodSummaryCacheMs < 500) return this.lodSummaryCache;
    this.lodSummaryCacheMs = now;
    const meshed: number[] = [];
    const drawn: number[] = [];
    let meshedTotal = 0;
    let neverShown = 0;
    for (const [key, geo] of this.geometries) {
      if (!geo.hasGeometry()) continue;
      const lod = geo.chunk.lod;
      meshed[lod] = (meshed[lod] ?? 0) + 1;
      meshedTotal++;
      if (this.terrain.isChunkDisplayed(key)) drawn[lod] = (drawn[lod] ?? 0) + 1;
      else if (!this.everShown.has(key)) neverShown++;
    }
    // THE PERCENTAGE IS THE WHOLE SIGNAL. The raw count scales with view distance and the superseded
    // tally is the staircase working as designed — both were noise beside the one number that says
    // whether geometry is being built for nothing.
    this.wasteSummaryCache = meshedTotal > 0
      ? `${Math.round((neverShown / meshedTotal) * 100)}%` : '0%';

    // ONE ROW FOR THE WHOLE LADDER. The base level, the gate state, the three counts and the
    // decimation deficit are all indexed by the same thing, and as four separate rows reading a
    // fault meant looking the same level up four times.
    const active = this.lodManager.active;
    const deficit = active ? this.lodManager.deficitByLod : [];
    const topLod = Math.max(active ? this.lodManager.maxLod : 0, this.residentByLod.length - 1);
    const parts: string[] = [];
    let drawnTotal = 0;
    for (let lod = 0; lod <= topLod; lod++) {
      const res = this.residentByLod[lod] ?? 0;
      const gate = active ? this.lodGateGlyph(lod) : '';
      if (res === 0 && gate === '') continue;
      drawnTotal += drawn[lod] ?? 0;
      const name = lod === this.displayBaseLod ? `[${lod}]` : `${lod}`;
      const dec = (deficit[lod] ?? 0) > 0 ? ` -${deficit[lod]}` : '';
      parts.push(`${name}${gate} ${res}/${meshed[lod] ?? 0}/${drawn[lod] ?? 0}${dec}`);
    }
    if (active && this.decimateFrom > 0) parts.push(`dec${this.decimateFrom}→${this.stepLod}`);
    if (active && this.gateRelief > 0) parts.push(`wd+${this.gateRelief}`);
    this.drawnTotalCache = drawnTotal;
    this.lodSummaryCache = parts.join('  ');
    return this.lodSummaryCache;
  }

  /**
   * One level's fine-first gate state: `✓` RESOLVED, `▶` open and filling, `·` held by the
   * CEILING, `▪` the decimation step this pass leads with. An open level prints how much of its own
   * region is still unresolved (`r`) alongside the queues that are meant to be resolving it —
   * `u`nissued, in-`f`light, `m` when mesh work is dispatchable. The `r` term is the one that made
   * the panel honest: the three queues all read empty on a level with nothing on screen.
   */
  private lodGateGlyph(lod: number): string {
    if (lod === 0) return this.baseComplete ? '✓' : '▶';
    if (lod > this.openMaxLod) return '·';
    if (lod === this.stepLod) return '▪';
    if (this.regionResolved(lod)) return '✓';
    const row = this.regionRows[lod];
    const left = row ? row.wanted - row.resolved : 0;
    return `▶r${left}u${this.lodUnissued[lod] ?? 0}f${this.pendingCountAtLod(lod)}`
      + (this.remeshPipeline.hasDispatchableAtLod(lod) ? 'm' : '');
  }

  /** Drawn total from the last getLodStatusSummary() — call it first; both share the 500 ms cache. */
  private wasteSummaryCache = '';

  /** Share of built meshes NEVER shown — geometry built for nothing; `0%` on a settled world.
   *  Refreshed by getLodStatusSummary, which already walks every geometry; call that first. */
  getWasteSummary(): string {
    return this.wasteSummaryCache;
  }

  getDrawnTotal(): number {
    return this.drawnTotalCache;
  }

  private lodGateCache = '';
  private lodGateCacheMs = 0;

  /**
   * THE FINE-FIRST LADDER, for the debug panel and the audit: `base✓ L1▶u12/f3/m1 L2·`.
   *
   * `✓` finished, `▶` open and filling, `·` held by the CEILING, `▪` held by the FLOOR (a coarser
   * level is being stepped down first) — which end is blocking is the first thing you need to know. The open level prints the three inputs its
   * completion is decided on — unissued / in-flight / dispatchable-mesh — because a stalled ladder is
   * only actionable if it says WHICH of the three is not draining. `wd` appears when the watchdog has
   * force-opened levels, which is the difference between "filling in order" and "a level wedged and
   * we gave up on it".
   *
   * Cached on the same 500 ms window as getLodStatusSummary: each level's test costs a queue scan.
   */
  getLodGateSummary(): string {
    const now = Date.now();
    if (now - this.lodGateCacheMs < 500) return this.lodGateCache;
    this.lodGateCacheMs = now;
    if (!this.lodManager.active) return (this.lodGateCache = 'rings off');
    const parts: string[] = [];
    for (let lod = 0; lod <= this.lodManager.maxLod; lod++) {
      parts.push(`${lod === 0 ? 'base' : `L${lod}`}${this.lodGateGlyph(lod)}`);
    }
    if (this.decimateFrom > 0) parts.push(`dec${this.decimateFrom}→${this.stepLod}`);
    if (this.gateRelief > 0) parts.push(`wd+${this.gateRelief}`);
    return (this.lodGateCache = parts.join(' '));
  }

  // ---- Stale pending request cleanup ----

  /**
   * Remove entries from a pending set whose timestamps exceed REQUEST_TIMEOUT_MS.
   * Returns true if any entries were cleaned (caller may want to invalidate BFS).
   */
  private expireStale<K>(
    pending: Set<K>, timestamps: Map<K, number>, now: number, skip?: (key: K) => boolean,
  ): boolean {
    let cleaned = false;
    for (const [key, time] of timestamps) {
      if (skip?.(key)) continue;
      if (now - time > REQUEST_TIMEOUT_MS) {
        pending.delete(key);
        timestamps.delete(key);
        cleaned = true;
      }
    }
    return cleaned;
  }

  /**
   * Sweep all pending sets for stale entries.
   * Called once per update() before BFS so freed slots are available for new requests.
   */
  private cleanStalePending(): void {
    const now = Date.now();
    // A chunk whose payload is queued for ingest is NOT stale — its voxels are already in hand, only
    // the apply is paced. Expiring it would delete the pending entry and re-request data we have.
    const hadStaleChunks = this.expireStale(this.pendingChunks, this.pendingChunkTimes, now,
      (key) => this.ingestPayloads.has(key));
    const hadStaleTiles = this.expireStale(this.pendingTiles, this.pendingTileTimes, now);
    const hadStaleColumns = this.expireStale(this.pendingColumns, this.pendingColumnTimes, now);
    this.expireStale(this.pendingLodChunks, this.pendingLodChunkTimes, now,
      (key) => this.ingestPayloads.has(key));
    this.expireStale(this.pendingLodColumns, this.pendingLodColumnTimes, now);

    if (hadStaleChunks || hadStaleTiles || hadStaleColumns) {
      // Re-discover and re-request on the next pass. The BOX is still valid (same centre, same
      // radius) — nulling lastBFSChunk here blanked every baseChunkState to 'outside' and bailed
      // assertVolumeOwnership until the next pass, exactly when a stale sweep most needs them (M3).
      this.dirtyVisibility();

      // If the initial surface column timed out, allow re-request
      if (hadStaleColumns && this.pendingColumns.size === 0 && this.columnInfo.size === 0) {
        this.initialColumnRequested = false;
        this.streamingBootstrapped = false;
      }
    }
  }

  /**
   * ChunkProvider interface — get a chunk by coordinates (lod 0). Coordinate-keyed, not string-keyed:
   * the BFS asks once per traversed cell (~1.7k per pass) and already holds the coordinates, so a key
   * string would be pure garbage. Reads the numeric residency index.
   */
  getChunkAt(cx: number, cy: number, cz: number): Chunk | undefined {
    return this.chunkAt(cx, cy, cz, 0);
  }

  /** ChunkProvider interface — check if a chunk is pending, by coordinates. */
  isPendingAt(cx: number, cy: number, cz: number): boolean {
    return this.pendingChunks.has(chunkKey(cx, cy, cz));
  }

  /** True while a chunk request is outstanding (string-keyed; used by the mesher's margin logic). */
  isPending(key: string): boolean {
    return this.pendingChunks.has(key);
  }

  /**
   * ChunkProvider interface — true ONLY for genuine open sky (above the column's content top), where
   * no voxel data exists and none will ever load. Lets the visibility BFS traverse through empty sky
   * to reach terrain beyond it, while unloaded terrain (cy <= maxCy) and unknown columns (no tile
   * yet) return false so they must load before being traversed — we never see through unloaded rock.
   */
  isEmptyAir(cx: number, cy: number, cz: number): boolean {
    const info = this.columnInfo.get(packColumn(cx, cz, 0));
    return info ? cy > info.maxCy : false;
  }

  /** The clip and the heights for a column, from one tile — always set together. */
  private setColumnInfo(tx: number, tz: number, heights: ArrayLike<number>): void {
    const key = packColumn(tx, tz, 0);
    const range = columnChunkRange(heights);
    // Widen by the column's EDITED chunks (the only persisted ones): heights come from generation,
    // so a build above the terrain surface would otherwise be classified sky and never requested on
    // reload. The lod-0 counterpart of the coarse lod_clips widening; over-wide is one empty mesh,
    // too narrow is an invisible build.
    if (this.isLocal) {
      const edited = editedChunkRange(tx, tz);
      if (edited) {
        if (edited.minCy < range.minCy) range.minCy = edited.minCy;
        if (edited.maxCy > range.maxCy) range.maxCy = edited.maxCy;
      }
    }
    this.columnOwnRange.set(key, range);
    this.columnBorders.set(key, borrowedBorders(heights));
    // This column reads three neighbours' borders; three neighbours read this column's. All four
    // clips are re-derived, and a neighbour whose clip moved needs a walk: the chunk it now owns
    // reads as unloaded terrain rather than sky from here on, and the BFS is what requests it.
    this.publishColumnRange(tx, tz);
    let moved = false;
    for (const [dx, dz] of COLUMN_BORROWERS) {
      if (this.publishColumnRange(tx + dx, tz + dz)) moved = true;
    }
    if (moved) this.dirtyVisibility();
    if (this.columnHeights.size >= VoxelWorld.MAX_COLUMN_HEIGHTS) this.columnHeights.clear();
    this.columnHeights.set(key, heights);
  }

  /** Recompute one column's clip from its own range and the borders it borrows. True if it changed. */
  private publishColumnRange(cx: number, cz: number): boolean {
    const key = packColumn(cx, cz, 0);
    const own = this.columnOwnRange.get(key);
    if (!own) return false;
    const range = widenedColumnRange(
      own,
      this.columnBorders.get(packColumn(cx + 1, cz, 0)),
      this.columnBorders.get(packColumn(cx, cz + 1, 0)),
      this.columnBorders.get(packColumn(cx + 1, cz + 1, 0)),
    );
    const prev = this.columnInfo.get(key);
    if (prev && prev.minCy === range.minCy && prev.maxCy === range.maxCy) return false;
    this.columnInfo.set(key, range);
    return true;
  }

  /**
   * The surface height at this world position in metres, or null while its TILE has not arrived.
   *
   * Available before any chunk is resident, which is what makes it usable as the streaming
   * centre's seed: the centre must be at the surface BEFORE streaming starts, or the first fill is
   * generated underground and thrown away.
   *
   * AT THIS COLUMN, NOT THE TILE'S HIGHEST POINT. It used to answer with the max over the whole 32×32
   * footprint — 8 m × 8 m — which was harmless while the tallest thing on a column was a tree and is
   * not now that a city puts 160 m towers next to 6 m roads. A road is narrower than a tile, so its
   * tile almost always covers a lot as well: measured over one city, 47% of columns were handed a
   * height that was not their own, and 54% of the BARE-GROUND columns — the roads and the empty lots —
   * reported a building's roof, by 15.5 m on average and 81 m at worst. `isBelowTerrainAt` below has
   * always documented this hazard and answers from the LOW bound to avoid it; this is the same
   * hazard at the other end.
   */
  surfaceHeightAt(x: number, z: number): number | null {
    const cx = Math.floor(x / CHUNK_WORLD_SIZE), cz = Math.floor(z / CHUNK_WORLD_SIZE);
    const heights = this.columnHeights.get(packColumn(cx, cz, 0));
    return heights ? columnHeightFromTile(heights, x, z, cx, cz) : null;
  }

  /**
   * Is this world Y unambiguously BELOW the terrain of its column — under even the column's lowest
   * surface point? (Null while the tile has not arrived.)
   *
   * Deliberately the lowest point, not the highest: a column spans a 32×32 footprint, so its
   * highest surface can be tens of metres above the ground directly under a given spot, and
   * comparing against that would call a perfectly sensible camera height "underground". This is the
   * conservative form — true means there is no reading of the terrain under which you are outside
   * it — which is what makes it usable as an assertion.
   */
  isBelowTerrainAt(x: number, y: number, z: number): boolean | null {
    const cx = Math.floor(x / CHUNK_WORLD_SIZE), cz = Math.floor(z / CHUNK_WORLD_SIZE);
    const info = this.columnInfo.get(packColumn(cx, cz, 0));
    if (!info) return null;
    return y < info.minCy * CHUNK_WORLD_SIZE;
  }

  /**
   * May the player be PLACED at this eye position yet? (See spawnReadiness.ts for the rule.)
   *
   * The gate exists because coarse terrain is drawn wherever lod-0 has not arrived: a spot can
   * look solid while the physics engine, which reads lod-0 only, knows nothing about it — so
   * spawning there freezes the player in mid-air over a surface they can see, rather than putting
   * them on it. The probe is lod-0 residency + mesh completeness + provable sky, which is exactly
   * the vocabulary the freeze contract is written in.
   */
  isSpawnReadyAt(x: number, y: number, z: number): boolean {
    return isSpawnReadyAt(this.spawnProbe, x, y, z);
  }

  /**
   * Is the chunk that contains (x, y, z) at the given LOD actually CONTRIBUTING PIXELS right now?
   * The check goes through the TerrainBatch (not just the chunk's phase) on purpose: a chunk can be
   * MeshedComplete — geometry built, drawable — and still be hidden by the walk or taken by the preview. The explore Play button waits on this for its "play" mode because
   * a chunk that's "meshed but not displayed" is a hole from the user's perspective.
   */
  isChunkDisplayedAt(x: number, y: number, z: number, lod: number = 0): boolean {
    const { cx, cy, cz } = worldToChunk(x, y, z);
    return this.terrain.isChunkDisplayed(chunkKeyLod(cx, cy, cz, lod));
  }

  private readonly spawnProbe: SpawnProbe = {
    isResident: (cx, cy, cz) => this.chunkAt(cx, cy, cz, 0) !== undefined,
    isMeshed: (cx, cy, cz) => this.chunkAt(cx, cy, cz, 0)?.phase === ChunkPhase.MeshedComplete,
    isKnownAir: (cx, cy, cz) => this.isEmptyAir(cx, cy, cz),
  };

  /**
   * Margin policy for the mesher (see ChunkMesher.setMarginPolicy): resolve an absent neighbour
   * position to 'air' (provable open sky), 'solid' (provably below every surface in the coarse
   * column footprint), or 'unknown' (must load before the face can be built).
   */
  marginPolicyAt(cx: number, cy: number, cz: number, lod: number): MarginPolicy {
    if (lod === 0) {
      return this.isEmptyAir(cx, cy, cz) ? 'air' : 'unknown';
    }
    const info = this.lodColumnInfo.get(packColumn(cx, cz, lod));
    if (!info) return 'unknown';
    if (cy > info.maxCy) return 'air';
    if (cy < info.minCy) return 'solid';
    return 'unknown';
  }

  /**
   * PROVABLY NOTHING TO DRAW at this position — the resolution census's second term (see
   * LodResidencyManager.computeRegionResolution). Above lod 0 the column clip settles it: sky above
   * `maxCy`, provably solid below `minCy`, and 'unknown' until the probe lands — so a level whose
   * columns have never been surveyed can never read resolved. At lod 0 it is the base
   * classification's own vocabulary: 'sky' (nothing exists) and 'occluded' (unseeable), the two
   * states the render gate draws nothing for. 'waiting' deliberately is NOT one of them, which is
   * how an active BFS frontier keeps the base region open without a separate frontier term.
   */
  private provablyEmpty(cx: number, cy: number, cz: number, lod: number): boolean {
    if (lod > 0) return this.marginPolicyAt(cx, cy, cz, lod) !== 'unknown';
    const state = this.baseChunkState(cx, cy, cz);
    return state === 'sky' || state === 'occluded';
  }

  /** Delegates to CoverageCore — the single home of the classification (see CoverageCore.ts). */
  private isBaseChunkCovered(cx: number, cy: number, cz: number): boolean {
    return this.coverageCore.isBaseCovered(cx, cy, cz);
  }

  /** Delegates to CoverageCore.baseChunkState — see its doc for the five states' meaning. */
  private baseChunkState(cx: number, cy: number, cz: number): 'sky' | 'outside' | 'occluded' | 'drawable' | 'waiting' {
    return this.coverageCore.baseChunkState(cx, cy, cz);
  }

  /**
   * P4 mesh-readiness: is this positive margin-source neighbour EXPECTED to load but not here yet?
   * If so, the consumer should defer meshing so its high-side border is built once with real voxels
   * instead of extrapolated-now / re-meshed-when-it-arrives.
   *   - loaded            → not expected (ready to use)
   *   - policy-resolved (open sky / provably solid) → never loads, ready
   *   - pending or reachable-but-unloaded → the BFS/ring streamer will bring it → EXPECTED, wait
   *   - anything else (not loaded, not void, not wanted) → not coming soon → ready (mesh now; P8's
   *     re-mesh trigger still corrects a rare late arrival)
   */
  private isMarginSourceExpected(cx: number, cy: number, cz: number, lod: number = 0): boolean {
    const key = chunkKeyLod(cx, cy, cz, lod);
    if (this.chunks.has(key)) return false;
    if (this.marginPolicyAt(cx, cy, cz, lod) !== 'unknown') return false;
    if (lod === 0) {
      if (this.pendingChunks.has(key) || this.cachedReachable.has(packCoord(cx, cy, cz))) return true;
      // A margin in a column whose TILE is still in flight belongs to the tile pipeline: the
      // column ingest will bring the chunk (or the clip will resolve it). Meshing now would bake
      // a skipped boundary that flaps hidden until the column lands — the +X/+Z leading-edge gap
      // (meshes read POSITIVE margins, so panning +X/+Z always fronts into unarrived columns;
      // −X/−Z fronts onto already-loaded ones, which is why the gaps were one-sided). A column
      // with no tile AND no pending tile is the true edge of the streamed world — mesh incomplete
      // as designed.
      const colKey = packPairKey(cx, cz);
      return !this.columnInfo.has(packColumn(cx, cz, 0))
        && (this.pendingTiles.has(colKey) || this.pendingColumns.has(colKey));
    }
    // Coarse: in flight, or in the level's load set but still queued behind the request budget —
    // the ring streamer will bring both (the lod≥1 mirror of "pending or reachable-but-unloaded";
    // treating budget-queued margins as never-coming made coarse chunks mesh early with
    // extrapolated borders, sit hidden as MeshedIncomplete, and heal only on the −7 re-trigger).
    if (this.pendingLodChunks.has(key)) return true;
    return this.lodManager.desiredLoadSet(lod)?.has(packCoord(cx, cy, cz)) ?? false;
  }

  /**
   * Update the world based on player position.
   * Uses visibility BFS for loading and rendering.
   * @param playerPos Player world position
   */
  update(playerPos: THREE.Vector3): void {
    if (!this.initialized) return;

    // ONE FRAME BUDGET FOR ALL VOXEL WORK (voxel/frameBudget.ts). Every main-thread consumer below —
    // applies, ingest, the display commit, skirts, merges, dispatch — runs against this single
    // deadline in priority order, "at least one, then until the clock". Sized from the frame's measured
    // headroom: max(refresh period, GPU time) minus the frame's other CPU cost. The walk cannot be
    // split, but it is CHARGED: a long walk leaves less for what follows it.
    const snap = perfStats.getSnapshot();
    const allowance = voxelAllowanceMs({
      refreshPeriodMs: refreshPeriodMs(perfStats.minFrameIntervalMs),
      gpuMs: snap.gpu,
      fixedCpuMs: Math.max(0, snap.gameUpdate - snap.voxelUpdate),
      frameIntervalMs: snap.frameInterval,
    });
    perfStats.setVoxelBudget(allowance);
    const budgetEnd = performance.now() + allowance;
    this.frameBudgetEnd = budgetEnd;

    // Apply finished worker meshes first. They arrive in onmessage — between frames, outside every
    // budget — so a main-thread stall let them pile up and land all at once (measured: a frame
    // applying 222 of them, ~14 ms that appeared in no PERF term). Draining here, before the
    // visibility work, keeps the cost accounted AND lets this frame's walk see what just landed.
    perfStats.begin('applies');
    this.remeshPipeline.drainApplies(budgetEnd);
    perfStats.end('applies');

    // Same reasoning one step earlier in the pipeline: streamed chunk payloads are applied here
    // instead of in the callback that delivered them. Before the apply drain would be wrong —
    // an ingest queues a remesh, and its result cannot land in the same frame anyway.
    // (`ingest` excludes the `lighting` timed inside it — see PerformanceStats.EXCLUDE_FROM.)
    perfStats.begin('ingest');
    this.drainIngest(budgetEnd);
    // Neighbour relights the ingests above (or last frame's) could not afford — see relightNeighbor.
    this.drainDeferredRelights(budgetEnd);
    perfStats.end('ingest');

    // Get player's current chunk
    const playerChunk = worldToChunk(playerPos.x, playerPos.y, playerPos.z);
    this.lastPlayerChunk = { ...playerChunk };

    // Expire stale pending requests so they can be re-requested
    this.cleanStalePending();

    // Snapshot the "did anything move" signal BEFORE updateWithVisibility clears it — the remesh
    // pipeline uses it to skip its per-frame sort+scan when nothing can have become dispatchable.
    const worldChanged = this.visibilityDirty
      || !this.lastBFSChunk
      || playerChunk.cx !== this.lastBFSChunk.cx
      || playerChunk.cy !== this.lastBFSChunk.cy
      || playerChunk.cz !== this.lastBFSChunk.cz;

    // Use visibility-based loading
    this.updateWithVisibility(playerChunk, playerPos);

    // Dispatch from remesh queue to workers (async meshing). Timed as an audit section too, not just
    // for the panel: without it the whole remesh cost fell into the STEADY line's unaccounted residue,
    // which is where the thing being hunted would have hidden. getPriorityChunkKeys is inside the
    // section deliberately — it runs every frame BEFORE process()'s early-out gate, and a non-empty
    // result is itself one of the conditions that defeats that gate.
    perfStats.begin('remesh');
    const priorityKeys = this.terrain.getPriorityChunkKeys();
    this.remeshPipeline.process(playerPos, priorityKeys.size > 0 ? priorityKeys : undefined, worldChanged, budgetEnd);
    perfStats.end('remesh');

    // Report queue stats for debug overlay
    perfStats.setVoxelQueueStats(this.remeshPipeline.size, this.pendingChunks.size, this.remeshPipeline.parkedCount);
    perfStats.setMeshDispatches(this.remeshPipeline.meshDispatches);

    // Which condition stopped the remesh pass from early-outing (or 'idle' when it did) — a 'remesh'
    // term with no dispatches behind it means the gate is being defeated and we are paying the sort.

  }

  /**
   * Update using visibility BFS.
   * BFS only runs when player moves to a new chunk (hysteresis).
   * Frustum culling runs every frame for mesh visibility.
   */
  private updateWithVisibility(playerChunk: { cx: number; cy: number; cz: number }, playerPos?: THREE.Vector3): void {
    // Bootstrap: request the initial surface column, then WAIT FOR IT TO ARRIVE.
    //
    // The gate used to release as soon as the column was REQUESTED, which meant the first
    // streaming passes ran with the centre still at its initial y — underground, by hundreds of
    // metres on landform terrain. That fill is entirely wasted (the centre jumps to the surface the
    // moment the column lands, and everything behind it is evicted) and it is what shows the player
    // inside the ground for a moment on load. The column is a TILE: it carries the surface height
    // without any chunk being resident, so waiting for it costs nothing but one round trip.
    const gate = streamingGate({
      initialColumnRequested: this.initialColumnRequested,
      bootstrapped: this.streamingBootstrapped,
      centreColumnKnown: this.columnInfo.has(packColumn(playerChunk.cx, playerChunk.cz, 0)),
      msSinceRequest: this.initialColumnRequestedMs === 0 ? 0 : Date.now() - this.initialColumnRequestedMs,
    });
    this.streamingBootstrapped = gate.bootstrapped;
    if (gate.timedOut) {
      // Loud, once. The cold-start wait exists to avoid one wasted fill; if it ever expires, the
      // column path is broken in a way no probe of mine has reproduced, and I want the report.
      console.warn('[VoxelWorld] initial surface column never arrived — streaming anyway. '
        + 'The first fill may be placed against a guessed height.');
    }
    if (gate.gate === 'request-initial') {
      this.requestInitialSurfaceColumn(playerChunk.cx, playerChunk.cz);
      return;
    }
    if (gate.gate === 'wait-for-column') return; // cold start only — height unknown, don't guess

    // Need camera for frustum culling
    if (!this.camera) {
      console.warn('[VoxelWorld] No camera set, skipping visibility update');
      return;
    }

    // Check if player moved to a new chunk.
    const chunkChanged = !this.lastBFSChunk ||
      playerChunk.cx !== this.lastBFSChunk.cx ||
      playerChunk.cy !== this.lastBFSChunk.cy ||
      playerChunk.cz !== this.lastBFSChunk.cz;

    // LIVENESS: nothing is in flight and the chunk the player is standing in still isn't
    // loaded — re-run discovery even though the player hasn't moved.
    //
    // Streaming used to be driven entirely by movement + frontier changes, which was safe
    // only because a player with no ground beneath them always FELL, and falling changes
    // your chunk. Voxel physics holds a player still when their surroundings are unknown,
    // so "stationary" is now a normal sustained state and can no longer be read as "nothing
    // to do". Without this the world can never come back after a bulk clear or a stalled
    // request round: no movement, no arrivals, therefore no BFS, therefore no requests.
    const starved =
      this.pendingChunks.size === 0 &&
      this.pendingColumns.size === 0 &&
      this.pendingTiles.size === 0 &&
      !this.hasChunkAt(playerChunk.cx, playerChunk.cy, playerChunk.cz, 0);

    // Recompute the visibility BFS when the player crosses a chunk OR the frontier changes (a chunk
    // was meshed / removed, flagged via visibilityDirty). The BFS only traverses through LOADED
    // chunks, so each newly-streamed chunk extends the reachable set by a ring — re-running on
    // frontier change (not only on chunk-cross) is what lets a chunk become visible the moment it
    // loads, instead of waiting for the next cross. Cheap: a zero-alloc typed-array BFS over the
    // radius, and only on frames where the player moved or geometry actually changed.
    if (chunkChanged || this.visibilityDirty || starved) {
      // CLEAR ON ENTRY, RE-ARM DURING THE PASS. Clearing after the sub-steps (where this used to
      // live, below the walk) silently ate every re-arm they raised — including the streaming pump's:
      // requestLodShellData sets `requestsDeferred` when a per-pass budget cuts off the request tail,
      // updateLodStreaming re-arms on it, and three lines later that flag was wiped. The pump only
      // ever ran when some unrelated event happened to dirty visibility again.
      this.visibilityDirty = false;

      let jumped = false;
      if (chunkChanged) {
        const nowMs = performance.now();
        if (!this.centreMoving()) this.centreMoveStartMs = nowMs;
        this.lastCentreMoveMs = nowMs;
        // A JUMP IS A COLD LOAD: the new box cannot overlap the old, so nothing the previous fill
        // achieved covers the destination — re-arm fine-first or every level streams it at once.
        if (this.lastBFSChunk && Math.max(
          Math.abs(playerChunk.cx - this.lastBFSChunk.cx),
          Math.abs(playerChunk.cy - this.lastBFSChunk.cy),
          Math.abs(playerChunk.cz - this.lastBFSChunk.cz),
        ) > this._visibilityRadius) {
          this.baseEverComplete = false;
          jumped = true;
        }
        this.lastBFSChunk = { ...playerChunk };
        const [minX, maxX] = octetAlignedBounds(playerChunk.cx, this._visibilityRadius);
        const [minY, maxY] = octetAlignedBounds(playerChunk.cy, this._visibilityRadius);
        const [minZ, maxZ] = octetAlignedBounds(playerChunk.cz, this._visibilityRadius);
        this.lastBFSAlignedBox = { minX, maxX, minY, maxY, minZ, maxZ };
      }

      // The lod-0 BFS + request path only runs while the base region IS lod 0. At a coarse base
      // (explore zoomed out) fine streaming pauses entirely — the coarse streamer feeds the base
      // level instead — and resumes when zoom refines back to 0.
      //
      // WHAT THE BASE WANTS IS DECIDED HERE; WHEN IT IS SUBMITTED IS BELOW THE RINGS. Both streamers
      // feed one strictly-FIFO worker pool, so the pass's internal order is the fill order — see
      // buildBaseRequestPlan for the measurement. The backlog the coarse path reads is published by
      // the build, so moving the submit does not change what the rings see.
      perfStats.begin('bfs');
      let basePlan: BaseRequestPlan | null = null;
      if (this.displayBaseLod === 0) {
        // Split three ways: the traversal itself, the ×7 margin-source expansion, and the request
        // build. The aggregate read up to 10.5 ms/frame while streaming and there was no way to tell
        // which of the three owned it — and the three want completely different fixes.
        const frustum = getFrustumFromCamera(this.camera);
        const cameraDir = getCameraDirection(this.camera);
        const { reachable, toRequest, frontierActive } = getVisibleChunks(
          playerChunk,
          cameraDir,
          frustum,
          this,
          this._visibilityRadius,
          playerPos
        );
        this.cachedReachable = reachable;
        this.bfsFrontierActive = frontierActive;

        this.addMarginSourceRequests(reachable, toRequest);
        this.addPhysicsPrefetch(playerChunk, toRequest);

        basePlan = this.buildBaseRequestPlan(toRequest, playerPos, reachable);

      } else {
        // THE BACKLOG BELONGS TO THE BLOCK ABOVE, so it has to be cleared here. It is published by
        // buildBaseRequestPlan (`sorted.length`) and read by the coarse path to decide whether the
        // far rings should stand aside — and at a coarse base that publisher never runs. Left
        // alone, the number the last lod-0 pass wrote stands for the whole zoomed-out session: the
        // quota reads "cold load" forever and throttles the outer rings, which at a coarse base are
        // the entire view. Zero is the truth here — the base level has no unrequested lod-0
        // backlog, because there is no lod-0 streaming at all.
        this.baseRequestBacklog = 0;
      }

      perfStats.end('bfs');

      // THE DISPLAY WALK'S CADENCE, decided here because the coarse half of the request pass shares
      // it (below). Everything the walk needs — the ladder, the census, the onion — is recomputed on
      // the same beat, and nothing between beats can use a fresher answer than the walk itself will.
      // IMMEDIATE on the things that must not wait: a jump (the box cannot overlap the old one), a
      // base-LOD step (zoom), and the starved re-discovery. An ordinary chunk crossing is NOT one of
      // them: a glide or a fast pan crosses a chunk nearly every frame, and forcing the walk on each
      // made the pass — walk, eviction, gate, onion — a per-frame cost for the pan's whole duration.
      // BUDGET-AWARE: the walk is the one consumer that cannot stop at the frame's clock, so a merely
      // time-due walk waits for a frame with room for it (its own last cost, against the time left).
      // Forced after twice the interval, so a saturated frame can starve it for at most 100 ms.
      const walkNow = performance.now();
      const sinceWalk = walkNow - this.lastWalkMs;
      const forced = jumped || starved
        || this.displayBaseLod !== this.lastWalkBaseLod
        || sinceWalk >= 2 * VoxelWorld.WALK_MIN_INTERVAL_MS;
      // A commit backlog (transactions deferred for budget) makes the walk due on the first frame with
      // room for it: the deferred work is re-derived by a walk, and waiting out the interval would
      // stretch every budget-limited settle by the interval per step.
      const walkDue = forced
        || ((sinceWalk >= VoxelWorld.WALK_MIN_INTERVAL_MS || this.commitBacklog)
          && this.frameBudgetEnd - walkNow >= this.lastWalkCostMs);

      perfStats.begin('stream');
      // FINE-FIRST decides the rest of this pass, so the whole ladder is answered once here, on this
      // pass's own numbers, and cached (see recomputeLodGate). On the walk's beat only: the census
      // and the onion are the expensive half of the request pass, and between beats the cached
      // answer is the one the walk will read anyway.
      if (walkDue) this.recomputeLodGate();

      // WHO REACHES THE FIFO WORKER POOL FIRST. The pool is strictly first-in-first-out, so the
      // pass's internal order IS the fill order. Coarse normally submits first, deliberately, so the
      // base cannot queue the entire far view behind itself — but while the base is INCOMPLETE that
      // hands coarse the head of the queue on every single pass, which is half of why lod 1 reached
      // the screen at the same moment as lod 0.
      if (!this.baseComplete && basePlan) {
        this.issueBaseRequests(basePlan);
        basePlan = null;
      }

      // Far-view LOD rings: recompute the onion around the center (on the walk's beat) and stream
      // coarse columns/chunks (every pass — see updateLodStreaming).
      this.updateLodStreaming(playerPos ?? new THREE.Vector3(
        playerChunk.cx * CHUNK_WORLD_SIZE, playerChunk.cy * CHUNK_WORLD_SIZE, playerChunk.cz * CHUNK_WORLD_SIZE,
      ), walkDue);

      // ...and only now the base's own jobs. The coarse pass above submitted at most its own small
      // window (MAX_PENDING_LOD_COLS + maxLodInFlight), so the base's bundles still reach the pool
      // within one turnover — what it no longer does is queue the entire far view behind them.
      if (basePlan) {
        this.issueBaseRequests(basePlan);
      }
      perfStats.end('stream');

      if (!walkDue) {
        this.dirtyVisibility();
      } else {
        this.lastWalkMs = walkNow;
        this.lastWalkBaseLod = this.displayBaseLod;

        perfStats.begin('walk');
        this.updateMeshVisibility(this.cachedReachable);
        perfStats.end('walk');
        this.lastWalkCostMs = performance.now() - walkNow;

        // A CHANGED GATE MUST RE-ARM, same reason as the landed zoom step below. recomputeLodGate runs
        // BEFORE this walk, so it decides on the PREVIOUS pass's deficit; the pass where the floor's
        // level finishes therefore computes the new deficit here, one step too late to have acted on
        // it, and needs one more pass to actually lower the floor. Nothing scheduled that pass: the
        // request pump only re-arms for the coarse level loop (lod 0 is not in it), the mesh-apply that
        // woke this pass has already spent its flag, and a settled world raises nothing further. The
        // stall watchdog cannot rescue it either — it only ticks inside recomputeLodGate, so in a
        // wedged world its clock never runs. Symptom: the view stays coarse until an unrelated event
        // dirties visibility, i.e. "pan again and it appears".
        if (this.computeDecimateFrom() !== this.decimateFrom) this.dirtyVisibility();

        // A pending base-LOD step (zoom transition) resolves by TIME (the per-level step timeout)
        // or by ring-coverage progress — neither necessarily raises another visibility event once
        // the world has settled, and setTargetBaseLod resets the timeout clock on every target
        // change, so its own dirty pass always declines to step. Re-arm until the step lands or
        // the wedge is permanent: display sticks at 0 with target 3 and "scroll to zoom out just
        // doesn't load". (The old always-on remesh churn masked this by pumping every frame.)
        if (this.targetBaseLod !== this.displayBaseLod
            && this._farLodLevels > 0 && this.lodGenerationAvailable) {
          this.dirtyVisibility();
        }

        // Convergence sweep for the frontier's incomplete chunks (fine-level counterpart of
        // requeueUnmeshedLodChunks). Per PASS, not per frame: its inputs are the render set and
        // residency, which only move here.
        perfStats.begin('evict');
        this.healStuckIncompleteChunks();
        // NOTE: the set is UNCONVERGED, not just incomplete (see incompleteChunks) — the sweep's own
        // `extrapolatedDrawn` gauge splits out the drawn-but-wrong half, so this number staying up
        // while that one accounts for it is not a wedge.

        // Eviction runs on the pass, not on the frame. Its three inputs — the centre, residency, and
        // the last selection — can only change on a frame that gets here, so the every-frame version
        // was recomputing the same verdict over all ~2k chunks (measured: 0.12 ms/f, ~75% of a settled
        // frame's entire voxel cost). Inside the block it also sees this pass's fresh selection.
        this.unloadDistantChunks();
        this.unloadDistantLodChunks();
        perfStats.end('evict');

        // The invariants are POSTCONDITIONS of this pass (the retention check literally asserts what
        // the unload above just did), so they belong on the same cadence — checking them on frames that
        // changed nothing re-derived the same verdict, and one of them scans every lod-0 chunk.
        this.lastInvariantAssertMs = performance.now();
        perfStats.begin('asserts');
        this.assertChunkInvariants();
        perfStats.end('asserts');
      }
    }

    // Rebuild merged terrain groups (only dirty groups are re-merged).
    // Skip groups that still have chunks in the remesh queue or in-flight on workers
    // to avoid rebuilding the same group dozens of times during loading.
    if (this.lastPlayerChunk) {
      // WELD AFTER THE WALK AND THE UNLOADS, BEFORE THE BAKE (M9). Flushing before the
      // visibility pass meant (a) the display-true predicate read LAST frame's walk, and
      // (b) eviction-driven restores from unloadDistant*Chunks queued a frame late — the
      // stitcher welded fine borders to just-disposed coarse meshes. Here the flush sees this
      // frame's display verdicts and this frame's removals in the same frame.
      // THE COMMIT (C4): this pass's display decisions + their skirt consequences, one call.
      // Decisions are consumed whole — a deferred transaction re-arms a pass (onDeferred) and is
      // re-derived fresh by the next walk, never carried. Evictions ran above (removeChunk stops
      // pixels same-call since phase 3; onRemoved queued the seam consequences), so this flush
      // settles them in the same frame. On quiet frames the commit still runs its flush: that is
      // the ride the stitcher's unresolved retry set (and same-LOD arrivals) depend on.
      //
      // THE FRAME'S CLOCK (FPS tank fix, generalised). The commit's admission, its flushSeams and the
      // batch compaction below all run against the one deadline set at the top of update() — after the
      // applies, the ingest and the walk have taken their share. Skirts can no longer eat the frame
      // regardless of how many owners it admits (the wall-clock check inside reconcileCoarseSkirts
      // stops the loop), and the compaction gets whatever time the stitcher left on the clock — which
      // is what "fold into chunk display" means in practice.
      const displayBudgetEnd = this.frameBudgetEnd;
      const decisions = this.pendingDisplayDecisions;
      if (decisions.size > 0) this.pendingDisplayDecisions = new Map();
      // `commit` excludes the `skirts` flushed inside it (PerformanceStats.EXCLUDE_FROM).
      perfStats.begin('commit');
      const commitStats = this.displayCommit.commit(decisions, displayBudgetEnd);
      perfStats.end('commit');
      perfStats.setSkirtGauges({
        priority: this.seamStitcher.lastSkirtPriority,
        owners: commitStats.owners,
        maxTxOwners: commitStats.maxTxOwners,
        ownerMs: this.seamStitcher.lastOwnerMs,
        ownerMaxMs: this.seamStitcher.lastOwnerMaxMs,
        ownerMaxKey: this.seamStitcher.lastOwnerMaxKey,
        facesEmitted: this.seamStitcher.lastSkirtFacesEmitted,
        facesSkipped: this.seamStitcher.lastSkirtFacesSkipped,
      });
      this.commitBacklog = commitStats.deferred > 0;
      // Recorded HERE rather than sampled by getLodStatusSummary: a staircase step can appear and be
      // superseded well inside that 500 ms window, and counting it as never-shown would invert the
      // one distinction the waste split exists to make. `isVisible` after the commit is the
      // authoritative answer — a deferred decision did not flip and is re-decided next pass.
      for (const [key, want] of decisions) {
        if (want && this.terrain.isVisible(key)) this.everShown.add(key);
      }

      // No merge: the flips above landed in the batches directly. What remains on this clock is
      // room-making — a blocked apply's want, or a pre-emptive growth (TerrainBatch.tick).
      perfStats.begin('compact');
      this.terrain.tick(displayBudgetEnd);
      perfStats.end('compact');
    }

    // The trailing check (after the display pass) is DEV-only, but the dev server is where the game is
    // profiled, and this ran on EVERY frame — scanning all geometries and every lod-0 chunk for the
    // retention predicate — for ~2 ms/frame while streaming. The pass above just ran it; between
    // passes the maps it checks move only through applies and the display pass, so a 250 ms cadence still
    // catches drift within a quarter second instead of a frame.
    const now = performance.now();
    if (now - this.lastInvariantAssertMs >= VoxelWorld.INVARIANT_ASSERT_INTERVAL_MS) {
      this.lastInvariantAssertMs = now;
      perfStats.begin('asserts');
      this.assertChunkInvariants();
      perfStats.end('asserts');
    }
  }

  /** Display-walk cadence while streaming — see the `walkDue` gate in updateWithVisibility. */
  private static readonly WALK_MIN_INTERVAL_MS = 50;
  private lastWalkMs = 0;
  private lastWalkBaseLod = -1;
  /** What the last walk cost, ms — the budget-aware gate asks whether the frame has room for another. */
  private lastWalkCostMs = 0;
  /** This frame's shared deadline for all voxel work — set at the top of update(); see frameBudget.ts. */
  private frameBudgetEnd = 0;

  /** Off-pass cadence for the DEV invariant check — see the call in updateWithVisibility. */
  private static readonly INVARIANT_ASSERT_INTERVAL_MS = 250;
  private lastInvariantAssertMs = 0;

  /**
   * DEV-only cross-projection invariant checks (drift-doc guardrail #4). Run after each visibility
   * update: a violation means two per-chunk representations have drifted apart — the class of bug this
   * codebase keeps producing (a rendered chunk with no data, a pending set out of sync with its time
   * map, geometry left behind after unload). Non-fatal (console.error) so dev keeps running but loud;
   * `import.meta.env.DEV` strips the whole method body from production builds.
   */
  private assertChunkInvariants(): void {
    if (!import.meta.env.DEV) return;
    const fail = (msg: string): void => console.error(`[chunk-invariant] ${msg}`);

    // The numeric residency index must mirror `chunks` exactly. It is a co-maintained projection —
    // precisely the drift shape this codebase keeps producing — so it is asserted rather than
    // trusted: a missed indexChunk/unindexChunk site fails here instead of silently making the LOD
    // walk believe a resident chunk is absent (which would show as a hole or a stuck parent).
    let indexed = 0;
    for (const m of this.chunkIndex) indexed += m?.size ?? 0;
    if (indexed !== this.chunks.size) {
      fail(`chunkIndex out of sync: indexed=${indexed} chunks=${this.chunks.size}`);
    }

    // RETENTION POSTCONDITION: this runs immediately after unloadDistantChunks, so a resident lod-0
    // chunk must be inside the retention radius, currently drawn by the walk, or waiting behind the
    // eviction cap — and the backlog must SHRINK every pass. Progress, not zero, is the assertable
    // property: the pass evicts on the frame's clock (unloadOnClock), so a zoom-out legitimately leaves
    // hundreds queued for a few frames, while a retention clause that outlives its input (the stale
    // `cachedReachable` pin, which held ~1k chunks at every zoom-out with nobody noticing) shows as a
    // backlog that stops falling.
    const lod0 = this.chunkIndex[0];
    if (lod0) {
      let backlog = 0;
      for (const chunk of lod0.values()) {
        if (this.retainsLod0(chunk.cx, chunk.cy, chunk.cz)) continue;
        if (this.lastLodSelection?.get(chunk.cx, chunk.cy, chunk.cz, 0)) continue;
        backlog++;
      }
      // A backlog is allowed to JUMP (the pass that steps the base to 2 retires the whole island at
      // once) — it is not allowed to stop falling. 30 passes at the cap is ~2k evictions, far above
      // any legitimate backlog.
      this.lod0EvictStuckPasses = backlog > 0 && backlog >= this.lastLod0EvictBacklog
        ? this.lod0EvictStuckPasses + 1
        : 0;
      this.lastLod0EvictBacklog = backlog;
      if (this.lod0EvictStuckPasses > 30) {
        this.lod0EvictStuckPasses = 0; // report once per stall, not once per frame
        fail(`${backlog} lod0 chunks resident outside the retention radius and undrawn, not draining`);
      }
    }

    // Orphan geometry: every meshed chunk must still be loaded. unloadChunk removes from the batch
    // and disposes geometry BEFORE deleting the chunk, so after an update this must hold.
    for (const key of this.geometries.keys()) {
      if (!this.chunks.has(key)) fail(`geometry without a loaded chunk: ${key}`);
    }
    // A build-preview chunk must have loaded voxel data.
    for (const key of this.previewChunks) {
      if (!this.chunks.has(key)) fail(`preview chunk not loaded: ${key}`);
    }
    // The ingest queue and its payload map are two projections of one thing (order + contents), kept
    // in step at exactly two sites (queueIngest / drainIngest) — the shape that drifts.
    if (this.ingestQueue.length !== this.ingestPayloads.size) {
      fail(`ingest queue ${this.ingestQueue.length} != payloads ${this.ingestPayloads.size}`);
    }

    // Each pending* set and its stale-expiry time map are hand-paired at ~6 sites; keep them in lockstep.
    const pending: Array<[string, ReadonlySet<string | number>, ReadonlyMap<string | number, number>]> = [
      ['pendingChunks', this.pendingChunks, this.pendingChunkTimes],
      ['pendingColumns', this.pendingColumns, this.pendingColumnTimes],
      ['pendingTiles', this.pendingTiles, this.pendingTileTimes],
      ['pendingLodChunks', this.pendingLodChunks, this.pendingLodChunkTimes],
      ['pendingLodColumns', this.pendingLodColumns, this.pendingLodColumnTimes],
    ];
    for (const [name, set, times] of pending) {
      if (set.size !== times.size) fail(`${name} size ${set.size} != times size ${times.size}`);
      for (const k of set) if (!times.has(k)) fail(`${name} key ${k} has no time entry`);
    }

    // LOD overlap invariant: a visible chunk must not have a visible ancestor (a drawn parent
    // structurally suppresses descendants — if both draw, the same volume is rendered twice).
    if (this.lastLodSelection) {
      for (const { cx, cy, cz, lod, visible } of this.lastLodSelection.entries()) {
        if (!visible) continue;
        let px = cx, py = cy, pz = cz;
        for (let l = lod + 1; l <= this.lodManager.maxLod; l++) {
          px >>= 1; py >>= 1; pz >>= 1;
          if (this.lastLodSelection.get(px, py, pz, l)) {
            fail(`visible chunk ${chunkKeyLod(cx, cy, cz, lod)} has visible ancestor at lod ${l}`);
          }
        }
      }
    }

    this.assertVolumeOwnership(fail);
  }

  private lastVolumeAssertMs = 0;
  /** DEV retention-drain watchdog state (see assertChunkInvariants). */
  private lastLod0EvictBacklog = 0;
  private lod0EvictStuckPasses = 0;

  /**
   * VOLUME OWNERSHIP invariant (throttled — full box scan): every base position inside the BFS
   * aligned box must be sky, occluded, drawn, or covered by a selected ancestor. Flagged ONLY
   * when something RESIDENT could have drawn there (the chunk itself is drawable, or a meshed
   * ancestor exists) — genuine first-fill streaming, where nothing exists yet, stays quiet. This
   * is the tripwire for the class of bug that shipped twice: coverage releasing a parent over a
   * chunk the render gate then refuses to draw (a hole the application-consistency checks cannot
   * see, because hiding an "un-drawable" chunk is self-consistent).
   */
  private assertVolumeOwnership(fail: (msg: string) => void): void {
    if (!this.lastLodSelection || !this.lodManager.active || this.displayBaseLod !== 0) return;
    const box = this.lastBFSChunk ? this.lastBFSAlignedBox : null;
    if (!box) return;
    const now = Date.now();
    if (now - this.lastVolumeAssertMs < 1000) return;
    this.lastVolumeAssertMs = now;

    const sel = this.lastLodSelection;
    let failures = 0;
    /** Positions legitimately showing nothing because the no-steal rule is holding an ancestor. */
    let noStealHolds = 0;
    let emptyAncestorCovers = 0;
    for (let cz = box.minZ; cz <= box.maxZ && failures < 5; cz++) {
      for (let cy = box.minY; cy <= box.maxY && failures < 5; cy++) {
        for (let cx = box.minX; cx <= box.maxX && failures < 5; cx++) {
          const state = this.baseChunkState(cx, cy, cz);
          if (state === 'sky' || state === 'occluded') continue;
          const key = chunkKey(cx, cy, cz);
          // Drawn by the gate this frame? (in-box → the gate's clause is sel && drawable)
          if (state === 'drawable' && sel.get(cx, cy, cz, 0)) continue;
          // Pass 1: covered by a selected ancestor anywhere up the chain?
          let px = cx, py = cy, pz = cz;
          let ancestorDrawn = false;
          for (let l = 1; l <= this.lodManager.maxLod; l++) {
            px >>= 1; py >>= 1; pz >>= 1;
            if (sel.get(px, py, pz, l)) { ancestorDrawn = true; break; }
          }
          if (ancestorDrawn) continue;
          // A drawable chunk the walk failed to select at all is a walk bug regardless of chain.
          if (state === 'drawable') {
            failures++;
            fail(`unowned volume at ${key} (drawable but unselected, no drawn ancestor)`);
            continue;
          }
          // Pass 2 ('waiting'): walk up until the first legitimate stop. A vertical sky/solid
          // clip at any ancestor level covers WITHOUT drawing (fine level streams underneath —
          // the pre-LOD look) → quiet. A MESHED, undrawn, unclipped ancestor is the regression
          // signature: the walk released it even though this child cannot draw — coverage lied.
          // NOTE: this direct-ancestor walk cannot see the HEIGHT-DIP class (the prematurely
          // released chunk is the VERTICAL NEIGHBOR of this chain, never on it — and content-top
          // heuristics false-positive on tree canopies, which coarse levels legitimately omit).
          // The vertical surface anchor is guarded instead by its synthetic-dip unit tests and
          // the client/e2e ring-swap probe, which detects the dip empirically per frame.
          px = cx; py = cy; pz = cz;
          for (let l = 1; l <= this.lodManager.maxLod; l++) {
            px >>= 1; py >>= 1; pz >>= 1;
            const policy = this.marginPolicyAt(px, py, pz, l);
            if (policy !== 'unknown') break; // sky/solid clip: legitimately covers, draws nothing
            if (this.chunkAt(px, py, pz, l)?.phase === ChunkPhase.MeshedComplete) {
              // AN EMPTY MESH IS NOT A RELEASE. The clip test above says "provably nothing to draw
              // here" from the column's minCy/maxCy; a mesh with no vertices says the same thing
              // with better evidence — this level looked at the volume and found no surface in it
              // (all air, or all rock). It owns the volume and draws nothing because there is
              // nothing to draw, exactly like a clipped ancestor. Measured: every event this
              // tripwire produced on a pan storm reported ancestorVerts=0, i.e. the whole class was
              // false alarms, and they are load-bearing false alarms — verify-coverage-contract
              // fails on ANY [chunk-invariant] line, so leaving them in buries the real ones.
              // Requires the geometry to EXIST: MeshedComplete with no geometry object at all is a
              // contradiction, and still reported.
              const ancestorGeo = this.geometries.get(chunkKeyLod(px, py, pz, l));
              if (ancestorGeo && ancestorGeo.getVertexCount() === 0) { emptyAncestorCovers++; break; }
              // THE NO-STEAL YIELD is not a lie. A meshed ancestor is deliberately withheld while
              // anything in its subtree is on screen: drawing it would blank those pixels until
              // this chunk finishes streaming, which is the initial-load flicker. So this position
              // legitimately shows nothing for as long as it takes to arrive — a HOLD, gauged
              // below, not a coverage bug. With nothing displayed under the ancestor there is no
              // such excuse and the original tripwire stands: the walk released over volume that
              // nobody is drawing.
              if (this.anyDisplayedUnder(px, py, pz, l)) {
                noStealHolds++;
                break;
              }
              failures++;
              // The vertex count is what made this class diagnosable — without it an empty ancestor
              // and a real released one read identically in the log.
              fail(`unowned volume at ${key} (waiting; meshed ancestor at lod ${l} released without `
                + `drawing; ancestorVerts=${ancestorGeo ? ancestorGeo.getVertexCount() : 'none'})`);
              break;
            }
          }
        }
      }
    }
    // How much volume the no-steal rule is deliberately leaving blank this second. Zero at settle
    // by construction (a held position is held BY a streaming sibling); a number that never falls
    // means a child is stuck 'waiting' and the hold has become a hole — the one way this rule can
    // hurt, and the reason it is measured rather than assumed.
    // Positions covered by an ancestor whose mesh is legitimately EMPTY (see the excuse above).
    // Ordinary during streaming; a number that climbs without bound would mean coarse chunks are
    // meshing empty where they should not — a meshing bug wearing this excuse as a disguise.
  }

  /**
   * Is any descendant of this coarse node CONTRIBUTING PIXELS right now? (DEV invariant use: the
   * scan is throttled to 1 Hz and this walks at most 8^lod positions.) Mirrors the walk's
   * `anyDisplayedBelow`, which is what decided to withhold the node in the first place — the
   * invariant must ask the same question the decision asked, or it reports the decision as a bug.
   */
  private anyDisplayedUnder(cx: number, cy: number, cz: number, lod: number): boolean {
    if (lod <= 0) return false;
    const bx = cx << 1, by = cy << 1, bz = cz << 1;
    for (let i = 0; i < 8; i++) {
      const x = bx + (i & 1), y = by + ((i >> 1) & 1), z = bz + ((i >> 2) & 1);
      if (this.terrain.isChunkDisplayed(chunkKeyLod(x, y, z, lod - 1))) return true;
      if (this.anyDisplayedUnder(x, y, z, lod - 1)) return true;
    }
    return false;
  }

  /**
   * Request initial surface column to bootstrap the world.
   */
  private requestInitialSurfaceColumn(tx: number, tz: number): void {
    const columnKey = packPairKey(tx, tz);
    if (this.pendingColumns.has(columnKey)) return;
    
    this.pendingColumns.add(columnKey);
    this.pendingColumnTimes.set(columnKey, Date.now());
    this.initialColumnRequested = true;
    this.initialColumnRequestedMs = Date.now();

    if (this.isLocal) {
      // Always generated, never read from IDB: heights must come from the same generator run
      // that produces the chunks, or a stale cached height mis-ranges the column's requests.
      this.getLocalPool().requestColumn(tx, tz, (data) => this.handleLocalColumn(data));
      return;
    }

    const request = encodeSurfaceColumnRequest({ tx, tz });
    sendBinary(request);
  }

  /**
   * Request the 7 positive margin-source neighbours (+X/+Y/+Z faces, edges, corner) of every
   * reachable chunk. A rendered chunk's mesh reads those neighbours' voxels to place its high-side
   * border verts; the visibility BFS culls the ones behind opaque faces (occlusion is for SIGHT), but
   * their voxels are still needed to mesh the shared boundary — the source of the terrain gaps.
   * Loading them alongside the reachable frontier fixes the gaps AND shrinks the mesh-readiness wait
   * (a chunk's margins arrive with it, not a ring later). Bounded: one +ring around reachable, NO
   * cascade — we only ring reachable chunks, never the ring itself, so it can't flood-fill.
   */
  private addMarginSourceRequests(reachable: Set<number>, toRequest: Set<number>): void {
    if (reachable.size === 0) return;
    // ONE PROBE PER POSITION, NOT ONE PER (CHUNK, OFFSET).
    //
    // The dilation overlaps heavily between neighbouring reachable chunks — an interior position is
    // named by about ten of them — so asking about each naming ran ~22k probes to answer ~2.2k
    // questions at view 5 (measured as `bfs.margins`, 5x the flood fill itself). Marking the dilation
    // in a dense byte grid first collapses that to one probe per position; a mark is a byte write,
    // with no map lookup and no key.
    //
    // The probe order stays cheapest-first for the same reason as before: interior candidates, the
    // overwhelming majority, are already resident and exit on the numeric index without ever forming
    // a key. All three clauses mean "skip".
    const request = (nx: number, ny: number, nz: number) => {
      if (this.chunkAt(nx, ny, nz, 0)) return;   // already have it (numeric index, no key)
      if (this.isEmptyAir(nx, ny, nz)) return;   // open sky, won't ever load
      if (this.pendingChunks.has(chunkKey(nx, ny, nz))) return; // already coming
      toRequest.add(packCoord(nx, ny, nz));
    };

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (const packed of reachable) {
      const [cx, cy, cz] = unpackCoord(packed);
      if (cx < minX) minX = cx;
      if (cx > maxX) maxX = cx;
      if (cy < minY) minY = cy;
      if (cy > maxY) maxY = cy;
      if (cz < minZ) minZ = cz;
      if (cz > maxZ) maxZ = cz;
    }
    // Room for the one-chunk reach on every side, so a mark can never fall outside the grid.
    minX--; minY--; minZ--; maxX++; maxY++; maxZ++;
    const sx = maxX - minX + 1, sy = maxY - minY + 1, sz = maxZ - minZ + 1;
    const cells = sx * sy * sz;
    if (this.marginMarks.length < cells) this.marginMarks = new Uint8Array(cells);
    const marks = this.marginMarks;
    marks.fill(0, 0, cells);

    for (const packed of reachable) {
      const [cx, cy, cz] = unpackCoord(packed);
      const base = ((cz - minZ) * sy + (cy - minY)) * sx + (cx - minX);
      for (let i = 0; i < MARGIN_DILATION.length; i += 3) {
        marks[base + (MARGIN_DILATION[i + 2] * sy + MARGIN_DILATION[i + 1]) * sx
          + MARGIN_DILATION[i]] = 1;
      }
    }

    for (let iz = 0; iz < sz; iz++) {
      for (let iy = 0; iy < sy; iy++) {
        const row = (iz * sy + iy) * sx;
        for (let ix = 0; ix < sx; ix++) {
          if (marks[row + ix] !== 0) request(minX + ix, minY + iy, minZ + iz);
        }
      }
    }
  }

  /**
   * Request visible chunks (two-phase):
   * Phase 1: Request tiles for columns we don't know about yet
   * Phase 2: Request individual chunks for columns where we have tile data
   *
   * `toRequest` is the visibility BFS's own frontier — every unloaded chunk it reached. Loading is
   * driven ENTIRELY by this set (single source of truth: reachable = render set, toRequest = load
   * set, both from getVisibleChunks). The BFS steps to the immediate occluder walls one chunk past
   * visible air, so their voxels arrive as mesh margins without a separate stitch-loader. Chunks
   * above a column's maxCy are skipped (open sky, nothing to load).
   */
  /**
   * Always request the chunks immediately around the player, regardless of visibility.
   *
   * The visibility BFS deliberately skips chunks it cannot see — which is right for rendering and
   * wrong for physics: a player standing inside a cave, buried by an edit, or dug into a hillside
   * is surrounded by chunks the BFS will never ask for. The physics engine refuses to move into
   * unknown space, so without this the player would freeze where they stand and nothing would
   * ever arrive to un-freeze them.
   *
   * One ring covers the engine's per-tick sample region (capsule + step/probe margins, comfortably
   * under a chunk); the cost is at most 27 candidates, nearly all already resident and rejected on
   * the numeric index without forming a key — the same ordering `addMarginSourceRequests` uses.
   */
  private addPhysicsPrefetch(
    playerChunk: { cx: number; cy: number; cz: number },
    toRequest: Set<number>
  ): void {
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          const cx = playerChunk.cx + dx;
          const cy = playerChunk.cy + dy;
          const cz = playerChunk.cz + dz;
          if (this.chunkAt(cx, cy, cz, 0)) continue;                 // already have it
          if (this.isEmptyAir(cx, cy, cz)) continue;                 // known sky: no data will exist
          if (this.pendingChunks.has(chunkKey(cx, cy, cz))) continue; // already coming
          toRequest.add(packCoord(cx, cy, cz));
        }
      }
    }
  }

  /**
   * PHASE ONE of the base level's request pass: decide WHAT the base level wants and in what order,
   * and publish the backlog. Issues nothing.
   *
   * The split exists because of the WORKER QUEUE, which is strictly FIFO and never scans past a
   * blocked head (see workerDispatch). Whoever submits first owns the pool until its jobs drain, so
   * the order the two streamers RUN inside a pass is the order their terrain appears in — a fact no
   * amount of priority sorting downstream can undo. This phase used to be inseparable from the
   * issue, so the base submitted its whole window (up to MAX_PENDING_TILES column bundles plus
   * MAX_PENDING_CHUNKS chunks) before the coarse path was called at all: measured at cold start,
   * every pass read `6x baseColumnBundle -> 6x lodProbe -> 1x lodChunk`, so coarse work entered the
   * queue ~2 pool turnovers behind, structurally, on every pass forever.
   *
   * The backlog is published HERE, before anything is issued, because the fine-first gate reads it
   * (see computeBaseComplete and outstandingAt) and now runs first.
   */
  private buildBaseRequestPlan(
    toRequest: Set<number>,
    playerPos?: THREE.Vector3,
    reachable?: Set<number>,
  ): BaseRequestPlan {
    // In-flight request window, sized to the generation pool so every worker always has queued work
    // (a worker is only useful if the request layer keeps it fed). The old fixed 4 was the tightest
    // streaming throttle — with a core-scaled pool it left most workers idle and let a fast player
    // outrun loading. ~3× keeps a couple of jobs queued per worker; tiles are cheaper and fewer.
    // Their sum is `baseWindow(genCount)`, which is the unit the far-view trigger is measured in.
    const genCount = terrainWorkerCount();
    const MAX_PENDING_TILES = Math.max(4, genCount * 2);
    const MAX_PENDING_CHUNKS = genCount * 3;

    // Request NEAREST-FIRST. Only a few requests fit per call (throttle), so ordering decides what
    // loads now — and the margin-source loader appends occluded neighbours, which would otherwise sit
    // behind the whole frontier. Sort by distance to the player's FRACTIONAL chunk position (not just
    // the integer chunk), so the ordering tracks sub-chunk movement — otherwise it only refreshes on a
    // whole-chunk cross and a near chunk can wait behind a farther one for up to a chunk of travel.
    //
    // "Nearest" means the nearest chunk that NEEDS the position, not the position's own distance —
    // see marginSourceKey. `toRequest` is `reachable` plus the +7 margin dilation addMarginSourceRequests
    // just appended, and those seven offsets all point the same way, so keying each entry by its own
    // distance makes the frontier fill lopsided: chunks on the low side read inward at terrain that
    // is already here, chunks on the high side wait a ring for sources nothing else asked for.
    const pcx = playerPos ? playerPos.x / CHUNK_WORLD_SIZE : this.lastPlayerChunk?.cx;
    const pcy = playerPos ? playerPos.y / CHUNK_WORLD_SIZE : this.lastPlayerChunk?.cy;
    const pcz = playerPos ? playerPos.z / CHUNK_WORLD_SIZE : this.lastPlayerChunk?.cz;
    // Sort packed keys by distance directly, unpacking once per entry into a scratch array — the old
    // form built a Map<string,number> and parsed each key to fill it, then paid a map lookup per
    // comparison inside the sort.
    const haveCentre = pcx !== undefined && pcy !== undefined && pcz !== undefined;
    // Shell-major, same as the coarse rings: the base cube is a shell too, and its corners were
    // arriving after the next shell's faces for exactly the same reason (see shellDistance).
    const rawDist = (x: number, y: number, z: number): number =>
      haveCentre ? shellDistance(x - pcx!, y - pcy!, z - pcz!) : 0;
    const wanted = reachable ? (x: number, y: number, z: number) => reachable.has(packCoord(x, y, z))
      : () => false;
    const sorted: Array<{ p: number; cx: number; cy: number; cz: number; lod: number; d: number; own: number; pre: number; group: number }> = [];
    for (const packed of toRequest) {
      const [cx, cy, cz] = unpackCoord(packed);
      const { d, pre, group } = haveCentre
        ? marginSourceKey(cx, cy, cz, rawDist, wanted, packCoord)
        : { d: 0, pre: 1, group: packed };
      // `own` is the UNPROMOTED distance, carried alongside because the column order needs it and
      // the chunk order must not — see orderColumnsNeedingTiles.
      sorted.push({ p: packed, cx, cy, cz, lod: 0, d, own: rawDist(cx, cy, cz), pre, group });
    }
    // THE SHARED KEY (compareStreamOrder). This was a hand-rolled `(d, pre, group)` — which is what
    // that comparator reduces to at one level with no coverage flag, so it was never a different
    // policy, only a second copy of the same one. The copy is why lod 0 stayed correct while the
    // coarse path drifted: only one of the two grew the `uncovered` key that inverted the fill.
    if (haveCentre) sorted.sort(compareStreamOrder);
    // How much the base level still has to fetch — the fine-first gate's test for "is the base
    // done" (computeBaseComplete) and the stall clock's input at lod 0 (outstandingAt).
    this.baseRequestBacklog = sorted.length;

    // Phase 1: Identify columns that need tiles, INCLUDING the +X/+Z (and +X+Z corner) neighbours
    // of every requested chunk's column. Meshes read POSITIVE margins, so a frontier chunk can only
    // mesh COMPLETE once the columns on its + sides have arrived — this one-column look-ahead makes
    // those margins land with (not a wave after) the chunk, which is what kept the +X/+Z pan
    // frontier flapping incomplete (the one-sided gap strips).
    //
    // ORDERED BY THE SAME KEY AS THE CHUNKS, and that matters more than it looks. In local mode a
    // tile request is really a COLUMN BUNDLE: one worker job that generates the tile and every
    // surface chunk in the column together (requestLocalColumnAsTile). So the visible surface —
    // the skin the player actually looks at — is delivered by THIS list, not by the per-chunk
    // requests below, which mostly serve underground and margin volume. Measured in
    // probe-fill-order: whole rings drew with no per-chunk request recorded at all, and one ring's
    // faces drew at frame 145 against a first chunk request at 239.
    //
    // It used to be a plain Set, iterated in insertion order: near column, then the three columns
    // its look-ahead dragged in (up to a diagonal away), then the next near column. No distance key
    // anywhere, and a +X/+Z bias baked into the interleaving. That is the reported "map cells load
    // out of order of distance from the spawn, so chunks take too long to display" — and, because a
    // ring's corners are reached last and drag the farthest look-aheads, a large part of the
    // corner lag too. A column now takes the key of the NEAREST chunk that needs it — see
    // orderColumnsNeedingTiles for WHICH distance that is, which is the part that took two goes.
    const orderedColumns = orderColumnsNeedingTiles(
      sorted,
      packPairKey,
      (cx, cz) => !this.columnInfo.has(packColumn(cx, cz, 0))
        && !this.pendingTiles.has(packPairKey(cx, cz))
        && !this.pendingColumns.has(packPairKey(cx, cz)),
    );

    return { sorted, orderedColumns, maxTiles: MAX_PENDING_TILES, maxChunks: MAX_PENDING_CHUNKS };
  }

  /**
   * PHASE TWO: submit the base level's jobs. Runs AFTER the coarse path each pass (see
   * updateWithVisibility) so the rings reach the FIFO worker queue while its head is still short;
   * the base still takes the overwhelming majority of the window, it just no longer takes all of it
   * before the rings are considered.
   */
  private issueBaseRequests(plan: BaseRequestPlan): void {
    // Request tiles for unknown columns, nearest-needed first.
    for (const columnKey of plan.orderedColumns) {
      if (this.pendingTiles.size >= plan.maxTiles) break;
      const [tx, tz] = unpackPairKey(columnKey);
      this.requestTileFromServer(tx, tz);
    }

    // Phase 2: Request chunks only for columns we have tile info for (nearest-first)
    let chunkRequests = 0;
    for (const { cx, cy, cz } of plan.sorted) {
      if (this.pendingChunks.size >= plan.maxChunks) break;
      if (chunkRequests >= plan.maxChunks) break;
      if (this.pendingChunks.has(chunkKey(cx, cy, cz))) continue;

      const info = this.columnInfo.get(packColumn(cx, cz, 0));

      // Don't request chunks for columns without tile data yet
      if (!info) continue;

      // Skip chunks above the surface. maxCy is the STAMP-INCLUSIVE top (the chunk holding the
      // highest tree/building voxel), so it's exactly right on its own — no margin, so no empty
      // sky chunk is loaded above the canopy. The top face meshes against extrapolated air.
      if (cy > info.maxCy) continue;

      this.requestChunkFromServer(cx, cy, cz);
      chunkRequests++;
    }
  }

  /**
   * Occlusion-cull rendered chunks against the visibility BFS: a chunk's merged group is shown ONLY
   * if the BFS reached it from the player (through connected openings, on a non-reversing path).
   * Everything else stays RESIDENT — loaded within the cube (see unloadDistantChunks) — but hidden,
   * so geometry behind solid rock, or around a corner the player can't see, doesn't draw. This is the
   * step that actually applies the visibility graph to what's on screen; loading uses the coarse cube,
   * rendering uses the reachable set.
   *
   * NOTE: We intentionally do NOT frustum-cull here. Three.js's built-in per-mesh frustum culling
   * tests each mesh against the active camera independently (main camera for the scene pass, light
   * camera for shadow maps). Manual culling against the main camera would hide chunks that still need
   * to cast shadows into view.
   */
  private updateMeshVisibility(reachable: Set<number>): void {
    // LOD ownership pass: which chunk at which level covers each region. Null when rings are off —
    // then the legacy single-LOD path below runs unchanged.
    //
    // The retired index is rebuilt HERE, immediately before the walk that reads it, so the walk's
    // gate answers about this pass's residency and shells rather than a previous pass's.
    if (this.lodManager.active) this.recomputeRetiredMeshIndex();
    // The base classification is asked by the walk AND by the gate loop below, for largely the same
    // positions — memoize it across the pair. Closed at the end of the pass, because the dev audit
    // reads the same predicate at frame end and must never see a previous pass's verdicts.
    this.coverageCore.beginPass();
    const lodSel = this.lodManager.active ? this.lodManager.computeSelection() : null;
    this.lastLodSelection = lodSel;

    for (const [key, geo] of this.geometries) {
      // Preview chunks: their groups are suppressed and preview meshes render instead — never touch
      // their visibility here.
      if (this.previewChunks.has(key)) continue;
      const lod = geo.chunk.lod;
      let visible: boolean;
      if (!lodSel) {
        // Rings off: base chunks use the occlusion/completeness gate; stray coarse chunks hide.
        visible = lod === 0 && this.isRenderable(key, geo.chunk, reachable);
      } else if (lod === 0) {
        // Base level: the LOD walk decides volume ownership (a drawn parent suppresses its octet —
        // absent from the map = suppressed); what a selected chunk contributes is baseChunkState's
        // call — the SAME predicate coverage uses, so the walk can never release a parent over a
        // chunk this gate then refuses. 'drawable' draws; 'outside' draws when complete (a
        // fallback the walk chose because the coarse owner isn't meshed — it left `reachable` by
        // DISTANCE, not occlusion; gating it on reachability blanked one border of the fine
        // region); 'waiting'/'occluded'/'sky' draw nothing (incomplete meshes never draw).
        //
        // The 'outside' branch requires an APPLIED mesh, not `isMeshComplete` — which reports true
        // for a NEVER-meshed chunk (it only means "not MeshedIncomplete"). That made the branch
        // vacuous: the gate said visible=true for chunks with no geometry, setVisible no-oped for
        // want of registered geometry, and every layer believed the volume was covered while nothing
        // was drawn. "Will draw" has to mean it, or coverage is built on a lie.
        const c = geo.chunk;
        const state = this.baseChunkState(c.cx, c.cy, c.cz);
        visible = (lodSel.get(c.cx, c.cy, c.cz, 0) ?? false) && (state === 'drawable'
          || (state === 'outside' && c.phase === ChunkPhase.MeshedComplete));
      } else {
        // Ring level: selection + mesh completeness (the shells' sacrificial ring never meshes, and
        // an incomplete coarse mesh must never draw a boundary hole).
        const c = geo.chunk;
        visible = (lodSel.get(c.cx, c.cy, c.cz, lod) ?? false) && this.remeshPipeline.isMeshComplete(key);
      }
      // The walk DECIDES; DisplayCommit APPLIES (C4 — the one commit point). Only actual changes
      // are recorded: the commit's veto logic relies on "already in the desired state" never
      // appearing in a pass's decisions, and no-ops must not consume its budget. The filter reads
      // the same wanted-visibility predicate setVisible flips (isVisible), NOT isChunkDisplayed —
      // that one excludes empty meshes, and filtering on it would re-decide every empty chunk
      // forever. The stitcher trigger that used to fire here (M8) now fires per applied flip
      // inside the commit, unchanged in meaning.
      if (visible !== this.terrain.isVisible(key)) {
        this.pendingDisplayDecisions.set(key, visible);
      }
    }
    // Applied to the WHOLE pass's decisions, after the loop: the rule asks what will be drawing
    // AFTER this commit, and mid-loop that answer depends on map iteration order.
    if (lodSel) this.holdUncoveredHides();
    this.coverageCore.endPass();
  }

  /**
   * THE NO-UNCOVER RULE. A hide may not apply while the volume it releases is still WANTED and
   * nothing else will be drawing that volume after this commit. Every hide in this file is decided
   * per chunk — the walk deselects it, or its own render gate stops saying yes — and a lone hide is
   * a singleton transaction, so DisplayCommit's veto loop (which only protects paired swaps) never
   * sees it: the volume goes dark for as long as its next owner takes to display. Measured as up to
   * 512 cells for one to five frames on every shell shift.
   *
   * WITHHOLDING, NOT SELECTING, is what keeps this from live-locking. The same rule expressed in
   * the walk — keep the chunk selected — feeds `unloadDistantLodChunks` (which defers to any
   * walk-selected chunk) and the mesh demand signal, so the retained chunk holds up its own
   * replacement and neither ever lands. A withheld hide adds nothing to the selection: the
   * replacement still meshes, the chunk is still evictable, and the wanted set still coarsens. The
   * no-steal probe cannot deadlock on it either — it descends only through IN-RANGE positions, and
   * a chunk retained because its volume coarsened is by definition out of range at its own level.
   *
   * The hide is re-decided every pass, so it lands the pass its volume's new owner displays, or
   * the volume leaves the wanted set. Eviction wins outright — a chunk's pixels stop with its data,
   * which is the one darkening this cannot prevent.
   */
  private holdUncoveredHides(): void {
    let candidates: Chunk[] | null = null;
    for (const [key, want] of this.pendingDisplayDecisions) {
      if (want) continue;
      const c = this.chunks.get(key);
      // No pixels to lose: an empty mesh owns its volume without drawing any of it.
      if (!c || !this.terrain.isChunkDisplayed(key)) continue;
      // ONLY A CHUNK STILL FIT TO DRAW IS WORTH KEEPING. A mesh that has gone MeshedIncomplete has
      // a hole on the face whose margin it lost, so retaining it would trade one gap for another —
      // and, because the no-steal rule reads display state, keeping it on screen is exactly what
      // would stop its coarse parent from covering the volume properly.
      if (!this.remeshPipeline.isMeshComplete(key)) continue;
      // Provable sky, provable rock and occlusion-pruned volume are RESOLUTIONS, not owners:
      // nothing is ever coming to draw them, so retaining on one would hold the chunk on screen for
      // the rest of its residency. Same predicate the resolution census settles them with.
      if (this.provablyEmpty(c.cx, c.cy, c.cz, c.lod)) continue;
      if (!this.volumeWanted(c.cx, c.cy, c.cz, c.lod)) continue;
      (candidates ??= []).push(c);
    }
    let held: string[] | null = null;
    if (candidates) {
      const after = this.displayedAfterIndex();
      for (const c of candidates) {
        if (this.volumeOccupiedAfter(c.cx, c.cy, c.cz, c.lod, after)) continue;
        // ...AND NOTHING IS EVER COMING. The chunk itself is not provably empty (it draws), but the
        // finer owners the onion now has for its volume may all be provable sky or provable rock —
        // measured on landform as 320 cells of lod-1 terrain held on screen for the rest of the run
        // over lod-0 positions that were every one of them occluded or sky.
        if (this.volumeProvablyEmpty(c.cx, c.cy, c.cz, c.lod)) continue;
        (held ??= []).push(chunkKeyLod(c.cx, c.cy, c.cz, c.lod));
      }
    }
    let changed = (held?.length ?? 0) !== this.retainedHides.size;
    if (held) {
      for (const key of held) {
        this.pendingDisplayDecisions.delete(key);
        if (!this.retainedHides.has(key)) changed = true;
      }
    }
    this.retainedHides.clear();
    if (held) for (const key of held) this.retainedHides.add(key);
    // Re-decide while the set is in flux, so a withheld hide is never latched by a quiet world.
    if (changed) this.dirtyVisibility();
  }

  /**
   * Does every finer owner the onion has for this volume PROVABLY hold nothing to draw — open sky
   * above the column top, solid rock below its lowest surface, or occlusion-pruned at the base?
   * Then a hide releases no pixels, and withholding it would never end: no mesh is coming for a
   * position nothing will ever be drawn at.
   *
   * Descends only while the finer level is IN RANGE, so it stops at the level the onion actually
   * wants there, and short-circuits on the first sub-volume that holds something.
   */
  private volumeProvablyEmpty(cx: number, cy: number, cz: number, lod: number): boolean {
    if (this.provablyEmpty(cx, cy, cz, lod)) return true;
    if (lod === 0 || !this.lodManager.isInRange(cx << 1, cy << 1, cz << 1, lod - 1)) return false;
    const bx = cx << 1, by = cy << 1, bz = cz << 1;
    for (let i = 0; i < 8; i++) {
      const x = bx + (i & 1), y = by + ((i >> 1) & 1), z = bz + ((i >> 2) & 1);
      if (!this.volumeProvablyEmpty(x, y, z, lod - 1)) return false;
    }
    return true;
  }

  /** Does the onion still ask for this chunk's volume — at its own level or any coarser one? */
  private volumeWanted(cx: number, cy: number, cz: number, lod: number): boolean {
    let x = cx, y = cy, z = cz;
    for (let l = lod; l <= this.lodManager.maxLod; l++) {
      if (this.lodManager.isInRange(x, y, z, l)) return true;
      x >>= 1; y >>= 1; z >>= 1;
    }
    return false;
  }

  /**
   * DISPLAY STATE AFTER THIS COMMIT, as packed coordinates per level: `at` is what will be drawing,
   * `below` every ANCESTOR of something that will be drawing. Same shape as the resolution census's
   * display truth, and for the same reason — a point probe cannot answer "is anything drawing
   * somewhere under me" without an 8^k walk, and a depth-capped walk would answer it wrongly.
   *
   * Built once per pass that has a hide candidate; the alternative is thousands of string key
   * builds a frame in the two probes below.
   */
  private displayedAfterIndex(): { at: Array<Set<number>>; below: Array<Set<number>> } {
    const at: Array<Set<number>> = [];
    const below: Array<Set<number>> = [];
    const maxLod = this.lodManager.maxLod;
    for (const [key, c] of this.chunks) {
      const decided = this.pendingDisplayDecisions.get(key);
      if (decided === false || (decided === undefined && !this.terrain.isVisible(key))) continue;
      (at[c.lod] ??= new Set<number>()).add(packCoord(c.cx, c.cy, c.cz));
      let x = c.cx, y = c.cy, z = c.cz;
      for (let l = c.lod + 1; l <= maxLod; l++) {
        x >>= 1; y >>= 1; z >>= 1;
        (below[l] ??= new Set<number>()).add(packCoord(x, y, z));
      }
    }
    return { at, below };
  }

  /**
   * Is ANY of this chunk's volume taken by something that will be drawing after this commit — a
   * displayed ancestor (which covers it whole) or a displayed descendant (which covers part of it)?
   * Both are answers to "don't retain": the first because the volume is covered, the second because
   * retaining would double-draw it, which C1 forbids and the swap is about to settle anyway.
   */
  private volumeOccupiedAfter(
    cx: number, cy: number, cz: number, lod: number,
    idx: { at: ReadonlyArray<Set<number>>; below: ReadonlyArray<Set<number>> },
  ): boolean {
    let x = cx, y = cy, z = cz;
    for (let l = lod + 1; l <= this.lodManager.maxLod; l++) {
      x >>= 1; y >>= 1; z >>= 1;
      if (idx.at[l]?.has(packCoord(x, y, z))) return true;
    }
    return idx.below[lod]?.has(packCoord(cx, cy, cz)) ?? false;
  }

  // ============== Far-view LOD ring streaming ==============

  /** All-null face neighbours for the standalone coarse sunlight pass (no cross-chunk exchange). */
  private static readonly NO_NEIGHBORS: (Uint32Array | null)[] = [null, null, null, null, null, null];

  /**
   * Recompute the LOD onion around the center and stream coarse columns + chunks for the shells.
   * Runs inside the BFS-dirty block (center chunk-cross or visibility change), so cadence matches
   * the base streamer. LOD ≥ 1 data is ALWAYS generated locally (deterministic seed) — in server
   * mode too once the seed is known; until then rings stay off in server mode.
   */
  private updateLodStreaming(centerWorld: THREE.Vector3, recomputeShells: boolean): void {
    const active = this._farLodLevels > 0 && this.lodGenerationAvailable;
    const hadLodChunks = this.lodManager.active;

    // Step the displayed base LOD one level at a time toward the zoom target. Coarsening waits
    // until the next level fully covers its range (its meshes are the fallback that prevents holes
    // when the finer level leaves the onion), with a timeout so a stuck worker can't freeze the
    // zoom; refining steps immediately — the old base level (now ring 1) keeps covering while the
    // finer level streams (the swap walk shows fine chunks only as whole octets complete).
    if (active && this.targetBaseLod !== this.displayBaseLod) {
      const next = this.displayBaseLod + Math.sign(this.targetBaseLod - this.displayBaseLod);
      const coarsening = next > this.displayBaseLod;
      const since = Date.now() - this.lastBaseLodStepMs;
      // REFINING WAITS TOO, and on the decimation backlog rather than on coverage. It used to step
      // unconditionally, so a zoom-in ran the base level 3→2→1→0 in three passes and asked for
      // everything at once — the "wait, then it all pops in" zoom. Now each step waits until nothing
      // COARSER than the level being stepped to is still drawing over volume that wants finer
      // chunks, which is the same incremental staircase a pan gets, from the same signal.
      const ready = coarsening
        ? (since > VoxelWorld.BASE_LOD_STEP_TIMEOUT_MS
          || this.lodManager.isLevelFullyCovered(next))
        // A decimation step is a level FILLING AND DISPLAYING, which takes orders of magnitude
        // longer than the coarsening timeout was sized for (2.5 s) — reusing it here would time out
        // on every step and leave this gate inert. Tied to the gate's own stall window instead, at
        // twice it, so the floor's progressive relief gets first crack at a level that cannot drain;
        // that relief, not this clock, is the real anti-wedge.
        : (since > VoxelWorld.LOD_GATE_STALL_MS * 2 || this.decimateFrom <= next);
      if (ready) {
        this.displayBaseLod = next;
        // The far view is about to be the view: fold now rather than at the end of the debounce, or
        // the first seconds of a zoom-out show terrain where a build should be.
        if (this.editLayers.hasPendingEdits) void this.editLayers.flush();
        this.lastBaseLodStepMs = Date.now();
        // A LANDED STEP MUST RE-ARM. The lod-0 BFS gate (`if (this.displayBaseLod === 0)`) is read
        // ABOVE this call in the same pass, so on the pass that steps 1 → 0 the fine BFS is skipped
        // and the walk runs against the reachable set from before the zoom-out. The pending-step
        // re-arm in updateWithVisibility cannot cover it either: target and display are EQUAL once
        // the last step lands, so it declines. Nothing was left to wake another pass and lod 0
        // stayed invisible until an unrelated event dirtied visibility — a chunk cross, i.e. "pan
        // and it appears". Streaming churn hid this most of the time; a quiet, fully-resident world
        // wedged every time. One extra pass per step, which then runs the BFS at the new base.
        this.dirtyVisibility();
      }
    } else if (!active) {
      // Rings off (or coarse gen unavailable): the base snaps back to 0. Same re-arm reasoning as a
      // landed step — the BFS gate for this pass was read while the base was still coarse.
      if (this.displayBaseLod !== 0) {
        this.displayBaseLod = 0;
        this.dirtyVisibility();
      }
    }
    // BASE SET == THE BFS BOX. `computeLodShells` octet-rounds the cube of half-extent
    // `baseRadius`, which is exactly `octetAlignedBounds(center, baseRadius)` per axis — the same
    // box VisibilityBFS cuts at (pinned by a test in lodShellSelector.test.ts). Passing the plain
    // visibility radius therefore makes the managed base volume identical to the volume lod 0 can
    // DRAW.
    //
    // It used to be radius + 2, to be sure the set contained everything the render gate could
    // draw. That inverted the problem: the extra 2-chunk shell (~2300 positions at view 5) was
    // owned by lod 0 but unrenderable — outside the BFS box, so `shouldMeshChunk` parks it
    // forever while the walk still selects it. Whenever the lod-1 ring above wasn't loaded, that
    // shell was a permanent hole: the walk delegated volume to chunks the mesher refuses to build
    // ([lod-audit] reported them as `state=outside … parked` holes, 30-137 per sweep). Beyond the
    // box the coarse levels own the volume — which is the point of the far rings — and the walk's
    // retired fallback bridges the pass where the box shrinks.
    //
    // ON THE WALK'S BEAT ONLY (recomputeShells): the onion is what the walk reads, and rebuilding it
    // between beats buys nothing. The request pump below runs every pass regardless — its windows
    // are a dozen entries, and a pump that only refilled every 50 ms left the workers idle between
    // refills (measured: jump re-settle 2.6 s → 3.2 s when the pump rode the walk's cadence).
    if (recomputeShells) {
      const shellsShifted = this.lodManager.update(
        { x: centerWorld.x, y: centerWorld.y, z: centerWorld.z },
        this.displayBaseLod,
        this._visibilityRadius,
        active ? this._farLodLevels : 0,
      );
      if (shellsShifted) this.lastShellShiftMs = performance.now();
    }

    if (!this.lodManager.active) {
      // Sweep whenever coarse chunks are resident — not just on the off-transition — so late
      // worker responses that raced the toggle can't linger forever.
      if (hadLodChunks || this.lodChunkCount > 0) this.unloadAllLodChunks();
      return;
    }

    // PAN DEBOUNCE, PER CANDIDATE. A fast pan crosses a base chunk every few frames, and every
    // crossing used to recompute the shells AND immediately issue a full round of coarse requests —
    // work the pan invalidates a moment later, which is a big part of why refinement after a long
    // pan took so long (bug 3: the queue is full of terrain the camera already left). REFINEMENT of
    // volume something is already drawing still waits for LOD_PAN_DEBOUNCE_MS.
    //
    // What no longer waits is coarse cover for volume NOTHING is drawing. Blocking the whole path
    // meant a sustained pan (the debounce re-arms on every crossing) left the coarse levels idle for
    // its entire duration while lod 0 streamed unthrottled, so the ground the camera moved into
    // stayed empty until fine chunks finished — C5a's hole-then-sharp, measured as 48 of 108 watched
    // positions. Base lod-0 streaming is untouched either way.
    const panSettled = performance.now() - this.lastShellShiftMs >= VoxelWorld.LOD_PAN_DEBOUNCE_MS;
    const requestsDeferred = this.requestLodShellData(centerWorld, panSettled);
    if (panSettled) this.requeueUnmeshedLodChunks();
    // Coarse chunks whose layer changed while they were off-duty: refreshed the pass a level takes
    // ownership of them, which is what makes a zoom-out show the build rather than the terrain.
    this.refreshStaleLodChunks();
    // NOTE: coarse eviction (unloadDistantLodChunks) deliberately does NOT run here any more.
    // Running it inside the streaming step meant it compared the freshly-shifted desiredLoadSet
    // against the PREVIOUS pass's lastLodSelection — on a pan, a coarse chunk the new walk would
    // have kept (or bridged via the retired fallback) was disposed in the very pass that would
    // have re-selected it, leaving a permanent hole (bug M5). It now runs with the lod-0 unload,
    // AFTER the walk, against this pass's fresh selection — the ordering the fine path always had.

    // THE STREAMING PUMP. This whole pass only runs on a visibility-dirty frame, and the request
    // budgets are per-pass — so when a budget cut off the tail of the request list, nothing was left
    // to dirty visibility again and the remainder was NEVER REQUESTED. The world simply stopped
    // filling, mid-onion, with no error: [lod-audit] showed it as `WEDGE stall l1: … 83
    // in-range-unrequested + 64 columns with no probe, unchanged 27s` — no chunk was stuck, the
    // streamer had just stopped asking. Re-arming while requests remain makes the pass self-limiting
    // (it stops the moment the onion is fully requested) instead of dependent on unrelated churn to
    // wake it. Same pattern as the base-LOD step re-arm.
    if (requestsDeferred) this.dirtyVisibility();
  }

  /**
   * Request coarse column probes (vertical clip) and coarse chunks for every shell level in
   * DISTANCE ORDER from the player, so what you see fills from near to far. Budgets are separate
   * from — and smaller than — the base streamer's so neither can starve the other; ring chunk
   * requests additionally yield while base streaming is saturated (sprinting in first person
   * always wins).
   *
   * @param panSettled  has the centre been still for LOD_PAN_DEBOUNCE_MS? The debounce is applied
   *   PER CANDIDATE (see passesPanDebounce): refinement of drawn volume waits for the settle, work
   *   over volume nothing is drawing streams during the pan — C5a's blurry-then-sharp.
   * @returns true when work was left unissued because a budget filled — the caller must keep the
   *   streaming pump alive (see updateLodStreaming), or the tail is never requested at all.
   */
  private requestLodShellData(centerWorld: THREE.Vector3, panSettled: boolean): boolean {
    const genCount = terrainWorkerCount();
    // The coarse levels' budget is one number in two halves — see coarseColumnWindow /
    // MAX_BUNDLE_CHUNKS, which is also where the measurement that sized them lives.
    const MAX_PENDING_LOD_COLS = coarseColumnWindow(genCount);
    const MAX_PENDING_LOD = genCount * 2;
    // Yield to base streaming while it has more than half its own window in flight — but never
    // fully: a trickle keeps the pyramid filling during long base bursts (a starved coarse level
    // means no far view and no swap fallback — visible holes are worse than a marginally slower
    // base fill, and coarse gen is ~3× cheaper per chunk than lod0).
    //
    // NOT RAISED, and that is a measurement rather than an omission. The V13 note used to price a
    // bigger coarse window at "108/108 coarse-first", which was an artefact of a harness that gave
    // the base 9 slots and made column probes free. Re-measured on the app-accurate fixture, raising
    // this window to 9, 12 or 15 changes the result by nothing at all (0/108 in every case) — the
    // coarse levels' rate limiter is the COLUMN window above, not this one — while the same extra
    // bandwidth measured in the app cost lod 0 its first draw (7.5 s → 14.0 s at 12 coarse chunks in
    // flight). The budget stays where it is.
    const baseSaturated = this.pendingChunks.size >= Math.ceil((genCount * 3) / 2);
    const maxLodInFlight = baseSaturated ? genCount : MAX_PENDING_LOD;

    // THE FAR VIEW WAITS FOR THE NEAR TERRAIN; THE FALLBACK DOES NOT. Both windows feed the same
    // worker pool, and the base level's is only marginally larger while its workload is ~7× bigger
    // (measured at cold start: 693 base chunks against 102 coarse), so "the base has its own,
    // larger window" does not put the base first — it just runs alongside. Measured in
    // probe-fill-order, lod 1's EXCLUSIVE volume — the ring beyond lod 0, strictly farther than
    // anything the base level covers — was half drawn at frame 75 while lod 0 was half drawn at
    // 236. The far view finishing before the near terrain is exactly "the outer ring of chunks
    // still display far later than the next lod level chunks".
    //
    // The split is between a coarse level's INTERIOR and its OUTER RING, and only the ring waits.
    // Display coverage for this pass: which volumes something is actually drawing. Built once here
    // and read per candidate (see volumeUncovered) — the request path's only notion of a HOLE.
    this.rebuildDisplayCoverIndex();

    // Probes are per COLUMN — dedupe across the level's cy range, keeping the nearest priority.
    // A probe carries the column's WANTED cy window with it (`wantMinCy`/`wantMaxCy`, widened as the
    // level loop meets more rows of the same column) because it is issued as a BUNDLE: one job that
    // answers the clip and returns the chunks inside it. See LodColumnBundleResponse.
    type CoarseRequest = {
      cx: number; cy: number; cz: number; lod: number;
      d: number; pre: number; group: number; uncovered: boolean;
      wantMinCy?: number; wantMaxCy?: number;
    };
    const probes = new Map<number, CoarseRequest>();
    const candidates: CoarseRequest[] = [];
    /** Positions nothing draws and nothing is fetching — the octet-hole gauge (see below). */
    let uncoveredNoWork = 0;
    /** Work left for a later pass: a filled budget, a quota, or the per-candidate pan debounce. */
    let deferred = false;
    let deferredByDebounce = false;
    // PER-LEVEL leftovers — the "nothing left to ask for" half of levelComplete. Counted here rather
    // than tracked separately because this is the only place that knows what a level still wants.
    const unissued: number[] = [];
    const leftover = (lod: number): void => { unissued[lod] = (unissued[lod] ?? 0) + 1; };

    // When the base is coarse (explore zoomed out), the base LEVEL itself streams through this
    // path too — there is no lod-0 BFS streaming at a coarse base.
    const finestStreamedLod = Math.max(1, this.displayBaseLod);
    // Levels the player looks THROUGH bypass the fair share entirely (see scheduleLodRequests): the
    // current base level, and — while a zoom step is pending — the level being stepped to plus the
    // final target. A coarsening step waits on isLevelFullyCovered(next), so letting anything starve
    // that level wedges the zoom ("scroll to zoom just doesn't load").
    // NOT the final target — only the level being stepped TO. The exemption exists because a step
    // waits on its `next` level, so starving that wedges the zoom; the far target is not being
    // looked through yet, and exempting it let a zoom-in bypass the decimation floor for every
    // level at once, which is the staircase this is meant to build.
    const priorityLods = new Set<number>([this.displayBaseLod]);
    if (this.targetBaseLod !== this.displayBaseLod) {
      priorityLods.add(this.displayBaseLod + Math.sign(this.targetBaseLod - this.displayBaseLod));
    }

    for (let lod = this.lodManager.maxLod; lod >= finestStreamedLod; lod--) {
      // FINE-FIRST: nothing coarser is asked for until the base level is done. Not the old quota —
      // that only throttled a level's outer RING, and left its fallback INTERIOR competing with lod 0
      // for the whole cold load, which is what put lod 1 on screen around the spawn and then threw it
      // away when lod 0 arrived (along with the skirts built for a boundary about to stop existing).
      //
      // `priorityLods` IS EXEMPT, and that exemption is load-bearing rather than a nicety: a
      // coarsening zoom step waits on isLevelFullyCovered(next), so starving the level being stepped
      // to would wedge the zoom outright ("scroll to zoom just doesn't load"). The set holds the
      // current base, the zoom target, and the one step between — the levels the player is looking
      // THROUGH, which were already exempt from the fair share below for the same reason.
      if (!this.levelOpen(lod) && !priorityLods.has(lod)) { deferred = true; continue; }
      // This level is being surveyed, so its "nothing outstanding" answer means something now.
      this.lodOpened[lod] = true;
      const loadSet = this.lodManager.desiredLoadSet(lod);
      if (!loadSet) continue;
      const inRange = this.lodManager.levelInRange(lod);
      const chunkWorld = CHUNK_WORLD_SIZE * (1 << lod);
      // SHELL-MAJOR, in this level's own chunk units — a ring completes before the next one starts,
      // nearest-first inside it (see shellDistance). Measured in the level's own units rather than
      // in metres because the shell IS the level's chunk grid; ordering BETWEEN levels is the
      // scheduler's job, not this key's.
      const rawDist = (x: number, y: number, z: number): number => shellDistance(
        x + 0.5 - centerWorld.x / chunkWorld,
        y + 0.5 - centerWorld.y / chunkWorld,
        z + 0.5 - centerWorld.z / chunkWorld,
      );
      // ...and the position is keyed by the nearest chunk that NEEDS it, not by where it is. The
      // load set is inRange plus its +7 margin dilation, and those offsets all point one way, so
      // own-distance order fills each ring lopsidedly — see marginSourceKey.
      const wanted = (x: number, y: number, z: number) => inRange?.has(packCoord(x, y, z)) ?? false;
      // Both flags are judged ONCE PER CONSUMER and reused by its seven margin sources — they all
      // resolve to the same group, and the whole point of the group is that it is admitted as one.
      const consumerFlags = new Map<number, { uncovered: boolean }>();

      for (const packed of loadSet) {
        const [cx, cy, cz] = unpackCoord(packed);
        const { d, pre, group } = marginSourceKey(cx, cy, cz, rawDist, wanted, packCoord);
        // `uncovered` is judged on the CONSUMER, not on the position: the load set's sacrificial +7
        // dilation is never displayed and never will be, so its own coverage says nothing about
        // whether the request is filling a hole. marginSourceKey already resolved which chunk each
        // request is FOR.
        let flags = consumerFlags.get(group);
        if (!flags) {
          const [gx, gy, gz] = unpackCoord(group);
          flags = { uncovered: this.volumeUncovered(gx, gy, gz, lod) };
          consumerFlags.set(group, flags);
        }
        const { uncovered } = flags;
        // THE PAN DEBOUNCE, PER CANDIDATE. Refinement of drawn volume waits for the settle (it is
        // the work a pan invalidates a moment later); hole-covering work does not (it is the only
        // thing that makes fresh ground appear while the camera is still moving). `flags` is passed
        // straight in — the predicate reads only `uncovered`, so this allocates nothing per position.
        if (!passesPanDebounce(flags, panSettled)) { deferredByDebounce = true; continue; }

        const colKey = packColumn(cx, cz, lod);
        const info = this.lodColumnInfo.get(colKey);
        if (!info) {
          // Column unknown: ask for it as a BUNDLE — the clip plus the chunks the clip admits, in
          // one job (see LodColumnBundleResponse). The cy window is accumulated across every row of
          // this column the level loop reaches, so the bundle returns what this pass actually wants
          // rather than a guess; the column's own clip narrows it on the worker.
          if (this.pendingLodColumns.has(colKey)) continue;
          const prev = probes.get(colKey);
          if (!prev) {
            probes.set(colKey, {
              cx, cy, cz, lod, d, pre, group, uncovered, wantMinCy: cy, wantMaxCy: cy,
            });
          } else {
            if (cy < prev.wantMinCy!) prev.wantMinCy = cy;
            if (cy > prev.wantMaxCy!) prev.wantMaxCy = cy;
            // The nearest row that wants the column owns its scheduling key, exactly as before.
            if (d < prev.d) {
              prev.cx = cx; prev.cy = cy; prev.cz = cz; prev.d = d;
              prev.pre = pre; prev.group = group; prev.uncovered = uncovered;
            }
          }
          continue;
        }

        // Vertical clip: sky above maxCy and provably-solid below minCy never load (the margin
        // policy resolves them for meshing; coverage treats them as represented).
        if (cy > info.maxCy || cy < info.minCy) continue;

        const key = chunkKeyLod(cx, cy, cz, lod);
        // Resident, in flight, or ARRIVED AND QUEUED for ingest (a bundle's chunks carry no pending
        // marker of their own) — asking again would generate what is already in hand.
        if (this.chunks.has(key) || this.pendingLodChunks.has(key) || this.ingestPayloads.has(key)) continue;

        candidates.push({ cx, cy, cz, lod, d, pre, group, uncovered });
      }
    }

    // Both windows are ordered fine-to-coarse (see scheduleLodRequests). Probes need the same order
    // as chunks: a level whose columns are never probed produces no chunk candidates at all, so
    // probing out of order would pull a coarse level forward through the back door.
    const isPriority = (lod: number) => priorityLods.has(lod);
    // The decimation step leads the rest of the order (see stepLod / scheduleLodRequests).
    const isStep = (lod: number) => lod > 0 && lod === this.stepLod;
    // THE WINDOW IS ONE BUDGET FOR EVERY LEVEL, and the far ring could fill it: four lod-9 column
    // bundles (seconds each on a complex world) left lods 1-5 with candidates, idle workers and no
    // slot until they returned or expired - the stall watchdog fired three times over. The finest
    // streamed level and the two above it, the priority levels and the step keep the reserved half;
    // everything coarser is FAR and shares the rest. (The display base itself is not enough: at base
    // lod 0 it streams through the base path and holds nothing here, and lod 1 starved behind lod 5.)
    const nearMax = finestStreamedLod + 2;
    const isNear = (lod: number): boolean => lod <= nearMax || isPriority(lod) || isStep(lod);
    const reserved = this.reservedCoarseWindow(isNear, MAX_PENDING_LOD_COLS, maxLodInFlight);

    if (probes.size > 0) {
      const plan = scheduleLodRequests([...probes.values()],
        MAX_PENDING_LOD_COLS - this.pendingLodColumns.size, isPriority, isStep);
      if (plan.deferred) deferred = true;
      const issuedProbes = new Set(plan.issue);
      for (const p of plan.issue) {
        if (!isNear(p.lod) && !reserved.admitColumn()) { issuedProbes.delete(p); deferred = true; }
      }
      for (const pr of probes.values()) if (!issuedProbes.has(pr)) leftover(pr.lod);
      for (const p of issuedProbes) {
        const colKey = packColumn(p.cx, p.cz, p.lod);
        this.pendingLodColumns.add(colKey);
        this.pendingLodColumnTimes.set(colKey, Date.now());
        // ONE ROUND TRIP, NOT TWO. The chunks a bundle returns do NOT pass through pendingLodChunks
        // and do not spend the chunk window: they were never separate requests. Nothing can
        // double-request them either — while the bundle is in flight the column has no
        // `lodColumnInfo`, so every row of it takes the `!info` branch above and is stopped by the
        // `pendingLodColumns` check one line up.
        this.getLocalPool().requestLodColumnBundle(
          p.cx, p.cz, p.lod, p.wantMinCy ?? p.cy, p.wantMaxCy ?? p.cy, MAX_BUNDLE_CHUNKS,
          (data) => this.handleLodColumnBundle(data),
        );
      }
    }

    if (candidates.length > 0) {
      // ONE BUDGET, TWO HALVES. Every column bundle in flight is already carrying up to
      // MAX_BUNDLE_CHUNKS coarse chunks, so counting only `pendingLodChunks` here would let the two
      // paths spend the same window twice over — which is exactly the state that cost lod 0 its
      // first draw when it was measured. Nothing is stranded by a window that reads 0: the pass
      // returns `deferred` and the streaming pump brings it straight back (see updateLodStreaming).
      const plan = scheduleLodRequests(candidates,
        coarseChunkSlots(maxLodInFlight, this.pendingLodChunks.size, this.pendingLodColumns.size),
        isPriority, isStep);
      if (plan.deferred) deferred = true;
      const issued = new Set(plan.issue);
      for (const c of plan.issue) {
        if (!isNear(c.lod) && !reserved.admitChunk()) { issued.delete(c); deferred = true; }
      }
      for (const c of issued) {
        const key = chunkKeyLod(c.cx, c.cy, c.cz, c.lod);
        this.pendingLodChunks.add(key);
        this.pendingLodChunkTimes.set(key, Date.now());
        this.getLocalPool().requestLodChunk(c.cx, c.cy, c.cz, c.lod, (data) => this.handleLodChunk(data));
      }
      for (const c of candidates) {
        if (issued.has(c)) continue;
        leftover(c.lod);
        if (c.uncovered) uncoveredNoWork++;
      }
    }

    // THE OCTET-HOLE GAUGE: hole-covering work that this pass did NOT issue — an uncovered
    // candidate left behind by a filled window or by the far-view quota. Volume nothing is drawing
    // and nothing is currently fetching, in other words, which is precisely what the old far-view
    // starvation manufactured: a ring-edge chunk whose margin sources were never asked for cannot
    // draw, and the no-steal rule then forbids its parent from covering the octet, so the hole is
    // STRUCTURAL rather than slow.
    //
    // Read it as a rate, not a level: a falling number while streaming is the window doing its job,
    // and it must reach ZERO at settle, because settling means no candidates are outstanding at all.
    // A floor above zero says hole-cover is being deferred forever — the trigger for a coarse heal
    // lane, which is why this is measured instead of assumed.
    // A level GATED this pass produced no candidates at all, so it must keep whatever it was last
    // measured wanting — zeroing it would read as "finished" and unlatch the ladder above it.
    for (let lod = 1; lod <= this.lodManager.maxLod; lod++) {
      if (this.levelOpen(lod) || priorityLods.has(lod)) this.lodUnissued[lod] = unissued[lod] ?? 0;
    }

    // Refinement withheld by the debounce is not "done": the pump must keep a pass coming or the
    // settle itself would never issue it (the old whole-path debounce relied on the same re-arm).
    return deferred || deferredByDebounce;
  }

  /**
   * DISPLAY COVERAGE FOR ONE REQUEST PASS — which volumes something is actually drawing, in both
   * directions. `displayedAt` is the batch's truth per node; `drawnAtOrBelow` additionally marks
   * every ANCESTOR of a displayed node, so one Set lookup answers "is anything under here on
   * screen" without the 8^k descendant walk that question naively needs.
   *
   * Both directions are required, and the descendant one is the load-bearing half: at base 0 no
   * coarse chunk over the near volume is displayed and none of its ancestors are either, so an
   * ancestors-only test would call the entire fallback pyramid "uncovered" — every pass, forever —
   * and the hole-first order would collapse into the coarsest-first fill that has already been
   * reported as a bug twice ("far coarse chunks display before the inner rings").
   *
   * Rebuilt per pass rather than maintained: display flips come from the walk, the commit, evictions
   * and the preview's take/give-back, and a cache with four writers is a cache that lies. One pass over
   * the geometry map, which updateMeshVisibility walks anyway.
   */
  /**
   * How much of the coarse window the FAR levels may hold, counted against what they hold already;
   * a column bundle is charged MAX_BUNDLE_CHUNKS chunk slots, as coarseChunkSlots charges it. Half of
   * each budget stays free for the near levels (see isNear in requestLodShellData) at all times.
   */
  private reservedCoarseWindow(
    isNear: (lod: number) => boolean, maxCols: number, maxChunks: number,
  ): { admitColumn(): boolean; admitChunk(): boolean } {
    const far = (lod: number): boolean => !isNear(lod);
    let farCols = 0, farChunks = 0;
    for (const colKey of this.pendingLodColumns) if (far(columnKeyLod(colKey))) farCols++;
    for (const key of this.pendingLodChunks) if (far(parseChunkKey(key).lod)) farChunks++;
    const colCap = Math.max(1, maxCols - Math.ceil(maxCols / 2));
    const chunkCap = Math.max(MAX_BUNDLE_CHUNKS, maxChunks - Math.ceil(maxChunks / 2));
    return {
      admitColumn: () => {
        if (farCols + 1 > colCap || farChunks + (farCols + 1) * MAX_BUNDLE_CHUNKS > chunkCap) return false;
        farCols++;
        return true;
      },
      admitChunk: () => {
        if (farChunks + 1 + farCols * MAX_BUNDLE_CHUNKS > chunkCap) return false;
        farChunks++;
        return true;
      },
    };
  }

  private readonly displayedAt: Map<number, Set<number>> = new Map();
  private readonly drawnAtOrBelow: Map<number, Set<number>> = new Map();

  private rebuildDisplayCoverIndex(): void {
    for (const set of this.displayedAt.values()) set.clear();
    for (const set of this.drawnAtOrBelow.values()) set.clear();
    const maxLod = this.lodManager.maxLod;
    const mark = (map: Map<number, Set<number>>, lod: number, packed: number): void => {
      let set = map.get(lod);
      if (!set) { set = new Set<number>(); map.set(lod, set); }
      set.add(packed);
    };
    for (const [key, geo] of this.geometries) {
      if (!this.terrain.isChunkDisplayed(key)) continue;
      const c = geo.chunk;
      mark(this.displayedAt, c.lod, packCoord(c.cx, c.cy, c.cz));
      let x = c.cx, y = c.cy, z = c.cz;
      for (let l = c.lod; l <= maxLod; l++) {
        mark(this.drawnAtOrBelow, l, packCoord(x, y, z));
        x >>= 1; y >>= 1; z >>= 1;
      }
    }
  }

  /** C5a's HOLE test: is there no displayed chunk at, above, or below this volume? */
  private volumeUncovered(cx: number, cy: number, cz: number, lod: number): boolean {
    if (this.drawnAtOrBelow.get(lod)?.has(packCoord(cx, cy, cz))) return false;
    let x = cx, y = cy, z = cz;
    for (let l = lod + 1; l <= this.lodManager.maxLod; l++) {
      x >>= 1; y >>= 1; z >>= 1;
      if (this.displayedAt.get(l)?.has(packCoord(x, y, z))) return false;
    }
    return true;
  }

  /**
   * Coarse column BUNDLE response: the clip, then the chunks that came with it. Both halves land in
   * the same tick — the point of the bundle is that a coarse column costs one round trip, like a
   * base column, instead of a probe followed by a queue traversal.
   */
  private handleLodColumnBundle(
    data: { cx: number; cz: number; lod: number; minCy: number; maxCy: number;
      chunks: Array<{ cy: number; voxelData: Uint32Array; lit?: boolean }> },
  ): void {
    // The clip FIRST: ingestLodChunk indexes and queues chunks whose in-range test reads the
    // shells, and the streaming pass that follows needs the column open before it can ask for the
    // rows the bundle's cap left behind.
    this.handleLodColumn(data);
    for (const c of data.chunks) {
      // A chunk that arrived some other way (a heal, a racing per-chunk request issued before the
      // bundle existed) is already correct — generation is deterministic, so re-ingesting it would
      // re-light and re-mesh its neighbourhood for a byte-identical result.
      // ...unless its layer changed under it: the resident copy was merged from an older layer, so
      // "generation is deterministic" no longer implies "already correct".
      const key = chunkKeyLod(data.cx, c.cy, data.cz, data.lod);
      if (this.chunks.has(key) && !this.staleLodData.has(key) && !this.staleLodMeshes.has(key)) continue;
      this.handleLodChunk({
        chunkX: data.cx, chunkY: c.cy, chunkZ: data.cz, lod: data.lod, voxelData: c.voxelData, lit: c.lit,
      });
    }
  }

  /**
   * A cascade finished. DATA IS CHEAP, MESHES ARE NOT: every touched layer is already correct for
   * any chunk that has yet to be generated, so the only work left is for chunks resident RIGHT NOW —
   * and of those, only the ones a level currently owns are worth remeshing. A chunk resident but
   * outside its level's shells (the far view while the player builds at lod 0) is left marked and
   * refreshed if and when the onion includes it.
   */
  private onEditLayersChanged(touched: LodEditTouch[], clips: LodEditBatch['clips']): void {
    // THE CLIP FIRST, AND IN PLACE. `lodColumnInfo` has exactly one other writer — the column probe
    // response — and a column that already has an entry is never probed again, so a widening that
    // waits for a response never arrives. The build's top row then stays classified sky for the rest
    // of the session: never requested (the streaming pass skips `cy > maxCy`), the layer never
    // applies, `marginPolicyAt` caps the row below it against open sky, and coverage counts the
    // volume represented — a build that renders SHORT at every coarse level, with nothing reporting
    // a hole. Widen only, exactly as the ingest merge does.
    for (const c of clips) {
      const colKey = packColumn(c.cx, c.cz, c.lod);
      const info = this.lodColumnInfo.get(colKey);
      // An unprobed column needs nothing: its response merges the persisted extension on arrival.
      if (!info) continue;
      if (c.minCy < info.minCy) info.minCy = c.minCy;
      if (c.maxCy > info.maxCy) info.maxCy = c.maxCy;
    }
    for (const t of touched) {
      const key = chunkKeyLod(t.cx, t.cy, t.cz, t.lod);
      if (!this.chunks.has(key)) continue;
      if (t.lowered) this.staleLodData.add(key); else this.staleLodMeshes.add(key);
    }
    this.refreshStaleLodChunks();
    if (touched.length > 0 || clips.length > 0) this.dirtyVisibility();
  }

  /**
   * Bring resident coarse chunks up to date with their layer — but only where a level currently owns
   * the volume (displayed, or in range and therefore a candidate for display). Called after a
   * cascade and once per streaming pass, which is what makes a zoom-out find the far view correct.
   */
  private refreshStaleLodChunks(): void {
    if (this.staleLodData.size === 0 && this.staleLodMeshes.size === 0) return;
    const owned = (chunk: Chunk, key: string): boolean =>
      this.terrain.isChunkDisplayed(key)
      || this.lodManager.isInRange(chunk.cx, chunk.cy, chunk.cz, chunk.lod);

    // A LOWERED floor cannot be merged in: the procedural values the chunk would have to fall back
    // to were consumed by the previous merge, so the only honest refresh is to generate it again.
    for (const key of [...this.staleLodData]) {
      const chunk = this.chunks.get(key);
      if (!chunk) { this.staleLodData.delete(key); continue; }
      if (!owned(chunk, key)) continue;
      if (!this.lodGenerationAvailable || this.pendingLodChunks.has(key)) continue;
      this.staleLodData.delete(key);
      this.pendingLodChunks.add(key);
      this.pendingLodChunkTimes.set(key, Date.now());
      this.getLocalPool().requestLodChunk(chunk.cx, chunk.cy, chunk.cz, chunk.lod,
        (d) => this.handleLodChunk(d));
    }

    // A raised floor is a pure merge over data already in memory — no round trip.
    for (const key of [...this.staleLodMeshes]) {
      const chunk = this.chunks.get(key);
      if (!chunk) { this.staleLodMeshes.delete(key); continue; }
      if (!owned(chunk, key)) continue;
      this.staleLodMeshes.delete(key);
      this.editLayers.apply(chunk.data, chunk.cx, chunk.cy, chunk.cz, chunk.lod);
      // Standalone sunlight, exactly as coarse ingest computes it (stride-scaled block falloff),
      // then the folded lod-0 light floored back in — same two steps, same order.
      perfStats.begin('lightCoarse');
      computeAndPropagateLight(chunk.data, null, VoxelWorld.NO_NEIGHBORS, coarseBlockFalloff(chunk.lod));
      perfStats.end('lightCoarse');
      this.editLayers.applyLight(chunk.data, chunk.cx, chunk.cy, chunk.cz, chunk.lod);
      chunk.dirty = true;
      this.remeshPipeline.add(key);
    }
  }

  /** Load this world's persisted edit layers (called on boot and on every world switch). */
  async hydrateEditLayers(): Promise<void> {
    this.editLayers.clear();
    if (!this.isLocal) return;
    const epoch = this.worldEpoch;
    const state = await loadLodEditState();
    if (epoch !== this.worldEpoch) return;   // switched again mid-read; the later call owns it
    this.editLayers.hydrate(state.layers, state.clips, state.pending);
    // Coarse chunks resident from before the hydrate (a switch mid-session) hold unmerged data.
    for (const [key, chunk] of this.chunks) {
      if (chunk.lod > 0 && this.editLayers.has(chunk.cx, chunk.cy, chunk.cz, chunk.lod)) {
        this.staleLodMeshes.add(key);
      }
    }
    this.refreshStaleLodChunks();
    if (state.layers.length > 0) this.dirtyVisibility();
  }

  /** Coarse column probe response: store the vertical clip and let streaming re-evaluate. */
  private handleLodColumn(data: { cx: number; cz: number; lod: number; minCy: number; maxCy: number }): void {
    const colKey = packColumn(data.cx, data.cz, data.lod);
    this.pendingLodColumns.delete(colKey);
    this.pendingLodColumnTimes.delete(colKey);
    // THE PERSISTED WIDENING, MERGED IN. The generated clip comes from the terrain height, so a
    // column holding a player's tower (or a shaft below the surface) would be clipped as sky/solid:
    // never requested, layer never applied, and coverage counting the volume as represented. Widen
    // only — see LodEditLayers.finalizeLevel.
    const ext = this.editLayers.clipFor(data.cx, data.cz, data.lod);
    this.lodColumnInfo.set(colKey, ext
      ? { minCy: Math.min(data.minCy, ext.minCy), maxCy: Math.max(data.maxCy, ext.maxCy) }
      : { minCy: data.minCy, maxCy: data.maxCy });
    // Chunk requests for this column can proceed on the next pass; the box is unchanged. This
    // used to null lastBFSChunk — ON EVERY COARSE COLUMN PROBE — which forced a full BFS
    // re-partition per column and silenced the ownership invariant while streaming (M3's storm).
    this.dirtyVisibility();
  }

  /** Coarse chunk response: ingest with standalone sunlight; queue in-range members for meshing. */
  private handleLodChunk(data: { chunkX: number; chunkY: number; chunkZ: number; lod: number; voxelData: Uint32Array; lit?: boolean }): void {
    // Queued, not ingested here: this is a worker callback between frames (see queueLodIngest).
    this.queueLodIngest(data.chunkX, data.chunkY, data.chunkZ, data.lod, data.voxelData, data.lit ?? false);
  }

  private ingestLodChunk(data: { chunkX: number; chunkY: number; chunkZ: number; lod: number; voxelData: Uint32Array; lit?: boolean }): void {
    const { chunkX: cx, chunkY: cy, chunkZ: cz, lod } = data;
    const key = chunkKeyLod(cx, cy, cz, lod);
    this.pendingLodChunks.delete(key);
    this.pendingLodChunkTimes.delete(key);

    // Rings turned off while this response was in flight — drop it.
    if (!this.lodManager.active) return;

    let chunk = this.chunks.get(key);
    if (!chunk) {
      chunk = new Chunk(cx, cy, cz, lod);
      this.chunks.set(key, chunk);
      this.indexChunk(chunk);
      this.lodChunkCount++;
    }
    chunk.data.set(data.voxelData);
    // PLAYER EDITS, MERGED OVER THE SEED. Coarse generation cannot see lod-0 voxels, so this is the
    // only place a build reaches the far view. Before the relight, so the pass below sees the build's
    // geometry (shadow) and its emitter (glow); the folded light is floored back in after it.
    this.editLayers.apply(chunk.data, cx, cy, cz, lod);
    this.staleLodMeshes.delete(key);
    this.staleLodData.delete(key);
    chunk.dirty = true;

    // Standalone sunlight: open-sky above, no neighbour exchange, no relight fan-out. Correct for
    // the surface-only coarse terrain (heightfield + water); border light matches across chunks
    // because every chunk makes the same open-sky assumption. This is what keeps coarse ingest free of
    // the LOD-0 relight cascade churn.
    //
    // THE BLOCK-LIGHT FALLOFF IS SCALED BY THE STRIDE. Coarse chunks DO carry emitters now — the
    // buildings' exterior lamps — and a lod-n voxel is 2^n fine voxels wide, so the fine −1 per voxel
    // would throw a lamp's light 2^n times further in metres (62 m at lod 3). Charging 2^n per voxel
    // reproduces the LOD-0 field exactly, at full brightness. The flip side of standalone lighting
    // stands: a lamp within its radius of a chunk plane has its glow clipped there.
    //
    // LIT IN THE WORKER (terrainWorker.lightCoarse) for everything the generator produces: standalone
    // lighting is a pure function of the chunk's own data, so it travels with generation and costs
    // the main thread nothing. The relight runs HERE only when the chunk intersects a player edit —
    // the layer merged above changed the geometry (shadow) or added an emitter (glow) the worker
    // could not see — or when a producer did not light it.
    if (!data.lit || this.editLayers.has(cx, cy, cz, lod)) {
      perfStats.begin('lightCoarse');
      computeAndPropagateLight(chunk.data, null, VoxelWorld.NO_NEIGHBORS, coarseBlockFalloff(lod));
      perfStats.end('lightCoarse');
    }
    // THE EDITED VOLUME KEEPS ITS OWN LIGHT. Standalone coarse lighting is a guess by construction
    // (open sky above, no neighbour exchange); the layer carries the real lod-0 relight for exactly
    // the voxels a build touched, so it floors the result rather than being overwritten by it.
    //
    // UNCONDITIONAL. Gating this on the geometry merge having changed something skipped the light
    // exactly where it matters most: the higher the level, the more often a build's cell coincides
    // with already-solid coarse terrain, so the merge is a no-op while the folded light is the only
    // light there is. It also made the result depend on which path brought the chunk in — the
    // in-place refresh never had the gate.
    this.editLayers.applyLight(chunk.data, cx, cy, cz, lod);

    // No BFS runs at coarse levels — visibility is the shell selection.
    chunk.visibilityBits = VISIBILITY_ALL;

    // QUEUE UNCONDITIONALLY, park by policy — the same mechanism lod 0 uses. The in-range test
    // moved into shouldMeshChunk, so an out-of-range arrival (the sacrificial +margin ring, or a
    // chunk whose shells shifted while the request was in flight) sits PARKED in the queue and
    // meshes the moment the onion includes it.
    //
    // Testing in-range HERE instead dropped such arrivals on the floor: nothing ever re-queued
    // them, so they stayed resident-but-unmeshed forever — invisible terrain with data already in
    // memory. [lod-audit] caught both faces of it: `WEDGE stall l4: 5 resident-unmeshed … unchanged
    // 65s` and `HOLE l5(5,-1,2)[loaded,sel=F,noslot,none] rim` (`none` = not even queued). Every
    // base-LOD step recomputes all shells at once, so a zoom reliably orphaned a batch.
    this.remeshPipeline.add(key);
    // Same-lod margin consumers built WITHOUT this chunk re-mesh with the real data (the coarse
    // mirror of the LOD-0 −7 re-trigger, and it needs both markers for the same reason — see there:
    // a consumer missing only a diagonal draws, with a wrong edge/corner column, so the phase alone
    // never healed it). Both markers are exact: policy-resolved (sky/solid-clipped) positions are
    // never requested and so never arrive, and a consumer that HAD this source is left alone.
    for (let i = 0; i < NEGATIVE_MARGIN_OFFSETS_7.length; i++) {
      const [dx, dy, dz] = NEGATIVE_MARGIN_OFFSETS_7[i];
      const nKey = chunkKeyLod(cx + dx, cy + dy, cz + dz, lod);
      const consumer = this.chunks.get(nKey);
      if (!consumer || !this.lodManager.isInRange(cx + dx, cy + dy, cz + dz, lod)) continue;
      if (marginConsumerNeedsRemesh(consumer.phase, this.remeshPipeline.extrapolatedMarginsFor(nKey), i)) {
        this.remeshPipeline.add(nKey);
      }
    }

    this.dirtyVisibility();
  }

  /**
   * THE RESIDENCY INVARIANT, enforced every streaming pass: a resident chunk with no applied mesh must
   * be IN the remesh queue (parked or dispatchable). Anything else is a chunk with data in memory
   * that nothing will ever draw. Cheap to enforce — `add` is a Set insert and no-ops when present —
   * and it self-heals chunks stranded before this ran (or by any future path that forgets to
   * queue), instead of leaving them invisible until the player wanders far enough to evict them.
   *
   * `phase === Loaded` IS NOT "no mesh is coming". Phase is set at APPLY, and the dispatch loop
   * DELETES the queue entry when it dispatches — so from dispatch until drainApplies reaches the
   * result the chunk reads as Loaded, unqueued, and unmeshed, and this invariant re-adds it. The
   * re-add is harmless when made, and the entry then survives the apply and dispatches a second,
   * byte-identical mesh: measured lod 1 1.57x and lod 2 1.93x, ~41% of all coarse mesh work, with
   * nothing unconverged to justify a re-mesh.
   *
   * BOTH halves of that window are needed. `isBusy` covers in flight (the long half — a worker round
   * trip under load) and already-queued; `hasPendingApply` covers finished-but-not-yet-applied, which
   * isBusy deliberately does not see. Guarding only the second half moved nothing (lod 1 went 1.57x
   * to 1.83x) because almost every re-add lands while the worker is still running.
   */
  private requeueUnmeshedLodChunks(): void {
    // FINE-FIRST: the queue is not the place to park the far view. shouldMeshChunk would refuse
    // these anyway, so re-adding them only bloats the queue the base's own scan walks every frame.
    if (!this.baseComplete) return;
    for (const [key, chunk] of this.chunks) {
      if (chunk.lod === 0 || chunk.phase !== ChunkPhase.Loaded) continue;
      if (this.remeshPipeline.isBusy(key) || this.remeshPipeline.hasPendingApply(key)) continue;
      if (!this.lodManager.isInRange(chunk.cx, chunk.cy, chunk.cz, chunk.lod)) continue;
      this.remeshPipeline.add(key);
    }
  }

  /** Unload coarse chunks that left their level's load set (or whose level left the onion). */
  private unloadDistantLodChunks(): void {
    let toUnload: string[] | null = null;
    for (const [key, chunk] of this.chunks) {
      if (chunk.lod === 0) continue;
      const loadSet = this.lodManager.desiredLoadSet(chunk.lod);
      if (loadSet && loadSet.has(packCoord(chunk.cx, chunk.cy, chunk.cz))) continue;
      // RETIRED chunks: out of range but still drawn — the walk keeps selecting them as fallback
      // while the node that now owns their volume has no mesh (visitRetired). Evicting them here
      // was the "gap on LOD switch" bug (12 visible lod3/lod4 chunks unloaded mid-refine in the
      // instrumented run). They unload the pass after their owner renders and suppresses them.
      if (this.lastLodSelection?.get(chunk.cx, chunk.cy, chunk.cz, chunk.lod)) continue;
      // Still the only thing drawing wanted volume (holdUncoveredHides): evicting it is the hole.
      if (this.retainedHides.has(key)) continue;
      // Its hide is DECIDED this pass: the commit below lands it with the show that replaces it, or
      // defers both. Evicting first bet on the commit landing — when the skirt charge deferred the
      // owner's show, the chunk was already gone (T1 eviction holes). Once hidden it goes next pass.
      if (this.pendingDisplayDecisions.get(key) === false) continue;
      (toUnload ??= []).push(key);
    }
    if (toUnload) this.unloadOnClock(toUnload);
  }

  /** Drop every coarse chunk (rings turned off / leaving local mode). */
  private unloadAllLodChunks(): void {
    const toUnload: string[] = [];
    for (const [key, chunk] of this.chunks) {
      if (chunk.lod > 0) toUnload.push(key);
    }
    for (const key of toUnload) this.unloadChunk(key);
    this.lodColumnInfo.clear();
    this.pendingLodChunks.clear();
    this.pendingLodChunkTimes.clear();
    this.pendingLodColumns.clear();
    this.pendingLodColumnTimes.clear();
    this.lastLodSelection = null;
  }

  /**
   * Decide whether a chunk's mesh should be drawn this frame. Two gates:
   *
   * 1. COMPLETENESS — never draw a mesh that skipped a high boundary (a + margin neighbour was absent
   *    when it meshed), because that geometry has a hole on that face. It stays hidden until the
   *    neighbour streams in and it re-meshes complete. This is what keeps render ⊆ load − 1 ring: the
   *    outermost loaded (margin-shell) chunks are incomplete and hidden, so the visible frontier sits
   *    one ring inside the loaded region and never shows a "see through the world" seam.
   *
   * 2. VISIBILITY (one-ring dilation of `reachable`) — draw a chunk the BFS reached OR one sharing a
   *    face with a reached chunk. The per-chunk visibility graph is too coarse to gate by exactly: a
   *    surface chunk's air region often doesn't connect its solid-side faces, and the BFS only seeds
   *    all six faces from the camera chunk itself — so a chunk whose visible surface sits on a boundary
   *    with a reachable neighbour (e.g. the terrain directly below where you stand) is reached only
   *    when you're in the chunk beside it, and pops out the moment you cross away. Drawing the face-ring
   *    around `reachable` keeps that boundary geometry on screen from every adjacent position, while
   *    occlusion still hides anything two-or-more chunks deep behind a wall (the perf win).
   */
  private isRenderable(key: string, chunk: Chunk, reachable: Set<number>): boolean {
    if (!this.remeshPipeline.isMeshComplete(key)) return false;
    // Coordinates come from the chunk the caller already has, instead of parsing them back out of its
    // key — the packed reachable set is queried by coordinate.
    const { cx, cy, cz } = chunk;
    if (reachable.has(packCoord(cx, cy, cz))) return true;
    for (const [dx, dy, dz] of FACE_OFFSETS_6) {
      if (reachable.has(packCoord(cx + dx, cy + dy, cz + dz))) return true;
    }
    return false;
  }

  /**
   * Should this chunk be meshed now? Gates the remesh worker so we don't spend mesh slots on chunks
   * the render gate will never draw — the occluder shell and the underground bulk behind rock. It's
   * the render set (reachable ∪ face-ring) WITHOUT the mesh-completeness clause of isRenderable (we're
   * deciding whether to build the mesh in the first place). Before the first visibility BFS runs
   * (empty reachable set) it returns true so initial meshing isn't stalled; a skipped chunk stays in
   * the remesh queue and meshes the frame its view opens up (process runs every frame). Meshing is a
   * pure function of a chunk's own + neighbour VOXELS, so deferring it never changes the result —
   * only when it is paid.
   */
  /**
   * Is the level one step FINER than `lod` responsible for this volume?
   *
   * `levelInRange(lod - 1)` answers it uniformly and answers the edge case for free: below the
   * onion's base level it returns null, which is exactly "there is no finer level, so nothing owns
   * this" — true both for lod 0's parents at a lod-0 base and for the base level itself when explore
   * is zoomed out. An earlier draft special-cased the base with the BFS aligned box; that box is only
   * maintained while displayBaseLod is 0, so at a coarse base it would have consulted a stale one.
   *
   * A RANGE test, not a coverage test, deliberately: coverage moves with display state and would make
   * the mesh gate flap frame to frame. Where the finer level is in range but has stopped covering,
   * the demand signal (wantsMeshAt) is what catches it.
   */
  private finerLevelOwns(cx: number, cy: number, cz: number, lod: number): boolean {
    const finer = this.lodManager.levelInRange(lod - 1);
    if (!finer) return false;
    const bx = cx << 1, by = cy << 1, bz = cz << 1;
    for (let i = 0; i < 8; i++) {
      if (finer.has(packCoord(bx + (i & 1), by + ((i >> 1) & 1), bz + ((i >> 2) & 1)))) return true;
    }
    return false;
  }

  private shouldMeshChunk(cx: number, cy: number, cz: number, lod: number = 0): boolean {
    // Coarse ring chunks: the BFS/reachable set is LOD-0-only — the shell selection IS the render
    // set at lod ≥ 1, so in-range IS the mesh test. It lives here rather than at ingest so an
    // out-of-range arrival parks in the queue and meshes when the onion reaches it, instead of
    // being dropped and stranded (see ingestLodChunk). The sacrificial +margin ring is in the
    // load set but never in range, so it still loads data-only and never meshes.
    // The LADDER is not here — it is RemeshPipeline.levelAdmits, because this function is also
    // `inRenderSet` and feeds coverage. See that field.
    if (lod > 0) {
      if (!this.lodManager.active || !this.lodManager.isInRange(cx, cy, cz, lod)) return false;
      // THE ONION'S INTERIOR IS DEAD WEIGHT UNDER FINE-FIRST, and this is where that gets paid for.
      // Each coarse level covers the finer level's box PLUS a ring, and the interior exists as the
      // swap fallback — "drawn until their children mesh" (LodShellSelector). That only earns its
      // keep if coarse fills FIRST. Fine-first inverts it: a coarse level may not stream until the
      // level below is complete, so by the time an interior chunk meshes its children are already
      // drawn, the walk suppresses it, and it is never displayed once. Measured at 42% of lod 1 and
      // 30% of lod 2 — meshed purely to be thrown away.
      //
      // Two exceptions, and they are the whole reason this is safe:
      //  - A COARSENING ZOOM STEP waits on isLevelFullyCovered(next), which demands every in-range
      //    position at that level be rendered or clipped, interior included. Without this the step
      //    could only ever fire on its 2.5 s timeout.
      //  - THE WALK ASKING (wantsMeshAt) covers the rare case a range test cannot see: a fine chunk
      //    unloaded, edited or evicted under a coarse parent that must now draw. Demand-driven, so
      //    the fallback is built when it is actually needed rather than always.
      if (this.targetBaseLod > this.displayBaseLod) return true;
      if (this.lodManager.wantsMeshAt(cx, cy, cz, lod)) return true;
      return !this.finerLevelOwns(cx, cy, cz, lod);
    }
    const reachable = this.cachedReachable;
    if (reachable.size === 0) return true; // pre-BFS: don't block the initial mesh
    if (reachable.has(packCoord(cx, cy, cz))) return true;
    for (const [dx, dy, dz] of FACE_OFFSETS_6) {
      if (reachable.has(packCoord(cx + dx, cy + dy, cz + dz))) return true;
    }
    return false;
  }

  /**
   * RETENTION PREDICATE — the single answer to "may a lod0 chunk at (cx,cy,cz) exist right now?",
   * shared by the unloader (evicts outside it) and every out-of-band request path (margin healing
   * must not request outside it). One function on purpose: when these two disagreed, healing a
   * frontier chunk's margin became a live-lock — request → arrive → evict on the next pass →
   * consumer still incomplete → heal re-requests — burning a generation + a mesh dispatch ~1/s
   * per frontier chunk, forever (the "remesh queue never settles" report).
   *
   * The radius is the render set's MARGIN CLOSURE, from first principles:
   *   octet-aligned BFS box reach   ≤ visibilityRadius + 1   ((center+r) | 1)
   *   + face-ring render dilation   ≤ visibilityRadius + 2   (isRenderable draws box + 1 ring)
   *   + positive margin sources     ≤ visibilityRadius + 3   (a meshable chunk's +7 inputs)
   * plus the hysteresis buffer so straddlers don't flap as the player crosses chunk boundaries.
   * Anything further can never affect a drawn pixel, so evicting it is always safe — and anything
   * within it that a request path created must be allowed to stay, or that path loops.
   */
  /** Base chunks wanted but not yet requested, as of the last BFS pass — see computeBaseComplete. */
  private baseRequestBacklog = 0;

  /**
   * MESH CHURN, honestly. The panel used to divide CUMULATIVE dispatches by the CURRENT mesh count,
   * which are different kinds of quantity — the ratio therefore climbed with session length and
   * 1.0x was unreachable by construction. These three make it dimensionless: `total / distinct` is
   * "how many times was each chunk meshed", where 1.0 really is ideal, and `idle` counts dispatches
   * issued while NOTHING was in flight and nothing was left to ask for — re-meshing a settled world,
   * which should be flatly zero.
   */
  /**
   * Chunks that have been ON SCREEN at least once since they were meshed. Splits the panel's Waste
   * figure into the two things it used to conflate: NEVER SHOWN (built for nothing — real waste) and
   * SUPERSEDED (shown, did its job, replaced by a finer level — the LOD staircase working). Only the
   * first should trend to zero, and without the split a working staircase is indistinguishable from
   * speculative meshing.
   */
  private readonly everShown = new Set<string>();
  private readonly meshedEver = new Set<string>();
  private meshDispatchTotal = 0;
  private meshDispatchIdle = 0;

  /**
   * FINE-FIRST: is the base level DONE, so coarser levels may be queued at all?
   *
   * Cached per pass because `shouldMeshChunk` asks it once per queued chunk and the answer costs a
   * queue scan — recomputing per call is quadratic. Recomputed in updateWithVisibility, right after
   * the BFS publishes this pass's numbers.
   */
  private baseComplete = false;

  /** Base chunks ever taken into residency — monotone, so it is the gate's honest "is lod 0 moving".
   *  A new low in outstanding work is not: a cold load's backlog plateaus while chunks pour in. */
  private baseArrivals = 0;

  /** THE COLD-LOAD LATCH. Fine-first orders the FIRST fill; a moving box never completes, so
   *  re-imposing it during a pan pins the window at the base for the pan's whole duration. */
  private baseEverComplete = false;

  /**
   * "ALL FINE CHUNKS DISPLAYED" cannot be taken literally, and that is the whole difficulty: plenty
   * of in-range lod-0 positions never draw and never should — open sky above the surface, volume the
   * visibility BFS cannot reach behind rock, the underground bulk. A gate that waits for those never
   * opens, and the far view never streams at all.
   *
   * So "done" is expressed in the coverage vocabulary that already exists, POSITION BY POSITION:
   * A_0 resolved (see computeBaseComplete and regionResolved). Sky, occlusion-pruned volume and the
   * provably-solid bulk resolve without ever drawing; everything else must have delivered a mesh.
   * The frontier is not a separate term any more — while `bfsFrontierActive` is true, unreached
   * cells classify 'waiting' rather than 'occluded', so they hold the region open by themselves.
   *
   * A coarse BASE (explore zoomed out) is complete by definition: there is no lod-0 streaming to
   * wait for, and blocking the coarse path there would block the entire view.
   */
  /** Highest coarse level allowed to stream this pass; 0 = none (the base is still filling). */
  private openMaxLod = 0;
  /**
   * DECIMATION: the coarsest level currently drawing over volume where finer chunks belong, or 0 when
   * there is none. Read from the residency walk's per-level deficit (see deficitByLod) — the walk
   * runs AFTER this gate each pass, so like lodUnissued this is last pass's number.
   */
  private decimateFrom = 0;
  /** This pass's resolution census, indexed by lod — see LodResidencyManager.computeRegionResolution. */
  private regionRows: RegionCensus[] = [];
  /** Levels that have actually been through the gate — an unvisited level is not surveyed. */
  private lodOpened: boolean[] = [];
  /** Candidates a level left unissued on its last pass through requestLodShellData. */
  private lodUnissued: number[] = [];
  /** The un-watchdogged ladder position, the best outstanding count seen at it, and since when. */
  /** The level a drawn coarse node is being stepped down to — an ORDER, never a barrier. It leads
   *  the request order so the staircase steps one level at a time; held shut instead, its own
   *  deficit could never clear, since clearing it needs exactly the coverage it was denied. */
  private stepLod = 0;
  /** Identity of what the ladder is waiting on, for the stall clock: -decimateFrom while stepping a
   *  coarse level down, else the ceiling's first incomplete level. Deliberately NOT the relieved
   *  level — relief moves that, which would re-arm the clock it just fired and oscillate. */
  private gateActiveKey = NaN;
  private lodGateBest = Infinity;
  private lodGateBestMs = 0;
  /** How many levels the watchdog has force-opened (0 = the ladder is moving on its own). */
  /** How far the watchdog has widened the window, in levels. PROGRESSIVE: a level that can never
   *  drain must not pin the window forever, so each further stall widens by one more. */
  private gateRelief = 0;
  /**
   * How long the blocking level may make NO PROGRESS before the next level opens anyway. Same role as
   * BASE_LOD_STEP_TIMEOUT_MS: a stuck worker must not freeze the far view permanently.
   *
   * MEASURED ON NO PROGRESS, NOT ON ELAPSED TIME, and the first version got this wrong. A flat
   * timeout cannot tell "slow" from "wedged": on a 2 fps box lod 1 legitimately needs tens of
   * seconds to drain 73 candidates, so a 4 s flat timer force-opened every level almost immediately
   * (the gate read `wd+9`) and the ordering it was guarding never held. Progress is a NEW LOW in the
   * level's outstanding work, so a level that is filling — however slowly, and including a panning
   * player that keeps adding to it — keeps resetting the timer, while one that is genuinely stuck
   * cannot. Overshooting degrades to the old concurrent fill, which is slower, not a hole.
   */
  private static readonly LOD_GATE_STALL_MS = 8000;

  /**
   * The coarsest level with a decimation backlog — the level a pan or a zoom-in has left drawing
   * over volume that wants finer chunks. 0 when there is none, which is every cold load (nothing is
   * drawn, so nothing can be stepped down).
   */
  private computeDecimateFrom(): number {
    if (!this.lodManager.active) return 0;
    const deficit = this.lodManager.deficitByLod;
    for (let lod = this.lodManager.maxLod; lod >= 1; lod--) {
      if ((deficit[lod] ?? 0) > 0) return lod;
    }
    return 0;
  }

  /**
   * FINE-FIRST, ONE LEVEL AT A TIME: recompute how far up the ladder streaming is allowed.
   * Called once per pass — every gate below reads the cached answer, because `shouldMeshChunk` asks
   * per queued chunk and each level's test costs a queue scan.
   */
  private recomputeLodGate(): void {
    this.decimateFrom = this.computeDecimateFrom();
    // THIS PASS'S RESOLUTION CENSUS, once. Every predicate below reads these rows: the census is a
    // scan of the in-range sets and the base classification, and the ceiling asks about every level.
    this.regionRows = this.regionCensus();
    this.baseComplete = this.computeBaseComplete();
    const maxLod = this.lodManager.maxLod;
    const now = performance.now();
    if (this.baseComplete && this.displayBaseLod === 0) this.baseEverComplete = true;
    // A SUSTAINED PAN IS NOT A COLD LOAD. The first-fill order is decided against a STILL centre —
    // a moving box never completes — so without this it holds for the pan's whole duration and the
    // ceiling pins one level above the base (measured: lod 2 with nothing on screen for 4.9 s).
    // One crossing cannot trip it: a spawn snap or a teleport lapses before the window is out.
    else if (this.centreMoving() && now - this.centreMoveStartMs > VoxelWorld.CENTRE_MOTION_MS) {
      this.baseEverComplete = true;
    }

    // THE STEP and THE CEILING are the two things the ladder decides, and only one of them refuses:
    //  - CEILING (fine-first): with nothing on screen, fill finest-first — a cold load, and once
    //    that first fill has happened the latch keeps it open (see baseEverComplete).
    //  - STEP (decimation): with a coarse chunk ALREADY on screen where finer ones belong, its own
    //    children go FIRST, so the staircase comes down one level at a time. An ORDER, not an
    //    embargo — as a barrier it deadlocked, since every level below it was forbidden the very
    //    coverage its deficit needed to clear.
    this.stepLod = this.decimateFrom > 0 ? this.decimateFrom - 1 : 0;

    // Advance while the region below is RESOLVED — every position in it drawn, provably empty,
    // covered by its own applied mesh, or drawn by a finer level (see
    // LodResidencyManager.computeRegionResolution). The queue-drain predicate this replaced advanced
    // off levels that owned nothing on screen AND had nothing meshed, and a level that has never
    // been surveyed cannot fake this one: with no column probe its clip is 'unknown', so nothing in
    // it reads empty and nothing in it is meshed.
    let open = 0;
    while (open < maxLod && this.regionResolved(open)) open++;
    // THE CEILING IS THE FIRST FILL'S ORDER, AND ONLY THAT. Re-imposed every pass it forbids a
    // level that has capacity: pinned at 0 it was the reported pan hang, and pinned at 1 it left
    // lod 2 dark for a pan's whole duration. Past the first fill the staircase is ordering alone.
    this.openMaxLod = this.baseEverComplete ? maxLod : Math.min(maxLod, open + this.gateRelief);

    // ONE STALL CLOCK for both ends, over the level the ladder is actually working on. Both ask the
    // identical question — "is this level draining?" — so two clocks with two constants was one too
    // many. On STALL rather than on elapsed time: any new low in outstanding work is progress and
    // re-arms it.
    // NOT UNTIL THE LEVEL HAS BEEN SURVEYED. This runs BEFORE requestLodShellData, so on the pass a
    // level opens its outstanding count is still the empty 0 it had while held — which would stand as
    // an unbeatable low and run the clock straight into a false stall (measured: the watchdog fired
    // every time the ladder advanced). While unsurveyed the clock is re-armed instead, so the level's
    // FIRST real measurement becomes the low to beat.
    const activeKey = open;
    const active = open;
    if (activeKey !== this.gateActiveKey) {
      this.gateActiveKey = activeKey;
      this.gateRelief = 0;
      this.lodGateBest = Infinity;
      this.lodGateBestMs = now;
    }
    const surveyed = active === 0 ? !!this.lastBFSChunk : !!this.lodOpened[active];
    // lod 0 IS clocked — the old guard excluded exactly the state that needed rescuing. Its progress
    // is ARRIVALS, not a new low in outstanding work: a cold load's backlog plateaus for tens of
    // seconds while chunks pour in, and reading that as a stall force-opened the coarse levels
    // part-way through the very fill fine-first exists to order.
    if (active < maxLod && surveyed) {
      const outstanding = active === 0 ? -this.baseArrivals : this.outstandingAt(active);
      if (outstanding < this.lodGateBest) {
        this.lodGateBest = outstanding;
        this.lodGateBestMs = now;
      }
      if (now - this.lodGateBestMs > VoxelWorld.LOD_GATE_STALL_MS) {
        this.gateRelief++;
        this.lodGateBest = Infinity;
        this.lodGateBestMs = now;
      }
    } else {
      this.lodGateBest = Infinity;
      this.lodGateBestMs = now;
    }
  }

  /**
   * READ-ONLY snapshot of the fine-first gate and every queue it decides on, uncached — the panel
   * summaries above are on a 500 ms window, which at one frame per 16.7 ms of a virtual clock is
   * thirty frames of the same answer. Measurement only; nothing here mutates.
   *
   * `regions` is THE SAME census the gate decides on, recomputed live rather than read off the
   * pass's rows: a frame that ran no pass would otherwise report the last pass's screen, and the
   * headless oracle's region invariants compare it against the scene graph frame by frame.
   */
  debugStreamGate(): {
    now: number; displayBaseLod: number; targetBaseLod: number; maxLod: number;
    openMinLod: number; openMaxLod: number; decimateFrom: number; baseComplete: boolean;
    gateRelief: number; gateActiveKey: number; lodGateBest: number; stallAgeMs: number;
    baseEverComplete: boolean; centreMoving: boolean;
    bfsFrontierActive: boolean; baseRequestBacklog: number;
    pendingChunks: number; pendingTiles: number; pendingColumns: number;
    pendingLodChunks: number; pendingLodColumns: number;
    remeshQueue: number; remeshInFlight: number; pendingApply: number;
    terrain: { queued: number; inFlight: readonly number[] };
    perLod: Array<{
      lod: number; open: boolean; complete: boolean; unissued: number; inFlight: number;
      dispatchable: boolean; resident: number; deficit: number; opened: boolean; spills: boolean;
    }>;
    regions: readonly RegionCensus[];
  } {
    const now = performance.now();
    const maxLod = this.lodManager.active ? this.lodManager.maxLod : 0;
    const regions = this.regionCensus();
    const resolved = (lod: number): boolean => {
      if (regions.length === 0) return false;          // unsurveyed, not resolved
      const row = regions[lod];
      return row === undefined || row.resolved >= row.wanted;
    };
    const perLod = [];
    for (let lod = 0; lod <= maxLod; lod++) {
      perLod.push({
        lod,
        open: this.levelOpen(lod),
        complete: resolved(lod),
        unissued: lod === 0 ? this.baseRequestBacklog : (this.lodUnissued[lod] ?? 0),
        inFlight: lod === 0 ? this.pendingChunks.size : this.pendingCountAtLod(lod),
        dispatchable: this.remeshPipeline.hasDispatchableAtLod(lod),
        resident: this.residentByLod[lod] ?? 0,
        deficit: this.lodManager.active ? (this.lodManager.deficitByLod[lod] ?? 0) : 0,
        opened: lod === 0 ? !!this.lastBFSChunk : !!this.lodOpened[lod],
        spills: false,
      });
    }
    return {
      now,
      displayBaseLod: this.displayBaseLod, targetBaseLod: this.targetBaseLod, maxLod,
      openMinLod: this.stepLod, openMaxLod: this.openMaxLod,
      decimateFrom: this.decimateFrom, baseComplete: this.baseComplete,
      gateRelief: this.gateRelief, gateActiveKey: this.gateActiveKey,
      lodGateBest: this.lodGateBest, stallAgeMs: now - this.lodGateBestMs,
      baseEverComplete: this.baseEverComplete, centreMoving: this.centreMoving(),
      bfsFrontierActive: this.bfsFrontierActive, baseRequestBacklog: this.baseRequestBacklog,
      pendingChunks: this.pendingChunks.size, pendingTiles: this.pendingTiles.size,
      pendingColumns: this.pendingColumns.size,
      pendingLodChunks: this.pendingLodChunks.size, pendingLodColumns: this.pendingLodColumns.size,
      remeshQueue: this.remeshPipeline.size,
      remeshInFlight: this.remeshPipeline.inFlightMarginCount,
      pendingApply: this.remeshPipeline.pendingApplyCount,
      terrain: this.localPool?.stats() ?? { queued: 0, inFlight: [] },
      perLod,
      regions,
    };
  }

  /** READ-ONLY view of the resolution predicate — same function the census runs on. */
  debugProvablyEmpty(cx: number, cy: number, cz: number, lod: number): boolean {
    return this.provablyEmpty(cx, cy, cz, lod);
  }

  /**
   * READ-ONLY: where one wanted chunk actually is in the pipeline. Measurement only.
   * unwanted = not even in the level's load set (gated out), unissued-empty = in the load set but
   * provably nothing to draw, unissued-wanted = in the load set with no request out, inflight =
   * requested, parked/meshing/apply/hidden/drawn = past arrival.
   */
  debugChunkStreamState(cx: number, cy: number, cz: number, lod: number): string {
    const key = chunkKeyLod(cx, cy, cz, lod);
    if (this.chunks.has(key)) {
      if (this.terrain.isVisible(key)) return 'drawn';
      if (this.remeshPipeline.hasPendingApply(key)) return 'apply';
      if (this.remeshPipeline.isBusy(key)) return 'meshing';
      // TWO REASONS A RESIDENT CHUNK HOLDS NO MESH and the census cares which: the render set
      // refuses to build one ('parked' — occluded by design, and resolved), or it is queued and
      // still waiting on its high-side margin sources ('margins' — genuinely owed). `isBusy`
      // reports false for both, which is what made them one word.
      if (!this.geometries.has(key)) {
        return this.shouldMeshChunk(cx, cy, cz, lod) ? 'margins' : 'parked';
      }
      return 'hidden';
    }
    const k0 = lod === 0 ? this.pendingChunks.has(key) : this.pendingLodChunks.has(key);
    if (k0) return 'inflight';
    if (this.lodManager.desiredLoadSet(lod)?.has(packCoord(cx, cy, cz))) {
      return this.debugProvablyEmpty(cx, cy, cz, lod) ? 'unissued-empty' : 'unissued-wanted';
    }
    return 'unwanted';
  }

  /** Age of the oldest outstanding request in each pending set, in ms. Read-only; measurement only. */
  debugPendingAges(): { chunks: number; tiles: number; columns: number } {
    const now = Date.now();
    const oldest = (times: ReadonlyMap<string | number, number>): number => {
      let age = 0;
      for (const t of times.values()) age = Math.max(age, now - t);
      return age;
    };
    return {
      chunks: oldest(this.pendingChunkTimes),
      tiles: oldest(this.pendingTileTimes),
      columns: oldest(this.pendingColumnTimes),
    };
  }

  /** Nothing in flight and nothing left to ask for. A mesh dispatch in this state is churn, not
   *  progress — no data arrived to justify it. */
  private streamingIdle(): boolean {
    return this.pendingChunks.size === 0 && this.pendingTiles.size === 0
      && this.pendingColumns.size === 0 && this.pendingLodChunks.size === 0
      && this.pendingLodColumns.size === 0 && this.baseRequestBacklog === 0;
  }

  /** `1981 (1.4x/chunk) idle 0` — see meshedEver for why the old ratio could not be read. */
  getMeshChurnSummary(): string {
    // Dispatches per DISTINCT chunk ever meshed, and nothing else. The running total only counts up
    // whatever the engine does, and `idle` was a second pathology hiding behind the first: 1.0 means
    // each chunk meshed once, which is the only reading anyone ever acted on.
    const distinct = this.meshedEver.size;
    return `${distinct > 0 ? (this.meshDispatchTotal / distinct).toFixed(1) : '0.0'}x`;
  }

  /** Work still owed at one level: unrequested + in flight + dispatchable mesh. The stall clock's
   *  only input, so it must cover every way a level can still be busy. */
  private outstandingAt(lod: number): number {
    if (lod === 0) {
      return this.baseRequestBacklog + this.pendingChunks.size + this.pendingTiles.size
        + this.pendingColumns.size + (this.remeshPipeline.hasDispatchableAtLod(0) ? 1 : 0);
    }
    return (this.lodUnissued[lod] ?? 0) + this.pendingCountAtLod(lod)
      + (this.remeshPipeline.hasDispatchableAtLod(lod) ? 1 : 0);
  }

  /**
   * May level `lod` be ACQUIRED this pass? The fine-first CEILING, and nothing else — the decimation
   * end is an order (see stepLod), because a level held shut can never clear the deficit that named
   * it: clearing it needs precisely the coverage the embargo forbade, which is a self-sustaining
   * lock, and the measured one had all twelve workers idle for the whole of it.
   *
   * The ceiling itself is a first-fill contract, not a steady-state rule: `baseEverComplete` latches
   * it open after the first fill, and the stall clock is the backstop.
   */
  private levelOpen(lod: number): boolean {
    if (lod === 0) return true;
    return lod <= this.openMaxLod;
  }

  /** Is the streaming centre still moving? See lastCentreMoveMs. */
  private centreMoving(): boolean {
    return this.lastCentreMoveMs > 0
      && performance.now() - this.lastCentreMoveMs < VoxelWorld.CENTRE_MOTION_MS;
  }

  /** The census under the base-classification memo: it asks a lod-0 position's state twice (empty,
   *  then own-mesh) and each answer costs up to ten probes. */
  private regionCensus(): RegionCensus[] {
    this.coverageCore.beginPass();
    try {
      return this.lodManager.computeRegionResolution();
    } finally {
      this.coverageCore.endPass();
    }
  }

  /**
   * IS THE REGION LEVEL `lod` OWNS OUTRIGHT FULLY RESOLVED? Reads this pass's census (published by
   * recomputeLodGate), so every consumer in a pass gets the same answer for the price of one scan.
   *
   * THE DISTINCTION IS THE WHOLE DESIGN, and two wrong predicates sit either side of this one.
   * `isLevelFullyCovered` demands every in-range position be rendered or clipped, which a coarse
   * chunk INSIDE the finer level's box can never be (the walk suppresses it, and it is not sky) — so
   * it is false forever and embargoes everything above it. The queue-drain predicate that replaced
   * it went the other way and stopped looking at the screen at all. A_k — the annulus the level owns
   * outright — is the region where "has this level delivered" is a fair question, and RESOLVED is it.
   */
  private regionResolved(lod: number): boolean {
    // NO CENSUS AT ALL is not a resolved world, it is an unsurveyed one — the onion has no shells
    // yet (first pass, or coarse generation not available). An empty census read as "resolved"
    // latched baseEverComplete on frame 1 and handed the whole ladder to the coarse levels before
    // lod 0 had drawn a pixel.
    if (this.regionRows.length === 0) return false;
    const row = this.regionRows[lod];
    return row === undefined || row.resolved >= row.wanted;
  }

  /** Requests in flight for one level — chunks and column bundles both, since coarse arrives almost
   *  entirely as bundles. Both sets are windowed to a dozen entries, so scanning is free. */
  private pendingCountAtLod(lod: number): number {
    let n = 0;
    for (const key of this.pendingLodChunks) if (parseChunkKey(key).lod === lod) n++;
    for (const colKey of this.pendingLodColumns) if (columnKeyLod(colKey) === lod) n++;
    return n;
  }

  private computeBaseComplete(): boolean {
    if (this.displayBaseLod !== 0) return true;
    // Before the first BFS there is no survey to be complete against, and the census over absent
    // shells reads "nothing wanted" — which would open the gate on the very first pass, i.e. exactly
    // the cold start this exists to order.
    if (!this.lastBFSChunk) return false;
    // A_0 RESOLVED, and nothing else. The queue terms are subsumed — an unarrived chunk is neither
    // displayed nor provably empty — and so is the BFS frontier: while it is active, unreached cells
    // classify 'waiting' rather than 'occluded' (see CoverageCore), so they hold the region open.
    // The old explicit frontier term additionally had to be skipped under motion, where a
    // pan-fed frontier is never quiet; the latch above handles motion now, in one place.
    return this.regionResolved(0);
  }

  private retainsLod0(cx: number, cy: number, cz: number): boolean {
    if (!this.lastPlayerChunk) return true;
    // ZOOMED OUT ≥ 2 LEVELS: nothing at lod 0 is retained at all. The radius above is derived from
    // what the lod-0 RENDER SET needs, and at a coarse base there is no lod-0 render set — the BFS
    // doesn't run, the walk draws coarse nodes, and the whole island sits hidden under its parents
    // costing ~128 KB of voxel data plus a batch range each (~1k chunks after a session at the
    // surface). Zoom-out alone used to free none of it: the radius follows the stream centre, so the
    // island survived until the user panned 9 chunks clear of it.
    //
    // Threshold is 2, not 1, for two reasons: base 1 is a single refine step from 0 and the retired
    // fallback can still be drawing lod-0 chunks through that transition (so small zoom wiggles at
    // the surface must not thrash residency), and re-entry always passes through base 1, which gives
    // lod-0 streaming a head start while the coarse levels still cover the view. Chunks the walk
    // currently draws are kept regardless — that guard sits in unloadDistantChunks, ahead of this.
    if (this.displayBaseLod >= 2) return false;
    const r = this._visibilityRadius + 3 + VISIBILITY_UNLOAD_BUFFER;
    return Math.abs(cx - this.lastPlayerChunk.cx) <= r
      && Math.abs(cy - this.lastPlayerChunk.cy) <= r
      && Math.abs(cz - this.lastPlayerChunk.cz) <= r;
  }

  
  /**
   * Unload lod-0 chunks outside the retention predicate (see retainsLod0), except those the LOD walk
   * still draws.
   *
   * It used to ALSO keep anything in `cachedReachable`, which was both redundant and a leak. Redundant
   * at base 0: the BFS reaches at most the octet-aligned box (radius + 1), strictly inside the
   * retention radius (radius + 3 + buffer), so the clause could never save a chunk the predicate
   * wanted gone. A leak at a coarse base: the lod-0 BFS does not run while zoomed out (see the
   * `displayBaseLod === 0` gate), so `cachedReachable` freezes at whatever it held when you zoomed
   * out — and this clause pinned that entire snapshot, forever, no matter how far you then panned.
   * That was the "group of lod-0 chunk bounds floating in the distance at max zoom-out, still in the
   * LODs count" report: ~1k chunks of voxel data that nothing could draw and nothing could evict.
   * Retention is now exactly the predicate plus the draw guard — no third, unmaintained input.
   *
   * Evictions run ON THE FRAME'S CLOCK (unloadOnClock). A zoom-out past base 2 drops the entire lod-0
   * island at once (~1k chunks), and each unload frees a batch range, fires the remesh listener and
   * tells the stitcher — whose seam consequences re-copy neighbours into the batch. The old fixed cap
   * of 64 per pass still measured 36 ms on a chunk cross, because 64 is not a cost. Every chunk in the
   * list is already undrawn, so what does not fit this frame waits for the next pass with nothing
   * visible.
   */

  private unloadDistantChunks(): void {
    if (!this.lastPlayerChunk) return;

    const chunksToUnload: string[] = [];
    for (const [key, chunk] of this.chunks) {
      // Coarse ring chunks: their residency is per-level (unloadDistantLodChunks) — this radius is
      // in lod-0 chunk units and would evict them instantly.
      if (chunk.lod > 0) continue;
      // Keep while the LOD walk still draws it (an outside-box fallback under an unmeshed ring
      // parent) — unloading mid-fallback flashes a hole. Once the parent renders, the walk stops
      // selecting it and the next pass unloads it (mirrors the coarse eviction guard).
      if (this.lastLodSelection?.get(chunk.cx, chunk.cy, chunk.cz, 0)) continue;
      if (this.retainedHides.has(key)) continue;
      if (this.pendingDisplayDecisions.get(key) === false) continue;

      if (!this.retainsLod0(chunk.cx, chunk.cy, chunk.cz)) {
        chunksToUnload.push(key);
      }
    }
    this.unloadOnClock(chunksToUnload);
  }

  /**
   * Unload at least MIN_UNLOADS_PER_PASS, then until the frame's clock. The floor is what keeps a
   * saturated frame (the walk ate the budget) from draining a 1k-chunk zoom-out one chunk per pass;
   * the clock is what keeps a roomy frame from taking the whole list. Leftovers are not carried:
   * every unload dirties visibility, so the next pass re-derives the list from the same three
   * inputs (centre, residency, selection) and continues — the DisplayCommit's rule, not a queue.
   */
  private static readonly MIN_UNLOADS_PER_PASS = 4;
  private unloadOnClock(keys: string[]): void {
    let n = 0;
    for (const key of keys) {
      if (n >= VoxelWorld.MIN_UNLOADS_PER_PASS && performance.now() >= this.frameBudgetEnd) break;
      this.unloadChunk(key);
      n++;
    }
    if (n < keys.length) this.dirtyVisibility();
  }

  /**
   * Request any unloaded chunks in the given key list from the server.
   * Skips chunks that are already loaded or pending.
   * Used by applyBuildOperation and BuildPreview to fetch affected chunks
   * that the client hasn't streamed yet.
   */
  requestMissingChunks(keys: string[]): void {
    for (const key of keys) {
      if (this.chunks.has(key) || this.pendingChunks.has(key)) continue;
      const { cx, cy, cz } = parseChunkKey(key);
      this.requestChunkFromServer(cx, cy, cz);
    }
  }

  /** True when running offline (no multiplayer server) — generate chunks locally. */
  private get isLocal(): boolean {
    return !useGameStore.getState().useServerChunks;
  }

  private getLocalPool(): TerrainWorkerPool {
    if (!this.localPool) {
      if (this.isLocal) {
        // Seed + generation settings come from the active local world (multi-world save/load).
        this.localPool = new TerrainWorkerPool(getActiveWorldSeed(), getActiveWorldGenConfig());
      } else {
        // Server mode: the pool exists solely for coarse far-LOD generation, seeded AND CONFIGURED
        // by the server's world (both from WELCOME) so the rings the client draws are the world the
        // server generates. Callers gate on serverWorldSeed being known (lodGenerationAvailable).
        const st = useGameStore.getState();
        this.localPool = new TerrainWorkerPool(st.serverWorldSeed ?? 12345, st.serverGenConfig ?? undefined);
      }
    }
    return this.localPool;
  }

  /** Can coarse LOD terrain be generated client-side right now? Local mode always; server mode
   *  once the server announced its world seed (older servers never do → rings stay off). */
  private get lodGenerationAvailable(): boolean {
    return this.isLocal || useGameStore.getState().serverWorldSeed !== null;
  }

  /**
   * Local chunk source: use the saved chunk if this world has one (only EDITED chunks are
   * persisted — see executeBuildOperation), otherwise generate it off-thread. Unedited terrain
   * is a pure function of the seed + gen config, so regenerating on every load keeps it in
   * step with the coarse far-view rings (which always regenerate) instead of pinning the
   * output of whatever generator version first explored it.
   */
  private loadLocalChunk(cx: number, cy: number, cz: number, key: string): void {
    const generate = () =>
      this.getLocalPool().requestChunk(cx, cy, cz, (d) => this.receiveChunkData(d));

    if (!hasChunk(key)) {
      generate();
      return;
    }
    // A persisted read must ALWAYS terminate the request, one way or another: on success,
    // on rejection, or on timeout. An IndexedDB read that simply never settles (observed
    // after clear-and-reload, where the store is torn down under in-flight reads) otherwise
    // strands the key in pendingChunks forever and permanently consumes request budget —
    // the world then never streams again.
    //
    // This used to be masked: a player with no ground fell, moved to different chunks, and
    // asked for different keys. Voxel physics instead FREEZES the player until their own
    // chunk arrives, so a stranded request is now a permanent, player-visible stall.
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn();
    };
    const timer = setTimeout(() => finish(generate), IDB_CHUNK_READ_TIMEOUT_MS);
    const epoch = this.worldEpoch;
    loadChunk(key)
      .then((saved) => finish(() => {
        if (epoch !== this.worldEpoch) return;          // switched worlds mid-read
        if (saved) this.receiveChunkData({ chunkX: cx, chunkY: cy, chunkZ: cz, voxelData: saved, lastBuildSeq: 0 });
        else generate();
      }))
      .catch(() => finish(() => { if (epoch === this.worldEpoch) generate(); }));
  }

  /**
   * Local surface-column source. The worker generates the whole column; a chunk with a saved
   * copy (i.e. an edited one — see loadLocalChunk) is replaced by it so edits win over
   * regeneration. Unedited chunks are used as generated and never persisted.
   */
  private async handleLocalColumn(columnData: SurfaceColumnResponse): Promise<void> {
    const epoch = this.worldEpoch;
    for (const chunk of columnData.chunks) {
      const key = chunkKey(columnData.tx, chunk.chunkY, columnData.tz);
      if (hasChunk(key)) {
        const saved = await loadChunk(key);
        // Re-checked INSIDE the loop: this awaits once per chunk in the column, so a switch can
        // land between two of them.
        if (epoch !== this.worldEpoch) return;
        if (saved) chunk.voxelData = saved;
      }
    }
    if (epoch !== this.worldEpoch) return;
    this.receiveSurfaceColumnData(columnData);
  }

  /** Drop the terrain pool so the next request rebuilds it on the CURRENT active world's seed.
   *  For callers that need a fresh-world request in flight BEFORE reloadLocalWorld runs (the path
   *  spawn ask-first flow) — pair with reloadLocalWorld(pos, true) so the reload doesn't kill it. */
  resetLocalPool(): void {
    this.localPool?.dispose();
    this.localPool = null;
  }

  /** Rebuild the local world from scratch (e.g. after switching active world). `keepPool` keeps the
   *  terrain pool alive — only for callers that already reset it on the new world's seed and have a
   *  request in flight on it. */
  reloadLocalWorld(playerPos?: THREE.Vector3, keepPool = false): void {
    // Drop the terrain pool so it recreates with the new active-world seed.
    if (!keepPool) this.resetLocalPool();
    // DROPPED BEFORE ANYTHING STREAMS, not after. `clearAndReload` runs a full update pass, and
    // `handleLodColumn` widens every coarse column's clip by `editLayers.clipFor(...)` — so with the
    // hydrate (whose own clear is synchronous) left until afterwards, that first pass of the NEW
    // world reads the OLD world's coarse edit clips and loads chunks against them.
    this.editLayers.clear();
    this.clearAndReload(playerPos);
    // The OUTGOING world's unfolded edits are deliberately abandoned rather than flushed here:
    // WorldManager has already switched worlds, so a flush would write them under the NEW world's
    // id. Their markers survive in the old world's store and replay when it is next activated.
    void this.hydrateEditLayers();
  }

  /**
   * Ask a terrain worker for a spawn point on the path network (world metres), or null — a world
   * without regions/paths, or server play, where the server owns spawn. The pending callback dies
   * with the pool on a world switch, so a stale world's answer can never land.
   */
  requestPathSpawn(cb: (pt: { x: number; y: number; z: number } | null) => void): void {
    if (!this.isLocal) { cb(null); return; }
    this.getLocalPool().requestPathSpawn(cb);
  }

  /**
   * Request a chunk — from the server, or generate it locally (off-thread) in
   * offline mode. The worker callback feeds the same receiveChunkData path.
   */
  private requestChunkFromServer(cx: number, cy: number, cz: number): void {
    const key = chunkKey(cx, cy, cz);
    this.pendingChunks.add(key);
    this.pendingChunkTimes.set(key, Date.now());

    if (this.isLocal) {
      this.loadLocalChunk(cx, cy, cz, key);
      return;
    }

    const request = encodeVoxelChunkRequest({
      chunkX: cx,
      chunkY: cy,
      chunkZ: cz,
      forceRegen: useGameStore.getState().forceRegenerateChunks,
    });
    sendBinary(request);
  }

  /**
   * Request a tile from the server.
   * Server generates all surface chunks under the hood but only returns tile data.
   */
  private requestTileFromServer(tx: number, tz: number): void {
    const columnKey = packPairKey(tx, tz);
    if (this.pendingTiles.has(columnKey)) return;

    this.pendingTiles.add(columnKey);
    this.pendingTileTimes.set(columnKey, Date.now());

    if (this.isLocal) {
      // Request the FULL COLUMN, not a tile. generateTile ran buildSurfaceColumn —
      // generating every chunk in the column (stamps included) — and DISCARDED the chunks,
      // returning only heights; the per-chunk phase then regenerated them, usually on a different
      // worker (per-worker caches, no affinity). That double generation plus the extra tile→chunk
      // round-trip per ring was the streaming bottleneck (~2.5 columns/s: the world never
      // converged, dispatches never quiesced, and the load frontier lagged pans → gaps).
      // generateColumn does the same work ONCE and returns tile + surface chunks together.
      this.requestLocalColumnAsTile(tx, tz, columnKey);
      return;
    }

    sendBinary(encodeMapTileRequest({ tx, tz }));
  }

  /**
   * Local-mode frontier column request registered under the TILE pending set (the caller,
   * requestTileFromServer, budgeted and keyed it there): one worker task generates the tile AND
   * its surface chunks; the shared column ingest (handleLocalColumn → receiveSurfaceColumnData)
   * persists and applies both.
   */
  private requestLocalColumnAsTile(tx: number, tz: number, columnKey: number): void {
    this.getLocalPool().requestColumn(tx, tz, (data) => {
      this.pendingTiles.delete(columnKey);
      this.pendingTileTimes.delete(columnKey);
      void this.handleLocalColumn(data);
    });
  }

  /**
   * A build changed this column's surface: re-derive its chunk range (the lod-0 sky clip) from
   * the edited tile heights. In-memory only — column heights are never persisted; a reload
   * re-derives them from generation plus the persisted edited chunks streaming back in.
   */
  updateColumnFromEditedTile(tx: number, tz: number, heights: Int16Array): void {
    this.setColumnInfo(tx, tz, heights);
    this.dirtyVisibility();
  }

  /**
   * Receive standalone tile data from the server.
   * Stores column info (maxCy) so BFS can request chunks for this column.
   */
  receiveTileData(tileData: MapTileResponse): void {
    try {
      this.receiveTileDataInner(tileData);
    } finally {
    }
  }

  private receiveTileDataInner(tileData: MapTileResponse): void {
    const { tx, tz, heights, materials } = tileData;
    const columnKey = packPairKey(tx, tz);
    
    // Remove from pending
    this.pendingTiles.delete(columnKey);
    this.pendingTileTimes.delete(columnKey);
    
    // Compute and store the column's chunk range from the (stamp-corrected) tile heights.
    this.setColumnInfo(tx, tz, heights);

    // Notify external systems (map cache)
    if (this.onTileReceived) {
      this.onTileReceived(tx, tz, heights, materials);
    }

    // Chunk requests for this column can proceed on the next pass (box unchanged — see M3).
    this.dirtyVisibility();
  }

  /**
   * A streamed chunk payload waiting to be ingested — see queueIngest / drainIngest.
   * `alwaysFanout` distinguishes a single-chunk arrival (server push / heal / margin request, which
   * always re-meshes its neighbours) from a column member (which only does so when it REPLACED
   * existing data — generation is deterministic, so a chunk that raced ahead of its column is
   * byte-identical and re-ingesting it would relight its whole neighbourhood for nothing).
   */
  private ingestQueue: string[] = [];
  private ingestPayloads = new Map<string, {
    cx: number; cy: number; cz: number; data: Uint32Array; lastBuildSeq: number; alwaysFanout: boolean;
    /** Coarse (lod > 0) arrivals share the queue: the same clock, a different ingest (ingestLodChunk). */
    lod: number; lit: boolean;
  }>();

  /**
   * Ingest pacing. Ingest is main-thread work per chunk (sunlight + visibility + relight fan-out) and
   * used to run straight out of the worker/server callback — so a column landing at once cost
   * 19 × 0.63 = 12 ms in a single frame GAP, inside no budget and invisible to the frame profiler
   * ([lod-audit] reported it as `offFrame ingest=12.0ms x19`). Same reasoning as the apply drain: the
   * total work is unchanged, just paced so one frame doesn't eat it all.
   *
   * Paced by the FRAME's clock (voxel/frameBudget.ts), not a fixed count. A fixed count couples
   * streaming throughput to frame rate: 6/frame is ~360 chunks/s at 60 fps (far above what the
   * generators produce, so it never binds) but only ~3/s at 0.5 fps, where it throttled filling hard
   * enough to leave transient holes in the verification run. The clock adapts instead — a slow device
   * pays the same milliseconds and simply takes more frames. At least one always lands, so progress can
   * never stop.
   */

  /**
   * Queue a streamed chunk payload for ingest on a later frame. Deliberately does NOT touch
   * `pendingChunks`: that set means "data is coming" (isPending / isMarginSourceExpected read it, and
   * the mesher defers a chunk whose margin source is expected), which stays true until the data is
   * actually in the chunk. cleanStalePending skips queued keys so it can't expire — and re-request —
   * a chunk whose voxels are already in hand.
   */
  private queueIngest(
    cx: number, cy: number, cz: number, data: Uint32Array, lastBuildSeq: number, alwaysFanout: boolean,
  ): void {
    const key = chunkKey(cx, cy, cz);
    const existing = this.ingestPayloads.get(key);
    // REPLACE, never stack: a second payload for the same key (a server edit re-send racing a stream
    // arrival) supersedes the first, and keeps its queue position so column order is preserved.
    this.ingestPayloads.set(key, {
      cx, cy, cz, data, lastBuildSeq, alwaysFanout: alwaysFanout || (existing?.alwaysFanout ?? false),
      lod: 0, lit: false,
    });
    if (!existing) this.ingestQueue.push(key);
  }

  /**
   * The coarse counterpart. Coarse chunks used to ingest INSIDE the worker callback — a bundle of up
   * to MAX_BUNDLE_CHUNKS at once, each a data copy, an edit-layer merge, a remesh enqueue and a
   * seven-neighbour scan — between frames, in no budget: the `offfrm` peak (35 ms measured while
   * running). Nothing needs a coarse chunk the frame it arrives (its mesh is 50-100 ms behind in the
   * worker pipeline anyway), so it lands under the clock like lod 0. The chunk is not yet in
   * `chunks` while queued, so the request planner skips queued keys (see requestLodShellData) — the
   * same "in hand, not yet applied" rule cleanStalePending applies to lod 0.
   */
  private queueLodIngest(cx: number, cy: number, cz: number, lod: number, data: Uint32Array, lit: boolean): void {
    const key = chunkKeyLod(cx, cy, cz, lod);
    const existing = this.ingestPayloads.get(key);
    this.ingestPayloads.set(key, { cx, cy, cz, data, lastBuildSeq: 0, alwaysFanout: false, lod, lit });
    if (!existing) this.ingestQueue.push(key);
  }

  /**
   * Neighbour relights an ingest could not afford this frame — key → "every source was a NEW chunk"
   * (false once any source was an update, which needs the full re-mesh path). Keyed by neighbour so
   * two sources deferring the same chunk cost one relight: computeChunkSunlight gathers all six
   * neighbours anyway, so a single pass covers both donors. See relightNeighbor in ingestChunkData.
   */
  private readonly deferredRelights = new Map<string, boolean>();

  /**
   * The relight of a neighbour whose border light an arrival may have changed, and how that reaches
   * the screen (by phase). Split out of ingestChunkData's closure so a deferred relight runs the
   * same code a frame later.
   */
  private relightNeighborChunk(nChunk: Chunk, isNewChunk: boolean): void {
    this.computeChunkSunlight(nChunk.cx, nChunk.cy, nChunk.cz, nChunk.data);
    if (isNewChunk) {
      if (nChunk.phase === ChunkPhase.MeshedComplete && this.resampleChunkLight(nChunk)) {
        return; // light rewritten in place — geometry untouched, phase preserved
      }
      // An INCOMPLETE neighbour is hidden (MeshedIncomplete never renders), so its stale baked
      // light is invisible; its data is relit above, and the re-mesh that eventually completes
      // it bakes that light. The NEGATIVE_MARGIN_OFFSETS_7 loop in ingest re-queues it exactly when
      // the arrival is one of its margin sources (new geometry input) — re-adding it HERE too
      // re-meshed the whole hidden frontier line on every nearby arrival, gaining nothing
      // (the never-settling remesh queue while stationary).
      if (nChunk.phase === ChunkPhase.MeshedIncomplete) {
        nChunk.dirty = true;
        return;
      }
    }
    nChunk.dirty = true;
    this.remeshPipeline.add(nChunk.key);
  }

  /** Run deferred neighbour relights against the frame's clock, at least one per frame. */
  private drainDeferredRelights(budgetEnd: number): void {
    if (this.deferredRelights.size === 0) return;
    let n = 0;
    for (const [nKey, newOnly] of this.deferredRelights) {
      if (n > 0 && performance.now() >= budgetEnd) break;
      this.deferredRelights.delete(nKey);
      n++;
      const nChunk = this.chunks.get(nKey);
      if (!nChunk) continue; // evicted since — nothing owes it light any more
      perfStats.begin('lightNbr');
      this.relightNeighborChunk(nChunk, newOnly);
      perfStats.end('lightNbr');
    }
  }

  /**
   * Apply queued chunk payloads until the frame's clock runs out, oldest first. FIFO matters: columns
   * are queued top-down so each chunk's sunlight sees the chunk above it already ingested, and a column
   * split across frames still drains in that order.
   */
  private drainIngest(budgetEnd: number): void {
    if (this.ingestQueue.length === 0) return;
    let n = 0;
    while (this.ingestQueue.length > 0) {
      if (n > 0 && performance.now() >= budgetEnd) break; // always land at least one
      const key = this.ingestQueue.shift()!;
      const p = this.ingestPayloads.get(key);
      if (!p) continue; // superseded by a path that ingested it directly
      this.ingestPayloads.delete(key);
      n++;

      if (p.lod > 0) {
        this.ingestLodChunk({ chunkX: p.cx, chunkY: p.cy, chunkZ: p.cz, lod: p.lod, voxelData: p.data, lit: p.lit });
        continue;
      }

      const existing = this.chunks.get(key);
      // Byte-identical re-arrival: nothing to ingest, nothing to fan out (the column path's dedupe,
      // moved here because "new vs replaced" is only knowable at ingest time).
      if (existing && !p.alwaysFanout && arraysEqual(existing.data, p.data)) continue;

      this.ingestChunkData(p.cx, p.cy, p.cz, p.data, p.lastBuildSeq);
      if (p.alwaysFanout || existing) this.queueNeighborRemesh(p.cx, p.cy, p.cz);
    }
  }

  /**
   * Internal helper to ingest chunk data (shared by receiveChunkData and receiveSurfaceColumnData).
   * @returns The chunk that was created/updated
   */
  private ingestChunkData(cx: number, cy: number, cz: number, voxelData: Uint32Array, lastBuildSeq: number = 0): Chunk {
    // Timed as a frame section: streamed arrivals are drained inside update() (drainIngest), so this
    // is no longer off-frame work. The direct callers that remain (edits, preview) are synchronous by
    // necessity and land in the same section.
    try {
      return this.ingestChunkDataInner(cx, cy, cz, voxelData, lastBuildSeq);
    } finally {
    }
  }

  private ingestChunkDataInner(cx: number, cy: number, cz: number, voxelData: Uint32Array, lastBuildSeq: number = 0): Chunk {
    const key = chunkKey(cx, cy, cz);
    
    // Remove from pending
    this.pendingChunks.delete(key);
    this.pendingChunkTimes.delete(key);
    
    // Create or update chunk
    let chunk = this.chunks.get(key);
    const isNewChunk = !chunk;
    if (!chunk) {
      chunk = new Chunk(cx, cy, cz);
      this.chunks.set(key, chunk);
      this.indexChunk(chunk);
    }

    // Skip re-processing if the voxel data is identical (server re-send or no-op)
    if (!isNewChunk && arraysEqual(chunk.data, voxelData) && chunk.lastBuildSeq === lastBuildSeq) {
      console.warn(`[VoxelWorld] Received identical chunk data for (${cx}, ${cy}, ${cz}), skipping update`);
      return chunk;
    }
    
    // Copy voxel data
    chunk.data.set(voxelData);
    chunk.dirty = true;
    chunk.lastBuildSeq = lastBuildSeq;
    
    // Sunlight column propagation (before visibility + remesh)
    perfStats.begin('lightSun');
    this.computeChunkSunlight(cx, cy, cz, chunk.data);
    perfStats.end('lightSun');
    
    // Compute visibility graph for this chunk
    chunk.visibilityBits = computeVisibility(chunk.data);

    // Queue for remeshing
    this.remeshPipeline.add(key);
    
    // A new chunk can open visibility paths — re-run the pass. The box is unchanged; nulling
    // it here (on EVERY ingest) was the other half of M3's re-partition storm.
    if (isNewChunk) {
      this.dirtyVisibility();
    }

    // Relight and re-queue face-adjacent neighbors so their border light and
    // margin data are up to date. Runs for both new and updated chunks —
    // updated chunks may carry build modifications that change boundary voxels.
    //
    // The relight of a neighbour is skipped when THIS chunk donates no light across the shared face
    // (faceDonatesLight): border injection ignores source voxels with light <= 1, so a fully-dark
    // shared face (the common rock↔rock underground case) cannot change the neighbour's lit state —
    // the skip is output-preserving. This is the dominant saving during column-load bursts, where
    // most internal faces are solid rock. Faces here: +Y (above) is face 2, the horizontals below.
    //
    // The chunk BELOW is exempt: a solid arrival changes its `lightFromAbove` (open-sky → capped)
    // even while donating no border light, so it always relights.
    //
    // For the +Y (above) and the 4 horizontal neighbours the relight AND the remesh are BOTH
    // gated on faceDonatesLight — a neighbour that got no new light needs no remesh either, because
    // its GEOMETRY doesn't depend on this chunk: a chunk meshes its high-side border from its OWN
    // +X/+Y/+Z margin sources, so its −direction neighbour (this chunk) is never a mesh input. The
    // margin-consumers that DO read this chunk (its −X/−Y/−Z faces/edges/corner) are remeshed
    // unconditionally by the NEGATIVE_MARGIN_OFFSETS_7 loop below. Result: a fully-dark underground
    // arrival fans out to just its below relight + the margin loop, instead of re-meshing all 6
    // faces every time — the dominant remesh churn during a column-load burst.
    //
    // How a relit neighbour propagates to the screen depends on its phase. A NEW arrival cannot
    // change a MeshedComplete neighbour's GEOMETRY: non-consumers never read this chunk (see the
    // gate rationale above), and a complete margin-consumer meshed with every margin real-or-policy
    // — policy positions never load, so it can't have meshed "without" a chunk that just loaded.
    // Only its LIGHT changed → rewrite the light attributes in place (resampleChunkLight) instead
    // of queueing a full re-mesh. The re-mesh was worse than redundant: re-dispatch at the frontier
    // can find a margin source newly expected-but-missing and DEMOTE the complete chunk to
    // MeshedIncomplete → hidden → transient gaps, and the endless add/dispatch cycle at the
    // streaming frontier was why the remesh queue never settled. UPDATED chunks (remote build
    // edits) keep the full re-mesh: changed boundary voxels do alter consumer geometry.
    const relightNeighbor = (nx: number, ny: number, nz: number, faceToward: number | null) => {
      const nKey = chunkKey(nx, ny, nz);
      const nChunk = this.chunks.get(nKey);
      if (!nChunk) return;
      // For the +Y and horizontal faces the ONLY channel is border injection, which is monotone
      // increasing — so if it cannot brighten this neighbour anywhere, its light is already correct
      // and the relight is a provable no-op. faceDonatesLight (source carries light) is true for
      // every face of a sky-lit air chunk, which is why the pairwise test is the one that pays:
      // measured 2.2 sunlight passes per arrival, ~69% of ingest cost, mostly no-ops.
      // The below face passes null — its channel is the sky cap, gated by bottomRowSkyClass instead.
      if (faceToward !== null && !faceWouldBrighten(chunk.data, faceToward, nChunk.data)) return;
      // THE FAN-OUT IS ON THE FRAME'S CLOCK. One admitted ingest used to carry up to six neighbour
      // relights with it (~0.5 ms each) regardless of the budget — the ingest drain's "at least one"
      // was really "at least seven". Past the deadline the relight is queued and lands next frame:
      // a neighbour lit a frame late is invisible (its remesh was asynchronous anyway), and the
      // ingest itself has already recorded everything the relight needs (see drainDeferredRelights).
      if (performance.now() >= this.frameBudgetEnd) {
        const prev = this.deferredRelights.get(nKey);
        // An UPDATE source (not new) needs the full re-mesh path; once any source says so, keep it.
        this.deferredRelights.set(nKey, (prev ?? true) && isNewChunk);
        return;
      }
      perfStats.begin('lightNbr');
      this.relightNeighborChunk(nChunk, isNewChunk);
      perfStats.end('lightNbr');
    };

    // THE BELOW FACE. The chunk below depends on this one through exactly one quantity: this chunk's
    // bottom-row light (as its `lightFromAbove`, and as +Y border injection — the same values). It
    // was already lit under an ASSUMPTION about that row, so the relight is a provable no-op when the
    // real row matches: open-sky assumption ⇔ an all-LIGHT_MAX row (a sky-lit air chunk arriving above
    // ground), dark assumption ⇔ an all-dark row (solid rock arriving in the underground bulk). Both
    // are the common cases, and sunlight was measured at 69% of ingest cost (0.445 ms/call, 2.2 calls
    // per arrival) — the dominant main-thread cost while panning. Only NEW chunks may be skipped: an
    // UPDATE (build edit, server re-send) changes a row we no longer have the previous value of.
    const belowAssumedDark = (() => {
      const info = this.columnInfo.get(packColumn(cx, cz, 0));
      return !!info && cy - 1 < info.minCy; // mirrors sunlightFromAbove's DARK_ABOVE branch
    })();
    const belowRowClass = isNewChunk ? bottomRowSkyClass(chunk.data) : BOTTOM_ROW_MIXED;
    const belowUnchanged = belowAssumedDark
      ? belowRowClass === BOTTOM_ROW_ALL_DARK
      : belowRowClass === BOTTOM_ROW_ALL_FULL;
    if (!belowUnchanged) relightNeighbor(cx, cy - 1, cz, null);

    if (faceDonatesLight(chunk.data, 2 /* +Y */)) {
      relightNeighbor(cx, cy + 1, cz, 2);
    }

    for (const [dx, dy, dz] of FACE_OFFSETS_6) {
      if (dy !== 0) continue; // vertical already handled above
      if (!this.hasChunkAt(cx + dx, cy, cz + dz, 0)) continue;
      // dx/dz map to FACE_OFFSETS_6 indices: +X=0,-X=1,+Z=4,-Z=5.
      const face = dx === 1 ? 0 : dx === -1 ? 1 : dz === 1 ? 4 : 5;
      if (faceDonatesLight(chunk.data, face)) {
        relightNeighbor(cx + dx, cy, cz + dz, face);
      }
    }

    // Re-mesh the consumers whose mesh was built WITHOUT this chunk (its 7 negative-direction
    // neighbours: faces, edges, corner). Two exact markers, and it took both:
    //
    //  - phase MeshedIncomplete: a high FACE was skipped, so the mesh never draws.
    //  - extrapolatedMargins bit: this exact source was extrapolated. Necessary because a consumer
    //    missing only a DIAGONAL (+XY/+XZ/+YZ/+XYZ) is topologically complete and therefore DRAWN,
    //    with a wrong one-vertex-wide edge/corner column — and gating on the phase alone meant a
    //    diagonal arriving after its consumer meshed healed nothing, ever. Permanent hairline
    //    cracks along chunk edges and pinholes at corners, wherever streaming order brought a
    //    diagonal last.
    //
    // Still exact, still not churn: NEGATIVE_MARGIN_OFFSETS_7[i] is the index-wise mirror of
    // POSITIVE_MARGIN_OFFSETS_7[i], so the consumer at negative offset i is precisely the one
    // whose bit i names THIS chunk. Consumers that had it are untouched. Never-meshed consumers
    // are already queued from their own ingest. Applies to every ingest path.
    //
    // The mask comes from the PIPELINE, not the chunk: a consumer whose mesh is between dispatch and
    // apply has published nothing yet (a first mesh publishes phase Loaded / mask 0), so reading the
    // chunk alone made every arrival inside that window invisible — the permanent-crack bug in
    // marginConvergence.ts.
    for (let i = 0; i < NEGATIVE_MARGIN_OFFSETS_7.length; i++) {
      const [dx, dy, dz] = NEGATIVE_MARGIN_OFFSETS_7[i];
      const nKey = chunkKey(cx + dx, cy + dy, cz + dz);
      const consumer = this.chunks.get(nKey);
      if (!consumer) continue;
      if (marginConsumerNeedsRemesh(consumer.phase, this.remeshPipeline.extrapolatedMarginsFor(nKey), i)) {
        this.remeshPipeline.add(nKey);
      }
    }

    // Execute any build operations that were waiting for this chunk
    this.drainDeferredBuildOps();

    // Notify the minimap so it can capture this chunk's stamps (trees/rocks/etc.).
    this.onChunkIngested?.(key);

    return chunk;
  }

  /**
   * Compute sunlight columns for a chunk in-place.
   * Checks chunk-above data to determine sky exposure.
   * Then injects border light from face-adjacent neighbors and runs BFS.
   */
  // Timed by its CALLERS ('lightSun' for the ingested chunk itself, 'lightNbr' for a neighbour
  // relight) — the same work needs different attribution depending on who asked for it.
  private computeChunkSunlight(cx: number, cy: number, cz: number, data: Uint32Array): void {
    const lightFromAbove = this.sunlightFromAbove(cx, cy, cz);
    const neighbors = this.gatherFaceNeighbors(cx, cy, cz);
    // Combined pipeline: sky (column + BFS) then block (emitter BFS). Returns whether
    // the chunk holds any block light, cached on the Chunk to gate future relights.
    const hasBlock = computeAndPropagateLight(data, lightFromAbove, neighbors);
    const chunk = this.chunkAt(cx, cy, cz, 0);
    if (chunk) chunk.hasBlockLight = hasBlock;
  }

  /** Gather the 6 face-adjacent neighbor voxel arrays (+X,-X,+Y,-Y,+Z,-Z), null if unloaded. */
  private gatherFaceNeighbors(cx: number, cy: number, cz: number): (Uint32Array | null)[] {
    return FACE_OFFSETS_6.map(
      ([dx, dy, dz]) => this.chunkAt(cx + dx, cy + dy, cz + dz, 0)?.data ?? null,
    );
  }

  /**
   * Per-column light entering the top of a chunk. Uses the loaded chunk above if present; otherwise
   * assumes open sky UNLESS the chunk is fully underground — i.e. below the column's LOWEST surface
   * point (cy < minCy), where there's no open sky anywhere in the footprint. Those default to DARK,
   * so an underground cave isn't lit as open sky when the (solid) chunk above it hasn't loaded (and,
   * since the BFS can't traverse solid rock, may never load). Surface chunks — slopes, flush tops,
   * and columns at the BFS edge whose air chunk above isn't loaded — are at cy >= minCy and stay lit.
   * When a missing chunk above does load, ingest relights this chunk correctly.
   */
  private sunlightFromAbove(cx: number, cy: number, cz: number): Uint8Array | null {
    const fromAbove = getSunlitAbove(this.chunkAt(cx, cy + 1, cz, 0)?.data);
    if (fromAbove) return fromAbove; // chunk above loaded → real propagated light
    const info = this.columnInfo.get(packColumn(cx, cz, 0));
    return info && cy < info.minCy ? DARK_ABOVE : null; // fully underground → dark, else open sky
  }

  /** Temp-aware accessor for preview relight: preview (temp) data where present, else committed. */
  private readonly tempGetData = (cx: number, cy: number, cz: number): Uint32Array | null => {
    const c = this.chunkAt(cx, cy, cz, 0);
    return c ? (c.tempData ?? c.data) : null;
  };

  /**
   * Whether preview block relight is warranted: an emitter in a drawn chunk, or existing block
   * light anywhere in the drawn chunks' 3×3×3 region.
   */
  private previewRunBlock(drawn: Chunk[]): boolean {
    if (drawn.some((c) => chunkHasEmitter(c.tempData!) || c.hasBlockLight)) return true;
    for (const c of drawn) {
      for (let dz = -1; dz <= 1; dz++)
        for (let dy = -1; dy <= 1; dy++)
          for (let dx = -1; dx <= 1; dx++) {
            if (this.chunkAt(c.cx + dx, c.cy + dy, c.cz + dz, 0)?.hasBlockLight) return true;
          }
    }
    return false;
  }

  /**
   * Relight the chunks that will be RE-MESHED this preview, synchronously on their temp buffers
   * (cheap — a handful of chunks), through the shared relightRegion() so preview and commit don't
   * drift. This is the drawn chunks PLUS their margin-consumer neighbours (whose +margin geometry
   * changed and so are re-meshed by Pass 2b) — every re-meshed chunk must carry preview light or its
   * new mesh shows stale/dark light at the edit (the dark-border bug). Runs before meshing so the
   * mesh shows correct light immediately, waiting on nothing. Light spilling into chunks that are NOT
   * re-meshed is handled separately by relightPreviewSpill(). Each passed chunk needs a temp buffer.
   */
  relightPreviewMeshSet(meshKeys: string[]): void {
    const drawn = meshKeys
      .map((k) => this.chunks.get(k))
      .filter((c): c is Chunk => !!c && !!c.tempData);
    if (drawn.length === 0) return;

    const runBlock = this.previewRunBlock(drawn);
    const map = new Map<string, RelightTarget>();
    for (const c of drawn) {
      map.set(c.key, { cx: c.cx, cy: c.cy, cz: c.cz, sky: true, block: runBlock });
    }

    perfStats.begin('lightEdit');
    relightRegion(this.tempGetData, this.finalizeTargets(map));
    perfStats.end('lightEdit');
  }

  /**
   * Relight the SPILL neighbours — chunks changed by LIGHT but not by the draw (the 6 face
   * neighbours for sky border bleed, plus the loaded 3×3×3 for block when a torch/emitter is
   * involved) — synchronously, reading the already-relit drawn chunks. The relight itself is cheap;
   * each neighbour's display is refreshed with a light-only resample + a ranged write into its
   * batch range (no re-mesh — see TerrainBatch.updateChunkLight).
   *
   * `excludeKeys` are chunks already being re-meshed this preview (the drawn chunks and their
   * margin-consumer neighbours from Pass 2b). Those are skipped: their geometry — and therefore the
   * margin a light-only resample would read — changed, so resampling stale geometry would leave dark
   * seams; the re-mesh gives them correct light instead.
   *
   * @returns the spill neighbour keys whose display was overridden (caller restores them on exit).
   */
  relightPreviewSpill(drawnKeys: string[], excludeKeys: Set<string>): string[] {
    const drawn = drawnKeys
      .map((k) => this.chunks.get(k))
      .filter((c): c is Chunk => !!c && !!c.tempData);
    if (drawn.length === 0) return [];

    const map = this.collectSpillTargets(drawn, excludeKeys, this.previewRunBlock(drawn));
    if (map.size === 0) return [];

    // Temp copy each spill chunk (non-destructive) before relighting reads/writes it.
    for (const t of map.values()) {
      const n = this.chunkAt(t.cx, t.cy, t.cz, 0);
      if (n && !n.tempData) n.copyToTemp();
    }
    const spillTargets = this.finalizeTargets(map);

    perfStats.begin('lightEdit');
    relightRegion(this.tempGetData, spillTargets);

    // Light-only refresh of each spill neighbour's display (resample + merged-buffer slice write).
    const spillKeys: string[] = [];
    for (const t of spillTargets) {
      const key = chunkKey(t.cx, t.cy, t.cz);
      if (this.resamplePreviewChunkLight(key, true)) spillKeys.push(key);
    }
    perfStats.end('lightEdit');

    return spillKeys;
  }

  /**
   * The spill target map for a preview around `drawn`: the 6 sky-face neighbours of each drawn chunk,
   * plus the 3×3×3 block region when a torch/emitter is involved (`runBlock`). Loaded chunks only,
   * excluding `excludeKeys` (the re-meshed set — those get correct light from their re-mesh instead).
   * Shared by relightPreviewSpill (which relights the map) and collectSpillKeys (keys only) so the
   * two can't drift on which neighbours count as spill.
   */
  private collectSpillTargets(
    drawn: Chunk[],
    excludeKeys: Set<string>,
    runBlock: boolean,
  ): Map<string, RelightTarget> {
    const map = new Map<string, RelightTarget>();
    const ensure = (cx: number, cy: number, cz: number): RelightTarget | null => {
      const k = chunkKey(cx, cy, cz);
      if (excludeKeys.has(k) || !this.chunks.has(k)) return null;
      let t = map.get(k);
      if (!t) { t = { cx, cy, cz, sky: false, block: false }; map.set(k, t); }
      return t;
    };
    for (const c of drawn) {
      for (const [dx, dy, dz] of FACE_OFFSETS_6) {
        const t = ensure(c.cx + dx, c.cy + dy, c.cz + dz);
        if (t) t.sky = true;
      }
      if (runBlock) {
        for (let dz = -1; dz <= 1; dz++)
          for (let dy = -1; dy <= 1; dy++)
            for (let dx = -1; dx <= 1; dx++) {
              const t = ensure(c.cx + dx, c.cy + dy, c.cz + dz);
              if (t) t.block = true;
            }
      }
    }
    return map;
  }

  /**
   * The spill neighbour keys for a preview at `drawnKeys` (loaded, minus `excludeKeys`) — keys only,
   * no relight or temp copy. Lets BuildPreview reconcile which spill overrides to drop as the cursor
   * moves (restoring far neighbours that are no longer adjacent) cheaply, every frame, while the
   * spill RELIGHT itself stays deferred to settle.
   */
  collectSpillKeys(drawnKeys: string[], excludeKeys: Set<string>): Set<string> {
    const drawn = drawnKeys
      .map((k) => this.chunks.get(k))
      .filter((c): c is Chunk => !!c && !!c.tempData);
    if (drawn.length === 0) return new Set();
    return new Set(this.collectSpillTargets(drawn, excludeKeys, this.previewRunBlock(drawn)).keys());
  }

  /**
   * Finalize a relight target map: seed each sky target that has no loaded chunk above with the
   * column's light-from-above, then return the targets sorted top-down (cy descending) so the shared
   * relight sees each chunk's freshly-relit neighbour above. Shared by commit + both preview passes.
   */
  private finalizeTargets(map: Map<string, RelightTarget>): RelightTarget[] {
    for (const t of map.values()) {
      if (t.sky && !this.hasChunkAt(t.cx, t.cy + 1, t.cz, 0)) {
        t.skyAbove = this.sunlightFromAbove(t.cx, t.cy, t.cz);
      }
    }
    return [...map.values()].sort((a, b) => b.cy - a.cy);
  }

  /**
   * Light-only resample of a chunk's existing geometry from its temp (preview) or committed data,
   * used to show/restore preview spill light without re-meshing. Marks the owning merged group
   * dirty so it re-copies the new light. No-op (returns false) if the chunk has no geometry yet.
   */
  private resamplePreviewChunkLight(key: string, useTemp: boolean): boolean {
    const chunk = this.chunks.get(key);
    const geo = this.geometries.get(key);
    if (!chunk || !geo || !geo.hasGeometry()) return false;
    const grid = this.meshPool.takeGrid();
    expandChunkToGrid(chunk, this.chunks, grid, useTemp);
    const changed = geo.resampleLightFromGrid(grid);
    this.meshPool.returnGrid(grid);
    // Push the two rewritten light attributes into the batch as a ranged write — no full re-copy
    // (that churn was the preview slowdown).
    if (changed) this.terrain.updateChunkLight(key);
    return true;
  }

  /**
   * Restore a spill neighbour's displayed light after it leaves preview: drop its temp buffer and
   * light-only resample its geometry back from committed data. Cheap (no re-mesh); committed data
   * was never mutated, so this fully reverts the preview override.
   */
  restorePreviewChunkLight(key: string): void {
    const chunk = this.chunks.get(key);
    if (chunk) chunk.discardTemp();
    this.resamplePreviewChunkLight(key, false);
  }

  /**
   * Revert a chunk's committed-geometry light override back to committed data WITHOUT discarding its
   * temp buffer. Used when a spill neighbour (whose committed display was overridden by an earlier
   * spill relight) becomes a re-meshed preview chunk: its committed override must be undone so it can't
   * leak past the preview, but its temp must survive for the pending re-mesh. The batch range is
   * rewritten so a later restore shows the corrected light instead of the stale override.
   */
  revertPreviewChunkDisplayLight(key: string): void {
    this.resamplePreviewChunkLight(key, false);
    this.terrain.refreshChunk(key);
  }

  /**
   * Light-only resample of a build-preview mesh (owned by BuildPreview, not a ChunkGeometry) from
   * the chunk's relit temp grid. Rewrites just the mesh's two light attributes — no re-mesh. Called
   * in the deferred lighting phase, AFTER the whole region (incl. this chunk's neighbours) is relit,
   * so boundary vertices sample their neighbours' updated light (fixes the dark border at edits).
   */
  resamplePreviewMeshLight(
    key: string,
    meshes: (THREE.Mesh | null)[],
    cellIndices: (Uint16Array | null)[],
  ): void {
    const chunk = this.chunks.get(key);
    if (!chunk || !chunk.tempData) return;
    const grid = this.meshPool.takeGrid();
    expandChunkToGrid(chunk, this.chunks, grid, true);
    for (let layer = 0; layer < meshes.length; layer++) {
      const mesh = meshes[layer];
      const cells = cellIndices[layer];
      if (mesh && cells) resampleLightAttributes(mesh.geometry, cells, grid);
    }
    this.meshPool.returnGrid(grid);
  }

  /**
   * Receive chunk data from the server.
   * Called by the network layer when chunk data arrives.
   */
  receiveChunkData(chunkData: VoxelChunkData): void {
    const { chunkX, chunkY, chunkZ, voxelData } = chunkData;
    // Queued, not ingested here: this runs in a worker/socket callback between frames, where the cost
    // is in no budget (see drainIngest). The neighbour fan-out moves with it.
    this.queueIngest(chunkX, chunkY, chunkZ, voxelData, 0, true);
  }

  /**
   * Receive surface column data from the server.
   * Called by the network layer when surface column data arrives.
   * Contains a tile + multiple chunks for the column.
   */
  receiveSurfaceColumnData(columnData: SurfaceColumnResponse): void {
    const { tx, tz, heights, materials, chunks } = columnData;
    const columnKey = packPairKey(tx, tz);
    
    
    // Remove from pending columns
    this.pendingColumns.delete(columnKey);
    this.pendingColumnTimes.delete(columnKey);
    
    // Store the column's chunk range from the (stamp-corrected) tile heights.
    this.setColumnInfo(tx, tz, heights);

    // Notify external systems (map cache)
    if (this.onTileReceived) {
      this.onTileReceived(tx, tz, heights, materials);
    }

    // Queue chunks top-down so sunlight propagates correctly from sky to ground (the drain is FIFO,
    // so this order survives a column split across frames). Server sends them bottom-up, so reverse.
    //
    // The byte-identical dedupe and the replaced-only neighbour fan-out moved into drainIngest: both
    // ask "is this new or a replacement?", which can only be answered at ingest time now that a
    // payload may sit in the queue while other paths run. ingestChunkData still fans out its own
    // gated −7 re-adds and relight triggers for FIRST arrivals.
    for (let i = chunks.length - 1; i >= 0; i--) {
      const { chunkY, lastBuildSeq, voxelData } = chunks[i];
      this.queueIngest(tx, chunkY, tz, voxelData, lastBuildSeq, false);
    }
  }

  /**
   * Handle a REQUEST_NACK from the server.
   * Clears the matching pending entry so it can be re-requested next BFS cycle.
   */
  handleRequestNack(nack: RequestNack): void {
    const { originalMsgId, x, y, z } = nack;

    switch (originalMsgId) {
      case MSG_VOXEL_CHUNK_REQUEST: {
        const key = chunkKey(x, y, z);
        console.warn(`[VoxelWorld] NACK: chunk request (${x},${y},${z}) rejected, will retry`);
        this.pendingChunks.delete(key);
        this.pendingChunkTimes.delete(key);
        break;
      }
      case MSG_MAP_TILE_REQUEST: {
        const columnKey = packPairKey(x, z);
        console.warn(`[VoxelWorld] NACK: tile request (${x},${z}) rejected, will retry`);
        this.pendingTiles.delete(columnKey);
        this.pendingTileTimes.delete(columnKey);
        break;
      }
      case MSG_SURFACE_COLUMN_REQUEST: {
        const columnKey = packPairKey(x, z);
        console.warn(`[VoxelWorld] NACK: surface column request (${x},${z}) rejected, will retry`);
        this.pendingColumns.delete(columnKey);
        this.pendingColumnTimes.delete(columnKey);
        // Allow initial column re-request if it was the one that got NACKed
        if (this.pendingColumns.size === 0 && this.columnInfo.size === 0) {
          this.initialColumnRequested = false;
        this.streamingBootstrapped = false;
        }
        break;
      }
    }

    // Freed slot gets re-requested on the next pass (box unchanged).
    this.dirtyVisibility();
  }

  /**
   * Unload a chunk by key.
   */
  private unloadChunk(key: string): void {
    // Notify listeners before cleanup
    this.everShown.delete(key);
    for (const listener of this.unloadListeners) listener(key);
    // OUT OF THE BATCH FIRST — its pixels stop here. The stitcher's retraction below restores this
    // chunk's own band masks and announces the index edit, and with the range still registered that
    // announcement was a full re-copy of a chunk about to be deleted, per eviction.
    this.terrain.removeChunk(key);
    // Retract whatever this chunk owns, and re-queue the neighbourhoods whose seam ownership its
    // departure changes — a coarse owner whose participant is going, or a fine chunk whose owner is.
    this.seamStitcher.onRemoved(key);
    this.incompleteChunks.delete(key);
    this.incompleteSinceMs.delete(key);

    // Geometry map is changing — need a visibility rescan
    this.dirtyVisibility();

    // Dispose geometry
    const geo = this.geometries.get(key);
    if (geo) {
      geo.dispose();
      this.geometries.delete(key);
      this.geometryIndex.delete(packChunkKeyLod(geo.chunk.cx, geo.chunk.cy, geo.chunk.cz, geo.chunk.lod));
      this.meshCountDirty = true;
    }

    // Remove from remesh queue (mesh-completeness lives on the chunk's `phase`, which is deleted below —
    // no separate forget-on-unload contract to maintain).
    this.remeshPipeline.delete(key);

    // Remove from pending if it was still pending
    this.pendingChunks.delete(key);
    this.pendingChunkTimes.delete(key);
    this.pendingLodChunks.delete(key);
    this.pendingLodChunkTimes.delete(key);

    // Remove chunk (its ChunkPhase goes with it)
    const removed = this.chunks.get(key);
    if (removed) {
      if (removed.lod > 0) this.lodChunkCount--;
      this.unindexChunk(removed);
    }
    this.chunks.delete(key);
  }

  /**
   * Re-mesh every chunk that reads chunk (cx,cy,cz) as a margin source — its 7 negative-direction
   * neighbours (faces, edges, corner). A mesh's high-side margin is filled from its +X/+Y/+Z faces,
   * edges, and corner (see expandChunkData), so those exact 7 neighbours must re-mesh when this
   * chunk's voxels change/arrive, or a diagonal neighbour keeps an extrapolated margin → misaligned
   * corner/edge boundary verts (a seam gap). Public so the build system can trigger it after commits.
   */
  queueNeighborRemesh(cx: number, cy: number, cz: number): void {
    for (const [dx, dy, dz] of NEGATIVE_MARGIN_OFFSETS_7) {
      const key = chunkKey(cx + dx, cy + dy, cz + dz);
      if (this.chunks.has(key)) {
        this.remeshPipeline.add(key);
      }
    }
  }

  /**
   * Remesh a single chunk synchronously.
   * Delegates to RemeshPipeline.
   */
  remeshChunk(chunk: Chunk): void {
    this.remeshPipeline.remeshSync(chunk);
  }

  /**
   * Remesh all dirty chunks synchronously.
   * Delegates to RemeshPipeline.
   */
  remeshAllDirty(): void {
    this.remeshPipeline.remeshAllDirty();
  }

  /**
   * Get a chunk by coordinates.
   * @returns The chunk, or undefined if not loaded
   */
  getChunk(cx: number, cy: number, cz: number): Chunk | undefined {
    return this.chunkAt(cx, cy, cz, 0);
  }

  /**
   * Get a chunk by world position.
   * @returns The chunk, or undefined if not loaded
   */
  getChunkAtWorld(x: number, y: number, z: number): Chunk | undefined {
    const { cx, cy, cz } = worldToChunk(x, y, z);
    return this.getChunk(cx, cy, cz);
  }


  /**
   * Get the total number of loaded chunks.
   */
  getChunkCount(): number {
    return this.chunks.size;
  }

  /**
   * Get the number of chunks with visible meshes.
   */
  getMeshCount(): number {
    if (!this.meshCountDirty) return this.cachedMeshCount;
    let count = 0;
    for (const geo of this.geometries.values()) {
      if (geo.hasGeometry()) {
        count++;
      }
    }
    this.cachedMeshCount = count;
    this.meshCountDirty = false;
    return count;
  }

  /**
   * Get stats about the world.
   */
  getStats(): {
    chunksLoaded: number;
    meshesVisible: number;
    remeshQueueSize: number;
  } {
    return {
      chunksLoaded: this.chunks.size,
      meshesVisible: this.getMeshCount(),
      remeshQueueSize: this.remeshPipeline.size,
    };
  }

  /**
   * Force reload and remesh all chunks.
   */
  refresh(): void {
    // Queue all chunks for remesh
    for (const key of this.chunks.keys()) {
      this.remeshPipeline.add(key);
    }
    this.remeshAllDirty();
  }

  /**
   * Apply a build operation to the world.
   * Used when receiving build commits from the server.
   *
   * If any affected chunk isn't loaded yet, the ENTIRE operation is deferred
   * until all chunks arrive (prevents holes at chunk boundaries).
   *
   * @param operation The build operation to apply
   * @returns Array of modified chunk keys (empty if deferred)
   */
  applyBuildOperation(operation: BuildOperation): string[] {
    const affectedKeys = getAffectedChunks(operation);

    // Check if any affected chunk is missing
    const hasMissing = affectedKeys.some(key => !this.chunks.has(key));
    if (hasMissing) {
      // Request missing chunks and defer the entire operation
      this.requestMissingChunks(affectedKeys);
      this.deferredBuildOps.push({ operation, affectedKeys });
      return [];
    }

    return this.executeBuildOperation(operation, affectedKeys);
  }

  /**
   * Execute a build operation (all affected chunks must be loaded).
   * All touched chunks are dispatched as an atomic batch so their meshes
   * update in the same frame — prevents boundary flashes.
   */
  private executeBuildOperation(operation: BuildOperation, affectedKeys: string[]): string[] {
    const modifiedKeys: string[] = [];

    // Before-images (local only) so the op can be undone.
    const undoSnapshots: ChunkSnapshot[] = [];

    const editedChunks: Chunk[] = [];
    // The LOD cascade folds the box this edit actually WROTE, not the operation's conservative
    // bbox — that margin is `max(size) + 3 + |offset|` per axis, which for a modest cube is ~10x
    // the volume, and every one of those voxels would land in a stored layer (measured: 11k layer
    // voxels for one 8 m block, against ~1k for the block itself).
    const drawn = { x0: 0, y0: 0, z0: 0, x1: -1, y1: -1, z1: -1 };
    for (const key of affectedKeys) {
      const chunk = this.chunks.get(key)!;

      // Snapshot the pre-mutation voxels for undo.
      const before = this.isLocal ? new Uint32Array(chunk.data) : null;

      const changed = drawToChunk(chunk, operation, chunk.data, drawn);
      if (changed) {
        modifiedKeys.push(key);
        editedChunks.push(chunk);
        if (before) undoSnapshots.push({ key, data: before });
        this.editLayers.noteEdit(chunk.cx, chunk.cy, chunk.cz, drawn);
      }
    }

    // Carving can expose previously-hidden chunks that need loading — re-run the pass with the
    // same box; the edit did not move the camera.
    if (modifiedKeys.length > 0) {
      this.dirtyVisibility();
    }

    // Relight the whole affected region in one shared pass, then re-mesh the chunks whose
    // geometry changed and light-only-resample the rest.
    const batchKeys = this.relightEditedChunks(editedChunks);
    this.dispatchRelightBatch(batchKeys, modifiedKeys);

    // Persist edited chunks + record undo for the active local world.
    if (this.isLocal) {
      for (const key of modifiedKeys) {
        const chunk = this.chunks.get(key);
        if (chunk) saveChunk(key, chunk.data);
      }
      if (undoSnapshots.length > 0) pushUndo({ chunks: undoSnapshots });
    }

    return modifiedKeys;
  }

  /** Live-data accessor for relightRegion — reads committed chunk voxel arrays. */
  private readonly liveGetData = (cx: number, cy: number, cz: number): Uint32Array | null =>
    this.chunkAt(cx, cy, cz, 0)?.data ?? null;

  /**
   * Collect the relight target set for an edit to `editedChunks` (loaded chunks only), matching the
   * previous per-chunk cascade but as one region:
   *  - SKY: each edited chunk + its loaded column below + the chunk above + the 4 horizontal faces.
   *  - BLOCK: the loaded 3×3×3 around each edited chunk — but only when warranted (an edited chunk
   *    emits, or some region chunk already holds block light), so ordinary edits stay cheap.
   * Sky targets are returned sorted top-down (cy descending) so the shared relight sees each chunk's
   * freshly-relit neighbour above. Edge chunks whose chunk-above isn't loaded carry a skyAbove seed.
   */
  private collectRelightTargets(editedChunks: Chunk[]): RelightTarget[] {
    const map = new Map<string, RelightTarget>();
    const ensure = (cx: number, cy: number, cz: number): RelightTarget => {
      const k = chunkKey(cx, cy, cz);
      let t = map.get(k);
      if (!t) { t = { cx, cy, cz, sky: false, block: false }; map.set(k, t); }
      return t;
    };

    // Block gate: emitter in an edited chunk, or existing block light anywhere in a 3×3×3.
    let runBlock = editedChunks.some((e) => chunkHasEmitter(e.data));
    if (!runBlock) {
      for (const e of editedChunks) {
        for (let dz = -1; dz <= 1 && !runBlock; dz++)
          for (let dy = -1; dy <= 1 && !runBlock; dy++)
            for (let dx = -1; dx <= 1; dx++) {
              if (this.chunkAt(e.cx + dx, e.cy + dy, e.cz + dz, 0)?.hasBlockLight) {
                runBlock = true;
                break;
              }
            }
        if (runBlock) break;
      }
    }

    for (const e of editedChunks) {
      ensure(e.cx, e.cy, e.cz).sky = true;
      // Cascade sky down the loaded column and up one.
      for (let by = e.cy - 1; this.hasChunkAt(e.cx, by, e.cz, 0); by--) {
        ensure(e.cx, by, e.cz).sky = true;
      }
      if (this.hasChunkAt(e.cx, e.cy + 1, e.cz, 0)) ensure(e.cx, e.cy + 1, e.cz).sky = true;
      // Horizontal face neighbours (border sky light).
      for (const [dx, dy, dz] of FACE_OFFSETS_6) {
        if (dy !== 0) continue;
        if (this.hasChunkAt(e.cx + dx, e.cy + dy, e.cz + dz, 0)) {
          ensure(e.cx + dx, e.cy + dy, e.cz + dz).sky = true;
        }
      }
      // Block region (loaded 3×3×3), when warranted.
      if (runBlock) {
        for (let dz = -1; dz <= 1; dz++)
          for (let dy = -1; dy <= 1; dy++)
            for (let dx = -1; dx <= 1; dx++) {
              if (this.hasChunkAt(e.cx + dx, e.cy + dy, e.cz + dz, 0)) {
                ensure(e.cx + dx, e.cy + dy, e.cz + dz).block = true;
              }
            }
      }
    }

    // Seed light-from-above for region-edge sky targets + sort top-down (shared with preview).
    return this.finalizeTargets(map);
  }

  /**
   * Relight the region affected by edits to `editedChunks` (shared by build commit + undo). Runs the
   * single shared region orchestration over live data, updates cached block-light flags + dirty
   * marks, recomputes visibility for the edited chunks, and returns every relit chunk key.
   */
  private relightEditedChunks(editedChunks: Chunk[]): Set<string> {
    const batchKeys = new Set<string>();
    if (editedChunks.length === 0) return batchKeys;

    const targets = this.collectRelightTargets(editedChunks);

    perfStats.begin('lightEdit');
    relightRegion(this.liveGetData, targets);
    perfStats.end('lightEdit');

    for (const t of targets) {
      const key = chunkKey(t.cx, t.cy, t.cz);
      const chunk = this.chunks.get(key);
      if (!chunk) continue;
      if (t.block) chunk.hasBlockLight = chunkHasBlockLight(chunk.data);
      chunk.dirty = true;
      batchKeys.add(key);
    }
    // Visibility only depends on voxels, so recompute it just for the edited chunks.
    for (const e of editedChunks) e.visibilityBits = computeVisibility(e.data);

    return batchKeys;
  }

  /**
   * Apply a relight result to the display: chunks whose voxels changed (`editedKeys`) plus any
   * relit chunk that reads an edited chunk's voxels as +margin (its boundary geometry changed) get
   * a full re-mesh; every other relit chunk only changed light and takes the light-only resample
   * path — no SurfaceNets and no geometry realloc (kills the edit judder).
   *
   * @param batchKeys  All chunks whose light was recomputed this edit.
   * @param editedKeys Subset of chunks whose VOXELS changed.
   */
  private dispatchRelightBatch(batchKeys: Set<string>, editedKeys: string[]): void {
    // Start with the edited chunks, then add any relit chunk that consumes an edited chunk's
    // voxels as margin (restricted to chunks already in the relight set — we don't widen it here).
    const remeshKeys = new Set<string>(editedKeys);
    for (const ek of editedKeys) {
      const c = this.chunks.get(ek);
      if (!c) continue;
      for (const [dx, dy, dz] of NEGATIVE_MARGIN_OFFSETS_7) {
        const nk = chunkKey(c.cx + dx, c.cy + dy, c.cz + dz);
        if (batchKeys.has(nk)) remeshKeys.add(nk);
      }
    }

    // Light-only resample everyone else; fall back to a full re-mesh if resampling isn't possible
    // (chunk not yet meshed, or a full re-mesh is already in flight and will produce fresh light).
    for (const key of batchKeys) {
      if (remeshKeys.has(key)) continue;
      const chunk = this.chunks.get(key);
      if (!chunk || !this.resampleChunkLight(chunk)) remeshKeys.add(key);
    }

    // Edited/boundary chunks re-mesh as one atomic batch (same-frame swap, no boundary flash).
    this.remeshPipeline.dispatchBatch(remeshKeys);
  }

  /**
   * Light-only relight of a single chunk: rebuild its GRID_SIZE³ grid (reading the already-updated light
   * bits from chunk.data + neighbours) and rewrite ONLY the mesh's light attributes — no re-mesh,
   * no geometry rebuild. Marks the owning merged group dirty so it re-copies the light next frame.
   *
   * @returns true if handled by resampling; false if the caller must fall back to a full re-mesh
   *   (no geometry yet, or a re-mesh is already in flight for this chunk).
   */
  private resampleChunkLight(chunk: Chunk): boolean {
    if (this.remeshPipeline.isBusy(chunk.key)) return false; // let the in-flight re-mesh own the light
    const geo = this.geometries.get(chunk.key);
    if (!geo || !geo.hasGeometry()) return false;

    const grid = this.meshPool.takeGrid();
    expandChunkToGrid(chunk, this.chunks, grid);
    const changed = geo.resampleLightFromGrid(grid);
    this.meshPool.returnGrid(grid);

    // Ranged write into the batch instead of a full re-copy (cuts edit judder).
    if (changed) this.terrain.updateChunkLight(chunk.key);
    return true;
  }

  /**
   * Undo the most recent local build op (restores chunks + persists the revert).
   * @returns the reverted chunk keys (for map-tile refresh) and the snap points the
   *          reverted placement deposited (for the caller to un-deposit).
   */
  undoLastBuild(): UndoResult {
    if (!this.isLocal) return NO_UNDO;
    const entry = popUndo();
    if (!entry) return NO_UNDO;

    const editedChunks: Chunk[] = [];
    for (const snap of entry.chunks) {
      const chunk = this.chunks.get(snap.key);
      if (chunk) {
        chunk.data.set(snap.data);
        editedChunks.push(chunk);
        saveChunk(snap.key, chunk.data);
      } else {
        // Chunk not loaded — revert the persisted copy so it reloads undone.
        saveChunk(snap.key, snap.data);
      }
    }
    if (editedChunks.length > 0) {
      this.dirtyVisibility();
      const batchKeys = this.relightEditedChunks(editedChunks);
      this.dispatchRelightBatch(batchKeys, editedChunks.map((c) => c.key));
    }
    // An undo is just another edit to the cascade — the fold reads whatever the voxels now say, so
    // the reverted volume LOWERS its coarse floors with no inverse operation anywhere.
    for (const snap of entry.chunks) {
      const { cx, cy, cz } = parseChunkKey(snap.key);
      this.editLayers.noteEdit(cx, cy, cz, WHOLE_CHUNK_BOX);
    }
    return { keys: entry.chunks.map((s) => s.key), snapPoints: entry.snapPoints ?? [] };
  }

  /**
   * Try to execute deferred build operations whose chunks have all arrived.
   * Called from ingestChunkData whenever a new chunk becomes available.
   */
  private drainDeferredBuildOps(): void {
    if (this.deferredBuildOps.length === 0) return;

    // Iterate in reverse so splice doesn't shift unprocessed indices
    for (let i = this.deferredBuildOps.length - 1; i >= 0; i--) {
      const { operation, affectedKeys } = this.deferredBuildOps[i];
      if (affectedKeys.every(key => this.chunks.has(key))) {
        this.deferredBuildOps.splice(i, 1);
        this.executeBuildOperation(operation, affectedKeys);
      }
    }
  }

  /**
   * Dispose of all chunks and meshes.
   * @param keepWorkers If true, workers are kept alive (for clearAndReload)
   */
  dispose(keepWorkers: boolean = false): void {
    // EVERY ASYNC ANSWER STILL OUTSTANDING IS NOW FOR A DEAD WORLD. Bump first, so anything that
    // resolves during the teardown below already fails its check.
    this.worldEpoch++;

    // Terminate workers unless told to keep them
    if (!keepWorkers) {
      this.meshPool.dispose();
      this.localPool?.dispose();
      this.localPool = null;
    } else {
      // The mesh pool SURVIVES a clear-and-reload by design, and its queues and pending callbacks
      // survive with it — keyed by chunk key, which repeats in the next world. Cancelling costs
      // nothing the new world wanted (its own chunks are re-queued by the reload) and is the only
      // thing standing between an in-flight mesh and the previous world's geometry being applied
      // over the new one. `clear()` on the remesh queue is not enough: that empties what has not
      // been dispatched, not what has.
      this.meshPool.cancelAll();
    }

    // Dispose the terrain batches (every batch mesh leaves the scene, skirts and aprons included)
    this.terrain.dispose();

    // THE SEAM STITCHER GOES WITH THEM. A bulk clear does not fire the per-chunk unload path, so
    // nothing here would otherwise call onRemoved — and every one of the stitcher's maps is keyed by
    // a chunk key, which carries no world identity. Left in place they alias onto the next world:
    // `skirtSig` reports a skirt the new world has never drawn as already correct, and the band /
    // fine-mask records describe index buffers that no longer exist.
    this.seamStitcher.reset();

    // Dispose all chunk geometry holders
    for (const geo of this.geometries.values()) {
      geo.dispose();
    }
    this.geometries.clear();
    this.geometryIndex.clear();

    // Clear chunks
    this.chunks.clear();
    this.chunkIndex.length = 0;
    this.residentByLod.length = 0;

    // Clear pending. The ingest queue goes with them: a payload for the world being torn down must
    // never be applied into the next one (world switch / reload).
    this.ingestQueue.length = 0;
    this.ingestPayloads.clear();
    this.incompleteChunks.clear();
    this.incompleteSinceMs.clear();
    this.pendingChunks.clear();
    this.pendingColumns.clear();
    this.pendingTiles.clear();
    this.pendingChunkTimes.clear();
    this.pendingColumnTimes.clear();
    this.pendingTileTimes.clear();
    this.pendingLodChunks.clear();
    this.pendingLodChunkTimes.clear();
    this.pendingLodColumns.clear();
    this.pendingLodColumnTimes.clear();
    this.staleLodMeshes.clear();
    this.staleLodData.clear();

    // Clear column info (fine + coarse) and LOD selection state
    this.columnInfo.clear();
    this.columnHeights.clear();
    this.columnOwnRange.clear();
    this.columnBorders.clear();
    this.lodColumnInfo.clear();
    this.lastLodSelection = null;
    this.lodChunkCount = 0;

    // The BFS answer and the display verdicts derived from it both describe chunks that are gone.
    // `cachedReachable` outlives `lastBFSChunk = null` below, and the retention predicate and
    // shouldMeshChunk read it directly — so the first pass of the new world would consult the old
    // world's reachability. `pendingDisplayDecisions` is flips queued for keys nothing now holds.
    this.cachedReachable.clear();
    this.bfsFrontierActive = true;
    this.baseEverComplete = false;
    this.lastCentreMoveMs = 0;
    this.centreMoveStartMs = 0;
    this.pendingDisplayDecisions.clear();
    this.retainedHides.clear();

    // Clear remesh queue
    this.remeshPipeline.clear();

    // Clear deferred build operations
    this.deferredBuildOps.length = 0;

    // Clear preview chunk tracking
    this.previewChunks.clear();

    // Reset streaming state so the next update re-seeds the world from scratch
    // (initial surface column + BFS). Without this a clear+reload — e.g. switching
    // worlds — never re-requests the seeding column and nothing streams.
    this.lastPlayerChunk = null;
    this.lastBFSChunk = null;
    this.initialColumnRequested = false;
    this.streamingBootstrapped = false;

    this.initialized = false;

    // Every chunk array just became garbage; tell the cache holders (see onWorldCleared).
    this.onWorldCleared?.();
  }

  /**
   * Clear all chunks and reload from server.
   * Used for dev/debug to force fresh chunk generation.
   */
  clearAndReload(playerPos?: THREE.Vector3): void {
    
    // Dispose chunks but keep workers alive
    this.dispose(true);
    this.meshedEver.clear();
    this.everShown.clear();
    this.meshDispatchTotal = 0;
    this.meshDispatchIdle = 0;
    
    // Re-initialize
    this.initialized = true;
    
    // If player position provided, trigger update to reload chunks
    if (playerPos) {
      this.update(playerPos);
    }
  }
}
